import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ApiError, isRecord, textOf, unwrapList, unwrapRecord } from './api-client';
import type { AdminApiClient, JsonRecord } from './api-client';
import { ActionButton, ConfirmDialog, Notice } from './admin-feedback';
import { AppConfigurationWorkbench } from './app-configuration-workbench';
import type { ConfigurationSection } from './app-configuration-model';
import './product-operations-workspace.css';
import { BookingConfigEditor } from './booking-config-editor';
import { defaultBookingConfig, parseBookingConfig } from '@travel/contracts';
import { ProductPresentationEditor } from './product-presentation-editor';
import { ProductThumbnail } from './product-thumbnail';
import { MiniappPageLinks } from './miniapp-page-links';
import { AUTHORIZED_SCOPE } from './authorized-scope-client';
import { configurationScopeAppId } from './canonical-admin';

type LoadState<T> = { data?: T; error?: unknown; loading: boolean; reload: () => void };

const confirmedDirtyNavigation = new WeakSet<Event>();
let confirmedClickDestination: { href: string; at: number } | undefined;

function confirmDiscardChanges() {
  return window.confirm('当前修改尚未保存，确认放弃并离开吗？');
}

function useUnsavedNavigationGuard(dirty: boolean) {
  useEffect(() => {
    if (!dirty) return undefined;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const click = (event: MouseEvent) => {
      if (confirmedDirtyNavigation.has(event)) return;
      const anchor = (event.target as Element | null)?.closest('a[href]');
      const refreshButton = (event.target as Element | null)?.closest('button');
      const isRefresh = refreshButton?.textContent?.trim() === '刷新数据';
      if (anchor?.getAttribute('href')?.startsWith('#')) return;
      if (!anchor && !isRefresh) return;
      confirmedDirtyNavigation.add(event);
      if (!confirmDiscardChanges()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      } else if (anchor) {
        const destination = new URL(anchor.getAttribute('href') ?? '', location.href);
        confirmedClickDestination = {
          href: `${destination.pathname}${destination.search}`,
          at: Date.now(),
        };
      }
    };
    const navigate = (event: Event) => {
      if (event.defaultPrevented || confirmedDirtyNavigation.has(event)) return;
      confirmedDirtyNavigation.add(event);
      const target = (event as CustomEvent<{ to?: string }>).detail?.to;
      if (
        target &&
        confirmedClickDestination?.href === target &&
        Date.now() - confirmedClickDestination.at < 1000
      ) {
        confirmedClickDestination = undefined;
        return;
      }
      if (!confirmDiscardChanges()) event.preventDefault();
    };
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('travel:admin-navigation', navigate);
    document.addEventListener('click', click, true);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      window.removeEventListener('travel:admin-navigation', navigate);
      document.removeEventListener('click', click, true);
    };
  }, [dirty]);
}

function useTask<T>(
  key: string,
  loader: () => Promise<T>,
  dependency: unknown,
  preserveOnReload = false,
): LoadState<T> {
  const [revision, setRevision] = useState(0);
  const requestKey = `${key}\u0000${revision}`;
  const cached = useRef<{ key: string; data: T }>();
  const [state, setState] = useState<Omit<LoadState<T>, 'reload'> & { key: string }>({
    key: requestKey,
    loading: true,
  });
  useEffect(() => {
    let active = true;
    setState({ key: requestKey, loading: true });
    void loader().then(
      (data) => {
        if (active) {
          cached.current = { key, data };
          setState({ key: requestKey, loading: false, data });
        }
      },
      (error: unknown) => active && setState({ key: requestKey, loading: false, error }),
    );
    return () => {
      active = false;
    };
  }, [requestKey, dependency]);
  const visibleState = state.key === requestKey ? state : { key: requestKey, loading: true };
  const retained =
    preserveOnReload && cached.current?.key === key ? cached.current.data : undefined;
  return {
    ...visibleState,
    ...(visibleState.loading && retained !== undefined ? { data: retained, loading: false } : {}),
    reload: () => setRevision((value) => value + 1),
  };
}

function useApi(api: AdminApiClient, path?: string, preserveOnReload = false) {
  return useTask(
    path ?? 'round02-disabled',
    () => (path ? api.get(path) : Promise.resolve({})),
    api,
    preserveOnReload,
  );
}

function useCommand(
  onDone: () => void,
  options: {
    onPermissionDenied?: () => void;
    onBusyChange?: (busy: boolean) => void;
    onConflict?: () => void;
    formatError?: (error: unknown) => string;
  } = {},
) {
  const running = useRef(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [failed, setFailed] = useState(false);
  const run = async (command: () => Promise<unknown>, success = '保存成功') => {
    if (running.current) return false;
    running.current = true;
    setBusy(true);
    options.onBusyChange?.(true);
    setMessage(undefined);
    setFailed(false);
    try {
      await command();
      setMessage(success);
      window.dispatchEvent(
        new CustomEvent('travel:admin-command-feedback', {
          detail: { message: success, tone: 'success' },
        }),
      );
      onDone();
      return true;
    } catch (error) {
      const failureMessage =
        options.formatError?.(error) ??
        (error instanceof ApiError ? error.message : '操作未完成，请稍后重试');
      if (error instanceof ApiError && error.isPermissionError) options.onPermissionDenied?.();
      if (error instanceof ApiError && error.status === 409) options.onConflict?.();
      setFailed(true);
      setMessage(failureMessage);
      window.dispatchEvent(
        new CustomEvent('travel:admin-command-feedback', {
          detail: { message: failureMessage, tone: 'error' },
        }),
      );
      return false;
    } finally {
      running.current = false;
      setBusy(false);
      options.onBusyChange?.(false);
    }
  };
  return { busy, failed, message, run };
}

function PermissionBlocked({ visible }: { visible: boolean }) {
  return visible ? (
    <Notice tone="warning">当前账号没有该操作权限，相关写入按钮已停用。</Notice>
  ) : null;
}

function CommandMessage({ command }: { command: ReturnType<typeof useCommand> }) {
  return command.message ? (
    <Notice tone={command.failed ? 'error' : 'success'}>{command.message}</Notice>
  ) : null;
}

function textValue(record: JsonRecord, key: string, fallback = '') {
  const value = record[key];
  return typeof value === 'string' || typeof value === 'number' ? String(value) : fallback;
}

function numberValue(value: string, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function lines(value: string) {
  return value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);
}

function validIsoDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function rows(value: unknown, key?: string): JsonRecord[] {
  if (key && isRecord(value) && Array.isArray(value[key])) return value[key].filter(isRecord);
  return unwrapList(value);
}

function nested(record: JsonRecord, key: string) {
  return isRecord(record[key]) ? record[key] : {};
}

function nestedRows(record: JsonRecord, key: string) {
  return Array.isArray(record[key]) ? record[key].filter(isRecord) : [];
}

function clean(value: unknown, fallback = '暂未配置') {
  if (value === undefined || value === null || value === '' || value === '—') return fallback;
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (typeof value === 'object') return fallback;
  return String(value)
    .replace(/\s*[（(]DEV\/TEST(?: Fixture)?[）)]/giu, '')
    .replace(/^dev(?:-test)?-/iu, '');
}

const labels: Record<string, string> = {
  ACTIVE: '已启用',
  DISABLED: '已停用',
  DRAFT: '草稿',
  PUBLISHED: '已发布',
  READY: '就绪',
  OPEN: '待处理',
  ACKNOWLEDGED: '处理中',
  RESOLVED: '已解决',
  PENDING_PAYMENT: '待支付',
  PAID_NEED_TRIP_INFO: '待补充出行信息',
  PAID_INFO_READY: '待确认',
  PENDING_CONFIRM: '待确认',
  PENDING_FULFILLMENT: '待履约',
  FULFILLMENT_READY: '待出发',
  FULFILLING: '服务中',
  COMPLETED: '已完成',
  CANCELLED: '已取消',
  REFUND_APPLIED: '退款已申请',
  REFUND_PROCESSING: '退款处理中',
  REFUNDED: '已退款',
  AFTER_SALES: '售后处理中',
  AFTER_SALES_CLOSED: '售后已关闭',
  PENDING: '处理中',
  SUCCEEDED: '支付成功',
  CLOSED: '已关闭',
  WECHAT_PAY: '微信支付',
  WECHAT: '微信支付',
  WXPAY: '微信支付',
  PER_PERSON: '按人计价',
  PER_CAR: '按车计价',
  PER_PACKAGE: '按套餐计价',
  INCLUDED: '已包含',
  SELF_PAY: '游客自理',
  NOT_INCLUDED: '不包含',
  CHARTER_GUIDE_PACKAGE: '包车＋向导＋套餐',
  TRANSFER_TICKET_PACKAGE: '接送＋票务＋套餐',
  CARPOOL_GUIDE_PACKAGE: '拼车＋向导＋套餐',
  STANDARD: '标准预订',
  QUICK: '快捷预订',
  PRODUCTION: '生产环境',
  STAGING: '预发布环境',
  DEVELOPMENT: '开发环境',
  TEST: '测试环境',
};

function label(value: unknown) {
  const raw = clean(value);
  return labels[raw] ?? labels[raw.toUpperCase()] ?? raw;
}

const ACTIVE_AFTER_SALES_ORDER_STATUSES = [
  'REFUND_APPLIED',
  'REFUND_PROCESSING',
  'AFTER_SALES',
] as const;

function isActiveAfterSalesOrder(status: string) {
  return (ACTIVE_AFTER_SALES_ORDER_STATUSES as readonly string[]).includes(status);
}

const CLOSED_ORDER_STATUSES = ['COMPLETED', 'CANCELLED', 'REFUNDED', 'AFTER_SALES_CLOSED'];

function isOpenOrder(status: string) {
  return !CLOSED_ORDER_STATUSES.includes(status);
}

function paymentProviderLabel(value: unknown) {
  const raw = textOf({ value }, 'value').toUpperCase();
  return ['WECHAT_PAY', 'WECHAT', 'WXPAY'].includes(raw) ? '微信支付' : '未提供';
}

function money(value: unknown) {
  const cents = Number(value);
  return Number.isFinite(cents)
    ? `¥${(cents / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '暂未配置';
}

function configured(value: unknown, fallback = '待运营配置') {
  return clean(value, fallback);
}

function priceRangeFromSkus(skus: JsonRecord[]) {
  const prices = skus.map((sku) => Number(sku.salePriceCents)).filter(Number.isFinite);
  if (!prices.length) return '待运营配置';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? money(min) : `${money(min)}–${money(max)}`;
}

function appAccessText(app: JsonRecord) {
  if (app.isFixture === true) return '开发/测试配置';
  return app.wechatAppIdRef ? '已登记微信接入引用' : '待外部验证';
}

function orderAttentionText(order: JsonRecord) {
  const raw = textOf(order, 'status');
  if (raw === 'PENDING_PAYMENT') return '待支付跟进';
  if (raw === 'PENDING_CONFIRM' || raw === 'PAID_INFO_READY') return '待运营确认';
  if (raw === 'REFUND_PROCESSING' || raw === 'REFUND_APPLIED') return '售后退款处理中';
  if (raw === 'AFTER_SALES') return '售后处理中';
  if (raw === 'PENDING_FULFILLMENT' || raw === 'FULFILLMENT_READY') return '待履约';
  return label(raw);
}

function peopleRange(minimum: unknown, maximum: unknown) {
  const min = typeof minimum === 'number' ? minimum : Number.NaN;
  const max = typeof maximum === 'number' ? maximum : Number.NaN;
  return Number.isInteger(min) && Number.isInteger(max) && min > 0 && max >= min
    ? `${min}–${max} 人`
    : '未限定';
}

function dateTime(value: unknown) {
  if (typeof value !== 'string') return '暂未记录';
  const valueDate = new Date(value);
  return Number.isNaN(valueDate.getTime())
    ? '暂未记录'
    : valueDate.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
}

function dateOnly(value: unknown, fallback = '待补充') {
  if (typeof value !== 'string') return fallback;
  const valueDate = new Date(value);
  if (Number.isNaN(valueDate.getTime())) return clean(value, fallback);
  return valueDate.toISOString().slice(0, 10);
}

function percentage(numerator: number, denominator: number) {
  return denominator > 0 ? `${Math.round((numerator / denominator) * 100)}%` : '暂无样本';
}

function orderTrip(order: JsonRecord) {
  return nested(order, 'tripInfo');
}

function orderContactName(order: JsonRecord) {
  const trip = orderTrip(order);
  const contact = nested(order, 'contact');
  return clean(order.contactName ?? trip.contactName ?? contact.contactName, '游客待补充');
}

function orderServiceDate(order: JsonRecord) {
  const trip = orderTrip(order);
  return dateOnly(order.serviceDate ?? order.tripDate ?? trip.travelDate);
}

function orderPeople(order: JsonRecord) {
  const trip = orderTrip(order);
  return clean(order.peopleCount ?? trip.peopleCount, '待补');
}

function orderProductTitle(order: JsonRecord) {
  return clean(
    order.productTitle ?? order.productName ?? order.spuTitle ?? order.skuTitle,
    '综合旅游服务',
  );
}

function todoCategoryForStatus(status: string) {
  if (['PAID_INFO_READY', 'PENDING_CONFIRM'].includes(status)) return '待确认';
  if (['PENDING_FULFILLMENT', 'FULFILLMENT_READY', 'FULFILLING'].includes(status))
    return '履约准备';
  if (isActiveAfterSalesOrder(status)) return '售后退款';
  if (status === 'PENDING_PAYMENT') return '待支付';
  return '跟进事项';
}

function todoPriorityForStatus(status: string, index = 0) {
  if (isActiveAfterSalesOrder(status)) return '紧急';
  if (['PAID_INFO_READY', 'PENDING_CONFIRM'].includes(status)) return '高';
  if (status === 'PENDING_PAYMENT') return index < 2 ? '高' : '普通';
  return '普通';
}

function todoSlaForStatus(status: string) {
  if (isActiveAfterSalesOrder(status)) return '需双人审核';
  if (['PAID_INFO_READY', 'PENDING_CONFIRM'].includes(status)) return '今日确认';
  if (['PENDING_FULFILLMENT', 'FULFILLMENT_READY'].includes(status)) return '出行前完成';
  if (status === 'PENDING_PAYMENT') return '等待支付';
  return '按状态跟进';
}

function tone(value: unknown) {
  const raw = String(value);
  if (['ACTIVE', 'READY', 'COMPLETED', 'PUBLISHED', 'RESOLVED'].includes(raw)) return 'green';
  if (['DISABLED', 'CANCELLED', 'REFUNDED', 'CLOSED', 'AFTER_SALES_CLOSED'].includes(raw))
    return 'gray';
  if (['FAILED', 'REFUND_APPLIED', 'REFUND_PROCESSING', 'AFTER_SALES'].includes(raw)) return 'red';
  return 'orange';
}

function Pill({ value }: { value: unknown }) {
  return <span className={`canonical-pill ${tone(value)}`}>{label(value)}</span>;
}

function Header({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="canonical-page-title">
      <div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      {actions && <div className="canonical-page-actions">{actions}</div>}
    </div>
  );
}

function Panel({
  title,
  hint,
  children,
  className = '',
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`r2-panel ${className}`}>
      <header>
        <div>
          <h2>{title}</h2>
          {hint && <p>{hint}</p>}
        </div>
      </header>
      {children}
    </section>
  );
}

function State({
  state,
  count,
  noun,
  allowEmpty = false,
  children,
}: {
  state: LoadState<unknown>;
  count: number;
  noun: string;
  allowEmpty?: boolean;
  children: React.ReactNode;
}) {
  const kind =
    state.loading && state.data === undefined
      ? 'loading'
      : state.error instanceof ApiError && state.error.isPermissionError
        ? 'permission'
        : state.error
          ? 'error'
          : count
            ? 'ready'
            : 'empty';
  if (kind === 'ready' || (kind === 'empty' && allowEmpty)) return <>{children}</>;
  const copy = {
    loading: ['正在加载', `正在读取${noun}…`],
    permission: ['无权访问', `当前账号不能查看${noun}。`],
    error: ['加载失败', `${noun}暂时无法读取，请稍后重试。`],
    empty: [`暂无${noun}`, `当前还没有${noun}。`],
  }[kind] ?? ['暂不可用', '请稍后重试。'];
  return (
    <div
      className={`canonical-state canonical-state--${kind}`}
      role={kind === 'error' ? 'alert' : undefined}
    >
      <span className={kind === 'loading' ? 'canonical-spinner' : 'canonical-state__icon'}>
        {kind === 'permission' ? '锁' : kind === 'error' ? '!' : '空'}
      </span>
      <strong>{copy[0]}</strong>
      <p>{copy[1]}</p>
      {kind !== 'loading' && <button onClick={state.reload}>重新加载</button>}
    </div>
  );
}

function Field({ name, value, wide = false }: { name: string; value: unknown; wide?: boolean }) {
  return (
    <div className={`r2-field ${wide ? 'wide' : ''}`}>
      <span>{name}</span>
      <strong>{clean(value)}</strong>
    </div>
  );
}

type WorkbenchStatus = {
  state: '已完善' | '待完善' | '未检查' | '需选择小程序' | '选填';
  reason: string;
};

function Tabs({
  items,
}: {
  items: Array<{ label: string; href: string; active: boolean; status?: WorkbenchStatus }>;
}) {
  return (
    <nav className="r2-tabs" aria-label="产品工作台步骤">
      {items.map((item) => (
        <a
          className={`${item.active ? 'active' : ''} ${item.status ? `r2-step-${item.status.state}` : ''}`}
          href={item.href}
          key={item.href}
          aria-current={item.active ? 'page' : undefined}
          title={item.status?.reason}
        >
          <span>{item.label}</span>
          {item.status ? <small>{item.status.state}</small> : null}
        </a>
      ))}
    </nav>
  );
}

function Table({
  heads,
  children,
  empty,
}: {
  heads: string[];
  children: React.ReactNode;
  empty?: boolean;
}) {
  return (
    <div className="canonical-table-wrap">
      <table className="canonical-business-table">
        <thead>
          <tr>
            {heads.map((head) => (
              <th key={head}>{head}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
      {empty && <div className="canonical-inline-empty">暂无符合条件的数据</div>}
    </div>
  );
}

function Metrics({
  items,
}: {
  items: Array<{ label: string; value: React.ReactNode; note?: string; accent?: boolean }>;
}) {
  return (
    <div className="r2-metrics">
      {items.map((item) => (
        <article key={item.label}>
          <span>{item.label}</span>
          <strong className={item.accent ? 'accent' : ''}>{item.value}</strong>
          {item.note && <small>{item.note}</small>}
        </article>
      ))}
    </div>
  );
}

interface BaseProps {
  api: AdminApiClient;
  targetAppId: string;
}

export function CanonicalLiveTransactionsPage({ api, targetAppId }: BaseProps) {
  const aggregate = targetAppId === AUTHORIZED_SCOPE;
  const transactionsState = useApi(
    api,
    targetAppId
      ? `/api/admin/m3/apps/${encodeURIComponent(targetAppId)}/payment-transactions`
      : undefined,
  );
  const ordersState = useApi(
    api,
    targetAppId ? `/api/admin/m4/orders?appId=${encodeURIComponent(targetAppId)}` : undefined,
  );
  const transactions = rows(transactionsState.data);
  const orders = rows(ordersState.data);
  const transactionsAvailable = !transactionsState.loading && !transactionsState.error;
  const ordersAvailable = !ordersState.loading && !ordersState.error;
  const paid = transactions.filter((item) => textOf(item, 'status') === 'SUCCEEDED');
  const paidAmounts = paid.map((item) => item.amountCents ?? item.totalAmountCents);
  const paidAmountAvailable = paidAmounts.every(
    (value) => value !== undefined && value !== null && Number.isFinite(Number(value)),
  );
  const paidAmount = paidAmounts.reduce<number>((sum, value) => sum + Number(value), 0);
  const openOrders = orders.filter((order) => isOpenOrder(textOf(order, 'status')));
  const pendingPayment = orders.filter((order) => textOf(order, 'status') === 'PENDING_PAYMENT');
  const afterSales = orders.filter((order) => isActiveAfterSalesOrder(textOf(order, 'status')));
  const failedTransactions = transactions.filter((item) =>
    ['FAILED', 'CLOSED'].includes(textOf(item, 'status')),
  );
  const recent = [...transactions]
    .sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')))
    .slice(0, 9);
  const latestEventTime = recent[0]?.createdAt ?? orders[0]?.createdAt ?? orders[0]?.updatedAt;
  const trendValues = [
    { label: '订单', value: orders.length },
    { label: '支付', value: transactions.length },
    { label: '成功', value: paid.length },
    { label: '售后', value: afterSales.length },
    { label: '异常', value: failedTransactions.length },
  ];
  const maxTrend = Math.max(...trendValues.map((item) => item.value), 1);
  const serviceRows = [
    { label: '支付成功', value: paid.length, total: Math.max(transactions.length, paid.length, 1) },
    {
      label: '人工待处理',
      value: openOrders.length,
      total: Math.max(orders.length, openOrders.length, 1),
    },
    {
      label: '售后处理中',
      value: afterSales.length,
      total: Math.max(openOrders.length, afterSales.length, 1),
    },
  ];
  const exceptionOrders = [...afterSales, ...pendingPayment].slice(0, 4);
  return (
    <div className="r2-page">
      <Header
        title="实时订单与支付监控"
        subtitle="经营总览 / 实时监控 · 交易状态以已记录的支付结果为准"
        actions={
          <>
            <button onClick={transactionsState.reload}>刷新</button>
            <a className="r2-button-link" href="/admin/alerts">
              告警规则
            </a>
          </>
        }
      />
      <div className="r2-live-strip">
        <div>
          <b>
            <i />
            {transactionsState.loading || ordersState.loading
              ? '监控数据读取中'
              : transactionsState.error || ordersState.error
                ? '部分监控源异常'
                : aggregate
                  ? '本页监控记录已读取'
                  : '交易服务正常'}
          </b>
          <span>最近事件：{dateTime(latestEventTime)}</span>
        </div>
        <span>
          支付成功率{' '}
          {transactionsAvailable ? percentage(paid.length, transactions.length) : '暂不可用'}
        </span>
        <span>待处理订单 {ordersAvailable ? openOrders.length : '暂不可用'}</span>
        <em>刷新后同步当前授权范围交易事实</em>
      </div>
      {aggregate && (
        <Notice>
          当前金额、支付成功率与状态分组只统计本页已读取的订单和支付记录，不代表全量经营汇总。
        </Notice>
      )}
      <Metrics
        items={[
          { label: '当前可见订单', value: ordersAvailable ? orders.length : '暂不可用' },
          { label: '支付记录', value: transactionsAvailable ? transactions.length : '暂不可用' },
          {
            label: '支付成功',
            value: transactionsAvailable ? paid.length : '暂不可用',
            accent: true,
          },
          {
            label: '实付金额',
            value: transactionsAvailable && paidAmountAvailable ? money(paidAmount) : '暂不可用',
          },
          {
            label: '退款 / 售后处理中',
            value: ordersAvailable ? afterSales.length : '暂不可用',
          },
          {
            label: '异常记录',
            value: transactionsAvailable ? failedTransactions.length : '暂不可用',
          },
        ]}
      />
      <div className="r2-two-one">
        <Panel title="实时交易流" hint="仅展示当前授权范围内已读取的最新支付事实">
          <State state={transactionsState} count={transactions.length} noun="支付记录">
            <Table heads={['时间', '订单', '金额', '结果', '支付方式']} empty={!recent.length}>
              {recent.map((item) => (
                <tr key={textOf(item, 'id')}>
                  <td>{dateTime(item.createdAt)}</td>
                  <td>{clean(item.orderNo ?? item.outTradeNo, '未提供')}</td>
                  <td>{money(item.amountCents ?? item.totalAmountCents)}</td>
                  <td>
                    <Pill value={item.status} />
                  </td>
                  <td>{paymentProviderLabel(item.provider)}</td>
                </tr>
              ))}
            </Table>
          </State>
        </Panel>
        <Panel title="支付服务概览" hint="商户健康详情以支付通道健康记录为准">
          <div className="r2-service-bars">
            {serviceRows.map((row) => (
              <div key={row.label}>
                <div>
                  <span>{row.label}</span>
                  <b>{ordersAvailable && transactionsAvailable ? row.value : '暂不可用'}</b>
                </div>
                <i
                  style={{ width: `${Math.max(8, Math.round((row.value / row.total) * 100))}%` }}
                />
              </div>
            ))}
          </div>
          <div className="r2-insight-box">
            <b>运营说明</b>
            <span>
              当前页只展示服务端已持久化订单与支付结果；未从客户端回调或文案推断交易成功。
            </span>
          </div>
        </Panel>
      </div>
      <div className="r2-three">
        <Panel title="支付趋势" hint="按当前可见数据绘制轻量趋势">
          <div className="r2-sparkline" aria-label="支付趋势概览">
            {trendValues.map((item) => (
              <span key={item.label}>
                <i style={{ height: `${18 + Math.round((item.value / maxTrend) * 64)}px` }} />
                <b>{item.label}</b>
                <em>{item.value}</em>
              </span>
            ))}
          </div>
        </Panel>
        <Panel title="失败原因" hint="不从错误文案反推原因">
          <div className="r2-failure-bars">
            <div>
              <span>失败 / 关闭交易</span>
              <b>{transactionsAvailable ? failedTransactions.length : '暂不可用'}</b>
            </div>
            <div>
              <span>退款 / 售后人工处理</span>
              <b>{ordersAvailable ? afterSales.length : '暂不可用'}</b>
            </div>
            <div>
              <span>待支付订单</span>
              <b>{ordersAvailable ? pendingPayment.length : '暂不可用'}</b>
            </div>
          </div>
          <div className="r2-insight-box muted">
            <span>失败分类需要支付通道健康事件支持；当前不展示推测值。</span>
          </div>
        </Panel>
        <Panel title="异常订单">
          <State state={ordersState} count={orders.length} noun="订单" allowEmpty>
            <div className="r2-exception-list">
              {exceptionOrders.length ? (
                exceptionOrders.map((order) => (
                  <a
                    key={textOf(order, 'id')}
                    href={`/admin/orders/${encodeURIComponent(textOf(order, 'id'))}`}
                  >
                    <b>{clean(order.orderNo, '未提供')}</b>
                    <span>{label(order.status)}</span>
                  </a>
                ))
              ) : (
                <div>
                  <b>暂无异常订单</b>
                  <span>当前可见范围内无待处理异常</span>
                </div>
              )}
            </div>
          </State>
        </Panel>
      </div>
    </div>
  );
}

export function CanonicalTodoCenterPage({ api, targetAppId }: BaseProps) {
  const state = useApi(
    api,
    targetAppId ? `/api/admin/m4/orders?appId=${encodeURIComponent(targetAppId)}` : undefined,
  );
  const all = rows(state.data);
  const todos = all.filter((order) => isOpenOrder(textOf(order, 'status')));
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('全部待办');
  const [selectedId, setSelectedId] = useState('');
  const categories = [
    ['全部待办', todos.length],
    [
      '出行信息待确认',
      todos.filter((o) => ['PAID_INFO_READY', 'PENDING_CONFIRM'].includes(textOf(o, 'status')))
        .length,
    ],
    [
      '履约准备',
      todos.filter((o) =>
        ['PENDING_FULFILLMENT', 'FULFILLMENT_READY', 'FULFILLING'].includes(textOf(o, 'status')),
      ).length,
    ],
    ['售后退款', todos.filter((o) => isActiveAfterSalesOrder(textOf(o, 'status'))).length],
  ] as const;
  const filtered = todos.filter((order) => {
    const status = textOf(order, 'status');
    const categoryMatch =
      category === '全部待办' ||
      (category === '出行信息待确认' && ['PAID_INFO_READY', 'PENDING_CONFIRM'].includes(status)) ||
      (category === '履约准备' &&
        ['PENDING_FULFILLMENT', 'FULFILLMENT_READY', 'FULFILLING'].includes(status)) ||
      (category === '售后退款' && isActiveAfterSalesOrder(status));
    const searchText = [
      order.orderNo,
      order.contactName,
      orderProductTitle(order),
      orderContactName(order),
      orderServiceDate(order),
      label(status),
    ].join(' ');
    return (
      categoryMatch && searchText.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
    );
  });
  const selected =
    filtered.find((order) => textOf(order, 'id') === selectedId) ?? filtered[0] ?? todos[0];
  const selectedStatus = textOf(selected ?? {}, 'status');
  return (
    <div className="r2-page">
      <Header
        title="运营待办中心"
        subtitle="经营总览 / 待办 · 按订单实际状态汇总需人工跟进的事项"
        actions={<button onClick={state.reload}>刷新待办</button>}
      />
      <Metrics
        items={[
          { label: '全部待办', value: todos.length },
          {
            label: '待确认',
            value: todos.filter((o) =>
              ['PAID_INFO_READY', 'PENDING_CONFIRM'].includes(textOf(o, 'status')),
            ).length,
          },
          {
            label: '待履约',
            value: todos.filter((o) =>
              ['PENDING_FULFILLMENT', 'FULFILLMENT_READY'].includes(textOf(o, 'status')),
            ).length,
          },
          {
            label: '退款处理中',
            value: todos.filter((o) => isActiveAfterSalesOrder(textOf(o, 'status'))).length,
            accent: true,
          },
          { label: '逾期', value: '未配置', note: '等待运营规则配置' },
        ]}
      />
      <div className="r2-todo-layout">
        <Panel title="待办分类">
          <div className="r2-category-list">
            {categories.map(([name, count]) => (
              <button
                className={category === name ? 'active' : ''}
                key={String(name)}
                onClick={() => setCategory(name)}
              >
                <span>{name}</span>
                <b>{count}</b>
              </button>
            ))}
          </div>
        </Panel>
        <Panel title="待办列表">
          <div className="r2-task-toolbar">
            <label className="canonical-search">
              <span>搜索</span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索订单号 / 游客 / 产品"
              />
            </label>
            <span>当前筛选 {filtered.length} 项</span>
          </div>
          <State state={state} count={all.length} noun="订单" allowEmpty>
            <Table
              heads={['优先级', '任务', '范围', '负责人 / SLA', '状态']}
              empty={!filtered.length}
            >
              {filtered.slice(0, 10).map((order, index) => (
                <tr
                  key={textOf(order, 'id')}
                  className={textOf(order, 'id') === textOf(selected ?? {}, 'id') ? 'selected' : ''}
                >
                  <td>
                    <button
                      className={`r2-priority-badge ${todoPriorityForStatus(textOf(order, 'status'), index) === '紧急' ? 'urgent' : ''}`}
                      onClick={() => setSelectedId(textOf(order, 'id'))}
                      type="button"
                    >
                      {todoPriorityForStatus(textOf(order, 'status'), index)}
                    </button>
                  </td>
                  <td>
                    <button
                      className="r2-table-row-button"
                      onClick={() => setSelectedId(textOf(order, 'id'))}
                      type="button"
                    >
                      <b>{todoCategoryForStatus(textOf(order, 'status'))}</b>
                      <span>
                        {clean(order.orderNo, '未提供')} · {orderProductTitle(order)}
                      </span>
                    </button>
                  </td>
                  <td>
                    <span>{orderServiceDate(order)}</span>
                    <small className="r2-cell-note">
                      {orderContactName(order)} · {orderPeople(order)} 人
                    </small>
                  </td>
                  <td>
                    <span>运营员工</span>
                    <small className="r2-cell-note">
                      {todoSlaForStatus(textOf(order, 'status'))}
                    </small>
                  </td>
                  <td>
                    <Pill value={order.status} />
                  </td>
                </tr>
              ))}
            </Table>
          </State>
        </Panel>
        <Panel title="任务详情" hint="选择待办后可在订单详情中处理">
          <div className="r2-task-detail">
            {selected ? (
              <>
                <div className="r2-task-detail-title">
                  <span className="r2-priority-badge urgent">
                    {todoPriorityForStatus(selectedStatus)}
                  </span>
                  <div>
                    <b>{clean(selected.orderNo, '未提供')}</b>
                    <small>{todoCategoryForStatus(selectedStatus)}</small>
                  </div>
                </div>
                <dl className="r2-task-meta">
                  <div>
                    <dt>游客</dt>
                    <dd>{orderContactName(selected)}</dd>
                  </div>
                  <div>
                    <dt>产品</dt>
                    <dd>{orderProductTitle(selected)}</dd>
                  </div>
                  <div>
                    <dt>出行</dt>
                    <dd>
                      {orderServiceDate(selected)} · {orderPeople(selected)} 人
                    </dd>
                  </div>
                  <div>
                    <dt>当前状态</dt>
                    <dd>{label(selectedStatus)}</dd>
                  </div>
                </dl>
                <div className="r2-sla-badges">
                  <span>{todoSlaForStatus(selectedStatus)}</span>
                  <span>服务端权限校验</span>
                  <span>操作写入审计</span>
                </div>
                <div className="r2-check-list">
                  <span>✓ 核对支付与订单状态</span>
                  <span>✓ 确认联系人和出行信息</span>
                  <span>✓ 按订单详情页执行合法状态动作</span>
                </div>
                <a
                  className="r2-button-link full"
                  href={`/admin/orders/${encodeURIComponent(textOf(selected, 'id'))}`}
                >
                  进入订单处理
                </a>
              </>
            ) : (
              <div className="r2-insight-box muted">
                <span>当前筛选范围内暂无待办。</span>
              </div>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function CityEditForm({
  api,
  city,
  onDone,
}: {
  api: AdminApiClient;
  city: JsonRecord;
  onDone: () => void;
}) {
  const command = useCommand(onDone);
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [status, setStatus] = useState('ACTIVE');
  useEffect(() => {
    setName(textValue(city, 'name'));
    setSortOrder(textValue(city, 'sortOrder', '0'));
    setStatus(textValue(city, 'status', 'ACTIVE'));
  }, [city]);
  const cityId = textValue(city, 'id');
  const region = nested(city, 'region');
  return (
    <form
      className="typed-form r2-city-edit-form"
      onSubmit={(event) => {
        event.preventDefault();
        void command.run(() =>
          api.send(`/api/admin/m2/cities/${encodeURIComponent(cityId)}`, 'PATCH', {
            name,
            sortOrder: numberValue(sortOrder),
            status,
          }),
        );
      }}
    >
      <label>
        城市名称
        <input required value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      <label>
        城市编码
        <input value={textValue(city, 'code')} disabled />
      </label>
      <label>
        所属地区
        <input value={clean(region.name)} disabled />
      </label>
      <label>
        时区
        <input value={textValue(city, 'timezone', 'Asia/Shanghai')} disabled />
      </label>
      <label>
        前端排序
        <input
          type="number"
          required
          value={sortOrder}
          onChange={(event) => setSortOrder(event.target.value)}
        />
      </label>
      <label>
        运营状态
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="ACTIVE">已启用</option>
          <option value="DISABLED">已停用</option>
        </select>
      </label>
      <div className="button-row">
        <button className="primary" disabled={command.busy || !cityId}>
          {command.busy ? '保存中…' : '保存城市配置'}
        </button>
        <CommandMessage command={command} />
      </div>
    </form>
  );
}

function CityLocationForm({
  api,
  cityId,
  kind,
  onDone,
}: {
  api: AdminApiClient;
  cityId: string;
  kind: 'pickup' | 'poi';
  onDone: () => void;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const command = useCommand(() => {
    setCode('');
    setName('');
    setAddress('');
    setSortOrder('0');
    onDone();
  });
  useUnsavedNavigationGuard(Boolean(code || name || address || sortOrder !== '0') || command.busy);
  return (
    <form
      className="typed-form"
      onSubmit={(event) => {
        event.preventDefault();
        const path =
          kind === 'pickup'
            ? `/api/admin/m2/cities/${encodeURIComponent(cityId)}/pickup-areas`
            : `/api/admin/m2/cities/${encodeURIComponent(cityId)}/pois`;
        void command.run(
          () =>
            api.send(path, 'PUT', {
              code,
              name,
              sortOrder: numberValue(sortOrder),
              ...(kind === 'poi' && address ? { address } : {}),
            }),
          kind === 'pickup' ? '接送区域已新增' : '推荐地点已新增',
        );
      }}
    >
      <label>
        {kind === 'pickup' ? '区域编码' : '地点编码'}
        <input required value={code} onChange={(event) => setCode(event.target.value)} />
      </label>
      <label>
        {kind === 'pickup' ? '区域名称' : '地点名称'}
        <input required value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      {kind === 'poi' && (
        <label>
          地址说明
          <input value={address} onChange={(event) => setAddress(event.target.value)} />
        </label>
      )}
      <label>
        展示排序
        <input
          type="number"
          value={sortOrder}
          onChange={(event) => setSortOrder(event.target.value)}
        />
      </label>
      <div className="button-row">
        <button className="primary" disabled={command.busy}>
          {command.busy ? '保存中…' : kind === 'pickup' ? '新增接送区域' : '新增推荐地点'}
        </button>
        <CommandMessage command={command} />
      </div>
    </form>
  );
}

export function CanonicalCityDetailPage({
  api,
  targetAppId,
  pageId,
  cityId,
}: BaseProps & { pageId: string; cityId: string }) {
  const access = useApi(api, '/api/admin/m8/security/me');
  const profile = unwrapRecord(access.data);
  const canManage =
    Array.isArray(profile.permissionCodes) && profile.permissionCodes.includes('city.manage');
  const state = useApi(
    api,
    cityId ? `/api/admin/m2/cities/${encodeURIComponent(cityId)}` : undefined,
  );
  const [loadedCity, setLoadedCity] = useState<{ id: string; data: JsonRecord }>();
  useEffect(() => {
    if (state.data && !state.loading && !state.error)
      setLoadedCity({ id: cityId, data: unwrapRecord(state.data) });
  }, [cityId, state.data, state.loading, state.error]);
  const city = state.data
    ? unwrapRecord(state.data)
    : loadedCity?.id === cityId
      ? loadedCity.data
      : {};
  const hasLoadedCity = Object.keys(city).length > 0;
  const displayState = hasLoadedCity ? { ...state, loading: false, error: undefined } : state;
  const pickup = nestedRows(city, 'pickupAreas');
  const pois = nestedRows(city, 'pois');
  const isLocations = pageId === 'ADM-B03';
  return (
    <div className="r2-page r2-city-workspace">
      <Header
        title={
          isLocations
            ? `${clean(city.name, '城市')} · 服务区域与景点`
            : `${canManage ? '编辑' : '查看'}城市：${clean(city.name, '城市')}`
        }
        subtitle={isLocations ? '接送服务范围与行程景点维护' : '城市基础资料与运营状态'}
        actions={
          <>
            <a className="button" href="/admin/cities">
              返回城市列表
            </a>
            <a
              className="button"
              href={
                isLocations
                  ? `/admin/cities/${encodeURIComponent(cityId)}`
                  : `/admin/cities/${encodeURIComponent(cityId)}/poi`
              }
            >
              {isLocations ? '基础信息' : '服务区域与景点'}
            </a>
            <button onClick={state.reload}>刷新数据</button>
          </>
        }
      />
      {hasLoadedCity && state.loading && (
        <span className="r2-city-retention" role="status">
          正在刷新列表，未保存的输入会保留…
        </span>
      )}
      {hasLoadedCity && Boolean(state.error) && (
        <Notice tone="error">
          列表刷新失败，当前输入已保留。
          <button type="button" onClick={state.reload}>
            重试读取
          </button>
        </Notice>
      )}
      <State state={displayState} count={Object.keys(city).length} noun="城市配置">
        {!canManage && <Notice>当前账号可查看城市信息，维护城市和景点需单独授权。</Notice>}
        {isLocations ? (
          <div className="r2-location-workspace">
            <Panel title={`接送区域（${pickup.length}）`}>
              <Table heads={['区域名称', '范围说明', '状态']} empty={!pickup.length}>
                {pickup.map((item) => (
                  <tr key={textOf(item, 'id')}>
                    <td>{clean(item.name)}</td>
                    <td>{clean(item.description, '—')}</td>
                    <td>
                      <Pill value={item.status} />
                    </td>
                  </tr>
                ))}
              </Table>
              {canManage && (
                <div className="r2-inline-create">
                  <h3>新增接送区域</h3>
                  <CityLocationForm api={api} cityId={cityId} kind="pickup" onDone={state.reload} />
                </div>
              )}
            </Panel>
            <Panel title={`推荐地点（${pois.length}）`}>
              <Table heads={['地点名称', '地址说明', '状态']} empty={!pois.length}>
                {pois.map((poi) => (
                  <tr key={textOf(poi, 'id')}>
                    <td>{clean(poi.name)}</td>
                    <td>{clean(poi.address ?? poi.description, '—')}</td>
                    <td>
                      <Pill value={poi.status} />
                    </td>
                  </tr>
                ))}
              </Table>
              {canManage && (
                <div className="r2-inline-create">
                  <h3>新增推荐地点</h3>
                  <CityLocationForm api={api} cityId={cityId} kind="poi" onDone={state.reload} />
                </div>
              )}
            </Panel>
          </div>
        ) : (
          <Panel title="基础信息">
            {canManage ? (
              <CityEditForm api={api} city={city} onDone={state.reload} />
            ) : (
              <dl className="r2-city-context">
                <dt>城市名称</dt>
                <dd>{clean(city.name)}</dd>
                <dt>城市编码</dt>
                <dd>{clean(city.code)}</dd>
                <dt>运营状态</dt>
                <dd>
                  <Pill value={city.status} />
                </dd>
                <dt>说明</dt>
                <dd>{clean(city.description, '—')}</dd>
              </dl>
            )}
            <div className="r2-city-context">
              <a href={`/admin/cities/${encodeURIComponent(cityId)}/poi`}>
                接送区域 {pickup.length} · 推荐地点 {pois.length} →
              </a>
              <span>最近更新：{dateTime(city.updatedAt ?? city.createdAt)}</span>
              {profile.platformAccess === true && (
                <a href="/admin/security/users">维护人员数据权限 →</a>
              )}
            </div>
            <p className="r2-city-retention">
              城市停用后不再承载新售卖配置，历史订单和关联数据继续保留。
            </p>
          </Panel>
        )}
      </State>
      {!isLocations && canManage && (
        <MiniappPageLinks
          api={api}
          appId={targetAppId}
          targetType="CITY"
          targetId={cityId}
          title="城市与栏目推广地址"
        />
      )}
    </div>
  );
}

export function AppCreatePage({ api }: { api: AdminApiClient }) {
  const citiesState = useApi(api, '/api/admin/m2/cities');
  const cities = rows(citiesState.data).filter((city) => city.status === 'ACTIVE');
  const [createdId, setCreatedId] = useState('');
  const command = useCommand(() => window.dispatchEvent(new Event('travel:apps-changed')));
  const [appKey, setAppKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [environment, setEnvironment] = useState('test');
  const [wechatRef, setWechatRef] = useState('');
  const [cityIds, setCityIds] = useState<string[]>([]);
  const [defaultCityId, setDefaultCityId] = useState('');
  const [defaultOrderMode, setDefaultOrderMode] = useState('STANDARD');
  const [serviceEnabled, setServiceEnabled] = useState(false);
  const [serviceName, setServiceName] = useState('');
  const [servicePhone, setServicePhone] = useState('');
  const [serviceHours, setServiceHours] = useState('09:00-18:00');
  useUnsavedNavigationGuard(
    !createdId &&
      Boolean(
        appKey ||
        displayName ||
        wechatRef ||
        cityIds.length ||
        serviceName ||
        servicePhone ||
        environment !== 'test' ||
        defaultOrderMode !== 'STANDARD' ||
        serviceEnabled ||
        serviceHours !== '09:00-18:00',
      ),
  );
  if (createdId)
    return (
      <div className="r2-page app-create-page">
        <Header title="小程序已创建" subtitle="基础配置、开放城市与运行配置已保存" />
        <Panel title={displayName} hint="可以继续配置产品展示、微信接入与发布。">
          <Notice tone="success">已开放 {cityIds.length} 个城市，默认城市和下单方式已生效。</Notice>
          <p>微信登录、支付与上线仍需完成对应接入验证；创建配置不会自动发布小程序。</p>
          <div className="button-row">
            <a
              className="r2-button-link primary"
              href={`/admin/apps/${encodeURIComponent(createdId)}/settings`}
            >
              管理配置
            </a>
            <a className="r2-button-link" href="/admin/apps">
              返回小程序列表
            </a>
          </div>
        </Panel>
      </div>
    );
  return (
    <div className="r2-page app-create-page">
      <Header
        title="新增小程序"
        subtitle="基础资料 → 服务范围 → 客服入口"
        actions={
          <>
            <a className="r2-button-link" href="/admin/apps">
              返回列表
            </a>
            <button
              type="submit"
              form="app-create-form"
              className="primary"
              disabled={
                command.busy ||
                citiesState.loading ||
                !!citiesState.error ||
                !cityIds.length ||
                !defaultCityId
              }
            >
              {command.busy ? '创建中…' : '保存并创建小程序'}
            </button>
          </>
        }
      />
      <form
        id="app-create-form"
        className="app-create-form"
        onSubmit={(event) => {
          event.preventDefault();
          void command.run(async () => {
            const result = await api.send('/api/admin/m2/apps', 'POST', {
              appKey: appKey.trim(),
              displayName: displayName.trim(),
              environment,
              ...(wechatRef.trim() ? { wechatAppIdRef: wechatRef.trim() } : {}),
              cityIds,
              defaultCityId,
              defaultOrderMode,
              customerServiceConfig: serviceEnabled
                ? {
                    enabled: true,
                    displayName: serviceName.trim(),
                    servicePhone: servicePhone.trim(),
                    serviceHours: serviceHours.trim(),
                  }
                : { enabled: false },
            });
            setCreatedId(textOf(unwrapRecord(result), 'id'));
          }, '小程序配置已创建');
        }}
      >
        <section className="app-create-section" aria-labelledby="app-create-basics">
          <header>
            <h2 id="app-create-basics">基础信息</h2>
            <span>必填资料</span>
          </header>
          <fieldset disabled={command.busy} className="app-create-grid">
            <label>
              小程序名称 *
              <input
                required
                maxLength={150}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="例如：昆明自由行"
              />
            </label>
            <label>
              业务编码 *
              <input
                required
                pattern="[a-z][a-z0-9\-]{2,99}"
                maxLength={100}
                value={appKey}
                onChange={(event) => setAppKey(event.target.value)}
                placeholder="例如：kunming-travel"
              />
              <small>小写字母开头，3–100 位；创建后固定。</small>
            </label>
            <label>
              运行环境 *
              <select value={environment} onChange={(event) => setEnvironment(event.target.value)}>
                <option value="test">测试</option>
                <option value="staging">预发布</option>
                <option value="production">正式运营</option>
                <option value="dev">开发</option>
                <option value="local">本地</option>
              </select>
            </label>
            <label>
              微信 AppID{' '}
              {['staging', 'production'].includes(environment) ? '*' : '（测试时可留空）'}
              <input
                required={['staging', 'production'].includes(environment)}
                pattern="wx[0-9a-f]{16}"
                maxLength={18}
                value={wechatRef}
                onChange={(event) => setWechatRef(event.target.value)}
                placeholder="wx 开头的 18 位 AppID"
              />
              <small>微信公众平台 → 开发管理，非 AppSecret。</small>
            </label>
          </fieldset>
        </section>
        <section className="app-create-section" aria-labelledby="app-create-cities">
          <header>
            <h2 id="app-create-cities">服务城市与下单</h2>
            <span>已选 {cityIds.length} 个城市</span>
          </header>
          <div className="app-create-section-body">
            <State state={citiesState} count={cities.length} noun="城市" allowEmpty>
              {!cities.length ? (
                <Notice tone="warning">暂无可用城市，请先在城市管理中添加或启用城市。</Notice>
              ) : (
                <fieldset
                  disabled={command.busy}
                  className="app-create-grid app-create-service-grid"
                >
                  <div className="app-city-options">
                    <strong>开放城市 *</strong>
                    <div>
                      {cities.map((city) => {
                        const id = textOf(city, 'id');
                        return (
                          <label key={id} className={cityIds.includes(id) ? 'is-selected' : ''}>
                            <input
                              type="checkbox"
                              checked={cityIds.includes(id)}
                              onChange={(event) => {
                                const next = event.target.checked
                                  ? [...cityIds, id]
                                  : cityIds.filter((value) => value !== id);
                                setCityIds(next);
                                if (!next.includes(defaultCityId)) setDefaultCityId(next[0] ?? '');
                              }}
                            />
                            {textOf(city, 'name')}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <label>
                    默认城市 *
                    <select
                      required
                      disabled={!cityIds.length}
                      value={defaultCityId}
                      onChange={(event) => setDefaultCityId(event.target.value)}
                    >
                      <option value="">请先选择开放城市</option>
                      {cities
                        .filter((city) => cityIds.includes(textOf(city, 'id')))
                        .map((city) => (
                          <option key={textOf(city, 'id')} value={textOf(city, 'id')}>
                            {textOf(city, 'name')}
                          </option>
                        ))}
                    </select>
                    <small>用户首次进入时展示此城市。</small>
                  </label>
                  <label>
                    默认下单方式
                    <select
                      value={defaultOrderMode}
                      onChange={(event) => setDefaultOrderMode(event.target.value)}
                    >
                      <option value="STANDARD">标准下单</option>
                      <option value="QUICK">快捷下单</option>
                    </select>
                    <small>具体产品仍按各自的预订规则执行。</small>
                  </label>
                </fieldset>
              )}
            </State>
          </div>
        </section>
        <section className="app-create-section" aria-labelledby="app-create-support">
          <header>
            <h2 id="app-create-support">客服设置</h2>
            <span>选填</span>
          </header>
          <fieldset disabled={command.busy} className="app-create-grid app-create-support-grid">
            <label className="app-city-options checkbox-row">
              <input
                type="checkbox"
                checked={serviceEnabled}
                onChange={(event) => setServiceEnabled(event.target.checked)}
              />
              启用客服入口
            </label>
            {!serviceEnabled && (
              <span className="app-create-support-status">暂不向用户展示客服入口</span>
            )}
            {serviceEnabled && (
              <>
                <label>
                  客服名称 *
                  <input
                    required
                    maxLength={100}
                    value={serviceName}
                    onChange={(event) => setServiceName(event.target.value)}
                  />
                </label>
                <label>
                  客服电话 *
                  <input
                    type="tel"
                    required
                    maxLength={30}
                    value={servicePhone}
                    onChange={(event) => setServicePhone(event.target.value)}
                  />
                </label>
                <label>
                  服务时间 *
                  <input
                    required
                    maxLength={100}
                    value={serviceHours}
                    onChange={(event) => setServiceHours(event.target.value)}
                  />
                </label>
              </>
            )}
          </fieldset>
        </section>
        <div className="app-create-feedback" aria-live="polite">
          <CommandMessage command={command} />
        </div>
      </form>
    </div>
  );
}

function AppDeleteButton({
  api,
  app,
  onDone,
}: {
  api: AdminApiClient;
  app: JsonRecord;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const name = textOf(app, 'displayName');
  const command = useCommand(() => {
    setOpen(false);
    onDone();
    window.dispatchEvent(new Event('travel:apps-changed'));
  });
  return (
    <>
      <button
        className="app-delete-link"
        type="button"
        onClick={() => {
          setConfirmation('');
          setOpen(true);
        }}
      >
        删除
      </button>
      <ConfirmDialog
        open={open}
        title={`删除小程序「${name}」`}
        dangerous
        pending={command.busy}
        confirmDisabled={confirmation !== name}
        confirmLabel="确认删除"
        onCancel={() => {
          if (!command.busy) setOpen(false);
        }}
        onConfirm={() => {
          if (confirmation === name)
            void command.run(
              () =>
                api.send(`/api/admin/m2/apps/${encodeURIComponent(textOf(app, 'id'))}`, 'DELETE', {
                  confirmation,
                }),
              '小程序已删除，历史订单已保留',
            );
        }}
        description={
          <div className="app-delete-description">
            <p>
              将移除该小程序，并清理城市关联、运行配置、产品覆盖、装修、发布等关联数据。删除后不能恢复。
            </p>
            <p>
              已产生的订单及支付、退款、履约和结算必要记录保留，仍可在订单和财务模块查询。共享城市、产品及其他小程序不受影响。
            </p>
            <label>
              输入小程序完整名称确认
              <input
                autoFocus
                value={confirmation}
                disabled={command.busy}
                onChange={(event) => setConfirmation(event.target.value)}
                placeholder={name}
              />
            </label>
            {confirmation !== name && <small>名称一致后才可执行删除。</small>}
            <CommandMessage command={command} />
          </div>
        }
      />
    </>
  );
}

export function CanonicalAppPage(props: BaseProps & { pageId: string; appId?: string }) {
  const configurationPages: Record<string, ConfigurationSection> = {
    'ADM-B05': 'base',
    'ADM-B06': 'cities',
    'ADM-B07': 'products',
    'ADM-B08': 'service',
    'ADM-B11': 'payment',
  };
  const section = configurationPages[props.pageId];
  const access = useApi(props.api, section ? '/api/admin/m8/security/me' : undefined);
  const menus = unwrapRecord(access.data).visibleMenuIds;
  if (section && access.loading) return <div className="r2-state">正在确认配置权限…</div>;
  if (section && (!Array.isArray(menus) || !menus.includes(props.pageId)))
    return <Notice>当前账号可查看小程序档案，维护此配置需单独授权。</Notice>;
  if (section)
    return (
      <AppConfigurationWorkbench
        key={props.appId ?? props.targetAppId}
        api={props.api}
        appId={props.appId ?? props.targetAppId}
        initialSection={section}
      />
    );
  return <AppDirectoryPage {...props} />;
}

function AppDirectoryPage({
  api,
  targetAppId,
  pageId,
  appId,
}: BaseProps & { pageId: string; appId?: string }) {
  const access = useApi(api, '/api/admin/m8/security/me');
  const profile = unwrapRecord(access.data);
  const menuIds = Array.isArray(profile.visibleMenuIds) ? profile.visibleMenuIds : [];
  const canConfigure = menuIds.includes('ADM-B05');
  const canCreate = menuIds.includes('ADM-B10');
  const canDelete =
    canConfigure &&
    Array.isArray(profile.permissionCodes) &&
    profile.permissionCodes.includes('app.manage');
  const listMode = ['ADM-B04', 'ADM-B09'].includes(pageId);
  const state = useApi(
    api,
    listMode
      ? '/api/admin/m2/apps'
      : appId
        ? `/api/admin/m2/apps/${encodeURIComponent(appId)}`
        : undefined,
  );
  const apps = rows(state.data);
  const ordersState = useApi(
    api,
    pageId === 'ADM-B09' && targetAppId
      ? `/api/admin/m4/orders?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const orders = rows(ordersState.data);
  const [appKeyword, setAppKeyword] = useState('');
  const [appStatus, setAppStatus] = useState('ALL');
  const [appEnvironment, setAppEnvironment] = useState('ALL');
  const [appPage, setAppPage] = useState(1);
  useEffect(() => setAppPage(1), [appKeyword, appStatus, appEnvironment]);
  const visibleApps = apps.filter((item) => {
    const keyword = appKeyword.trim().toLowerCase();
    const status = textOf(item, 'status');
    const keywordMatched =
      !keyword ||
      [item.displayName, item.appKey, item.environment, item.wechatAppIdRef]
        .map((value) => clean(value, '').toLowerCase())
        .some((value) => value.includes(keyword));
    const statusMatched = appStatus === 'ALL' || status === appStatus;
    return (
      keywordMatched &&
      statusMatched &&
      (appEnvironment === 'ALL' || item.environment === appEnvironment)
    );
  });
  const appPageCount = Math.max(1, Math.ceil(visibleApps.length / 20));
  const currentAppPage = Math.min(appPage, appPageCount);
  const openOrders = orders.filter((order) => isOpenOrder(textOf(order, 'status')));
  const afterSalesOrders = orders.filter((order) =>
    isActiveAfterSalesOrder(textOf(order, 'status')),
  );
  if (pageId === 'ADM-B10')
    return canCreate ? <AppCreatePage api={api} /> : <Notice>新增小程序需要平台管理权限。</Notice>;
  if (pageId === 'ADM-B04')
    return (
      <div className="r2-page r2-app-directory">
        <Header
          title="小程序矩阵"
          subtitle="小程序档案与运营配置"
          actions={
            <>
              {menuIds.includes('ADM-J08') && (
                <a className="r2-button-link" href="/admin/releases">
                  发布中心
                </a>
              )}
              <button onClick={state.reload}>刷新数据</button>
              {canCreate && (
                <a className="r2-button-link primary" href="/admin/apps/new">
                  + 新增小程序
                </a>
              )}
            </>
          }
        />
        <Panel title="小程序列表">
          <div className="r2-list-toolbar">
            <label>
              关键词
              <input
                value={appKeyword}
                onChange={(event) => setAppKeyword(event.target.value)}
                placeholder="输入小程序名称或业务编码"
              />
            </label>
            <label>
              运行环境
              <select
                value={appEnvironment}
                onChange={(event) => setAppEnvironment(event.target.value)}
              >
                <option value="ALL">全部环境</option>
                {Array.from(new Set(apps.map((item) => textOf(item, 'environment'))))
                  .filter(Boolean)
                  .map((environment) => (
                    <option key={environment} value={environment}>
                      {label(environment)}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              状态
              <select value={appStatus} onChange={(event) => setAppStatus(event.target.value)}>
                <option value="ALL">全部</option>
                <option value="ACTIVE">启用</option>
                <option value="DISABLED">停用</option>
              </select>
            </label>
            <button
              type="button"
              onClick={() => {
                setAppKeyword('');
                setAppStatus('ALL');
                setAppEnvironment('ALL');
              }}
            >
              重置
            </button>
          </div>
          <div className="r2-app-status-tabs" aria-label="按运营状态筛选">
            {(['ALL', 'ACTIVE', 'DISABLED'] as const).map((status) => (
              <button
                type="button"
                key={status}
                aria-pressed={appStatus === status}
                onClick={() => setAppStatus(status)}
              >
                {{ ALL: '全部', ACTIVE: '已启用', DISABLED: '已停用' }[status]}{' '}
                <span>
                  {status === 'ALL'
                    ? apps.length
                    : apps.filter((app) => app.status === status).length}
                </span>
              </button>
            ))}
          </div>
          <State state={state} count={visibleApps.length} noun="小程序" allowEmpty>
            <Table
              heads={[
                '小程序名称',
                '微信 AppID',
                '运行环境',
                '状态',
                '微信接入',
                '默认城市',
                '配置更新时间',
                '操作',
              ]}
              empty={!visibleApps.length}
            >
              {visibleApps.slice((currentAppPage - 1) * 20, currentAppPage * 20).map((item) => (
                <tr key={textOf(item, 'id')}>
                  <td>
                    <b>{clean(item.displayName)}</b>
                  </td>
                  <td>{clean(item.wechatAppIdRef, '未配置')}</td>
                  <td>{label(item.environment)}</td>
                  <td>
                    <Pill value={item.status} />
                  </td>
                  <td>{item.isFixture === true ? '开发测试配置' : '待外部验证'}</td>
                  <td>
                    {clean(nested(nested(item, 'runtimeConfig'), 'defaultCity').name, '未配置')}
                  </td>
                  <td>{dateTime(item.updatedAt)}</td>
                  <td className="app-row-actions">
                    {canConfigure && (
                      <a href={`/admin/apps/${encodeURIComponent(textOf(item, 'id'))}/settings`}>
                        管理配置
                      </a>
                    )}
                    {canDelete && <AppDeleteButton api={api} app={item} onDone={state.reload} />}
                    {!canConfigure && <span>仅查看</span>}
                  </td>
                </tr>
              ))}
            </Table>
          </State>
          <div className="r2-list-pagination">
            <span>共 {visibleApps.length} 条 · 每页 20 条</span>
            <div>
              <button disabled={currentAppPage <= 1} onClick={() => setAppPage(currentAppPage - 1)}>
                上一页
              </button>
              <span>
                {currentAppPage} / {appPageCount}
              </span>
              <button
                disabled={currentAppPage >= appPageCount}
                onClick={() => setAppPage(currentAppPage + 1)}
              >
                下一页
              </button>
            </div>
          </div>
        </Panel>
      </div>
    );
  if (pageId === 'ADM-B09')
    return (
      <div className="r2-page">
        <Header
          title="小程序运行状态"
          subtitle="多小程序管理 / 访问、配置与版本健康概览"
          actions={
            <button
              onClick={() => {
                state.reload();
                ordersState.reload();
              }}
            >
              刷新
            </button>
          }
        />
        <Metrics
          items={[
            { label: '已纳管小程序', value: apps.length },
            {
              label: '正常启用',
              value: apps.filter((a) => a.status === 'ACTIVE').length,
              accent: true,
            },
            { label: '停用', value: apps.filter((a) => a.status === 'DISABLED').length },
            { label: '当前待处理订单', value: openOrders.length, note: '当前选择小程序' },
            { label: '售后/退款关注', value: afterSalesOrders.length, note: '来自订单状态' },
          ]}
        />
        <Panel title="小程序状态列表" hint="只展示系统真实可得的配置与运营状态">
          <State state={state} count={apps.length} noun="小程序">
            <Table
              heads={['小程序', '环境', '微信接入', '当前状态', '最后更新', '运营入口']}
              empty={!apps.length}
            >
              {apps.map((item) => (
                <tr key={textOf(item, 'id')}>
                  <td>
                    <b>{clean(item.displayName)}</b>
                    <small className="r2-cell-note">
                      {configured(item.appKey, '业务编码待补')}
                    </small>
                  </td>
                  <td>{label(item.environment)}</td>
                  <td>{appAccessText(item)}</td>
                  <td>
                    <Pill value={item.status} />
                  </td>
                  <td>{dateTime(item.updatedAt)}</td>
                  <td>
                    {canConfigure && (
                      <a href={`/admin/apps/${encodeURIComponent(textOf(item, 'id'))}/settings`}>
                        详情
                      </a>
                    )}
                    {!canConfigure && <span>仅查看</span>}
                  </td>
                </tr>
              ))}
            </Table>
          </State>
        </Panel>
        <div className="r2-three">
          <Panel title="当前小程序订单关注">
            <State state={ordersState} count={orders.length} noun="订单" allowEmpty>
              <div className="r2-summary-list">
                <div>
                  <span>待处理订单</span>
                  <b>{openOrders.length}</b>
                </div>
                <div>
                  <span>售后/退款处理中</span>
                  <b>{afterSalesOrders.length}</b>
                </div>
                <div>
                  <span>最近订单</span>
                  <b>{orders[0] ? clean(orders[0].orderNo, '已有订单') : '无'}</b>
                </div>
              </div>
            </State>
          </Panel>
          <Panel title="服务能力状态">
            <div className="r2-check-list">
              <span>✓ 小程序配置按独立 App 范围读取</span>
              <span>✓ 生产密钥不在普通业务页面展示</span>
              <span>✓ 订单、支付、售后状态由服务端返回</span>
              <span>✓ 发布动作仍受发布中心 Gate 控制</span>
            </div>
          </Panel>
          <Panel title="最近运营事件">
            <State state={ordersState} count={openOrders.length} noun="待处理订单" allowEmpty>
              <div className="r2-select-list">
                {(openOrders.length ? openOrders : orders.slice(0, 2)).map((order) => (
                  <article key={textOf(order, 'id')}>
                    <div>
                      <strong>{clean(order.orderNo, '订单')}</strong>
                      <span>{clean(order.productTitle, '综合旅游服务')}</span>
                    </div>
                    <b>{orderAttentionText(order)}</b>
                  </article>
                ))}
              </div>
            </State>
          </Panel>
        </div>
      </div>
    );
  return null;
}

const productTabs = (
  id: string,
  pageId: string,
  statuses: Record<string, WorkbenchStatus> = {},
  appId = '',
) => [
  {
    label: '1 基本信息',
    href: productScopeHref(`/admin/products/${encodeURIComponent(id)}/basic`, appId),
    active: pageId === 'ADM-C02',
    ...(statuses.basic ? { status: statuses.basic } : {}),
  },
  {
    label: '推荐行程（选填）',
    href: productScopeHref(`/admin/products/${encodeURIComponent(id)}/itinerary`, appId),
    active: pageId === 'ADM-C03',
    ...(statuses.itinerary ? { status: statuses.itinerary } : {}),
  },
  {
    label: '3 多套餐',
    href: productScopeHref(`/admin/products/${encodeURIComponent(id)}/skus`, appId),
    active: pageId === 'ADM-C04',
    ...(statuses.skus ? { status: statuses.skus } : {}),
  },
  {
    label: '4 预订退款',
    href: productScopeHref(`/admin/products/${encodeURIComponent(id)}/booking-refund`, appId),
    active: pageId === 'ADM-C07',
    ...(statuses.rules ? { status: statuses.rules } : {}),
  },
  {
    label: '5 小程序覆盖',
    href: productScopeHref(`/admin/products/${encodeURIComponent(id)}/app-coverage`, appId),
    active: pageId === 'ADM-C09',
    ...(statuses.coverage ? { status: statuses.coverage } : {}),
  },
  {
    label: '6 发布检查',
    href: productScopeHref(`/admin/products/${encodeURIComponent(id)}/publish-check`, appId),
    active: pageId === 'ADM-C10',
    ...(statuses.readiness ? { status: statuses.readiness } : {}),
  },
];

function productScopeHref(href: string, appId: string) {
  if (!appId || appId === AUTHORIZED_SCOPE) return href;
  const [path, query = ''] = href.split('?');
  const params = new URLSearchParams(query);
  params.set('appId', appId);
  return `${path}?${params}`;
}

function skuPrice(sku: JsonRecord) {
  return money(sku.salePriceCents);
}

export function CanonicalProductPage(props: React.ComponentProps<typeof ProductPageContent>) {
  return (
    <div className="product-operations-workspace">
      <ProductPageContent {...props} />
    </div>
  );
}

export function CanonicalSkuPage(props: React.ComponentProps<typeof SkuPageContent>) {
  return (
    <div className="product-operations-workspace">
      <SkuPageContent {...props} />
    </div>
  );
}

export function CanonicalTagsSortPage(props: React.ComponentProps<typeof TagsSortPageContent>) {
  return (
    <div className="product-operations-workspace">
      <TagsSortPageContent {...props} />
    </div>
  );
}

const READINESS_LABELS: Record<string, string> = {
  PRODUCT_ACTIVE: '产品与城市状态允许激活',
  ACTIVE_SKU_EXISTS: '存在可售套餐',
  SKU_RULES_ACTIVE: '套餐规则有效',
  APP_EXPOSURE_ACTIVE: '小程序覆盖有效',
  PRODUCT_BASIC_INFO_COMPLETE: '产品基础信息完整',
  PRODUCT_MEDIA_READY: '产品媒体已配置',
  ROUTE_STOPS_READY: '推荐行程已配置',
  REFUND_POLICY_READY: '退款规则已配置',
};

function readinessLabel(check: JsonRecord, index: number) {
  const code = textOf(check, 'code');
  return clean(check.label ?? check.name ?? READINESS_LABELS[code], `发布条件检查 ${index + 1}`);
}

const READINESS_REPAIRS: Record<string, { step: string; reason: string }> = {
  PRODUCT_ACTIVE: { step: 'basic', reason: '产品已停用或所属城市未启用，请检查产品与城市状态' },
  ACTIVE_SKU_EXISTS: { step: 'skus', reason: '至少需要一个已启用的可售套餐' },
  SKU_RULES_ACTIVE: {
    step: 'booking-refund',
    reason: '请为已启用套餐绑定并启用服务日历、退款规则',
  },
  APP_EXPOSURE_ACTIVE: {
    step: 'app-coverage',
    reason: '请开启当前小程序的产品展示，并确认该小程序已开通产品所属城市',
  },
  PRODUCT_BASIC_INFO_COMPLETE: { step: 'basic', reason: '请补充产品名称、摘要和预订须知' },
  PRODUCT_MEDIA_READY: { step: 'basic', reason: '请上传并启用产品主图' },
  ROUTE_STOPS_READY: { step: 'itinerary', reason: '请补充推荐行程节点' },
  REFUND_POLICY_READY: { step: 'booking-refund', reason: '请为套餐绑定并启用退款规则' },
};

function readinessFailureReason(check: JsonRecord, index: number) {
  return clean(
    check.message ?? check.reason ?? READINESS_REPAIRS[textOf(check, 'code')]?.reason,
    `${readinessLabel(check, index)}未通过，请检查配置`,
  );
}

function yuanToMinorUnits(value: string) {
  const normalized = value.trim();
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(normalized);
  if (!match) throw new ApiError(400, 'VALIDATION_FAILED', '销售价请输入最多两位小数的金额');
  return `${BigInt(match[1] ?? '0') * 100n + BigInt((match[2] ?? '').padEnd(2, '0') || '0')}`;
}

function validateSkuEditor(input: {
  skuCode?: string;
  skuName: string;
  salePrice: string;
  minPeople: string;
  maxPeople: string;
  allowUnlimitedMax?: boolean;
  dateRuleId: string;
  refundPolicyId: string;
  includedItems: string;
  excludedItems: string;
  status?: string;
}) {
  if (
    input.skuCode !== undefined &&
    (input.skuCode.trim().length < 1 || input.skuCode.trim().length > 50)
  )
    return '套餐编码请输入 1—50 个字符';
  if (input.skuName.trim().length < 1 || input.skuName.trim().length > 100)
    return '套餐名称请输入 1—100 个字符';
  if (
    !/^(?:0|[1-9]\d{0,7})(?:\.\d{1,2})?$/.test(input.salePrice.trim()) ||
    Number(input.salePrice) <= 0
  )
    return '销售价请输入大于 0、最多两位小数的金额';
  const min = Number(input.minPeople),
    max = Number(input.maxPeople);
  const unlimited = input.allowUnlimitedMax && !input.maxPeople.trim();
  if (
    !Number.isInteger(min) ||
    min < 1 ||
    min > 999 ||
    (!unlimited && (!Number.isInteger(max) || max < 1 || max > 999))
  )
    return '人数请输入 1—999 的整数';
  if (!unlimited && min > max) return '最少人数不能大于最多人数';
  if (!input.dateRuleId || !input.refundPolicyId) return '请选择服务日历和退款规则';
  for (const [name, value] of [
    ['费用包含', input.includedItems],
    ['费用不含', input.excludedItems],
  ] as const) {
    const items = lines(value);
    if (items.length > 50) return `${name}最多填写 50 项`;
    if (items.some((item) => item.length > 100)) return `${name}的单项说明不能超过 100 个字符`;
  }
  return undefined;
}

function validateServiceItem(input: { code: string; name: string; description: string }) {
  if (input.code.trim().length < 1 || input.code.trim().length > 50)
    return '服务项编码请输入 1—50 个字符';
  if (input.name.trim().length < 1 || input.name.trim().length > 100)
    return '服务项名称请输入 1—100 个字符';
  if (input.description.trim().length > 500) return '游客可见说明不能超过 500 个字符';
  return undefined;
}

function clientCommandId(prefix: string) {
  const value = globalThis.crypto?.randomUUID?.();
  return `${prefix}-${value ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
}

function sortOrderOf(record: JsonRecord, fallback: number) {
  const raw = Number(record.sortOrder ?? record.sequence ?? fallback);
  return Number.isFinite(raw) ? raw : fallback;
}

function bySortOrder(items: JsonRecord[]) {
  return [...items].sort(
    (left, right) =>
      sortOrderOf(left, 0) - sortOrderOf(right, 0) ||
      textOf(left, 'id').localeCompare(textOf(right, 'id')),
  );
}

function activeRows(items: JsonRecord[]) {
  return items.filter((item) => textOf(item, 'status') !== 'DISABLED');
}

function lowestSkuPrice(skus: JsonRecord[]) {
  const prices = skus.map((sku) => Number(sku.salePriceCents)).filter(Number.isFinite);
  return prices.length ? money(Math.min(...prices)) : '待配置价格';
}

function primaryMedia(media: JsonRecord[]) {
  return bySortOrder(media).find((item) => textOf(item, 'status') !== 'DISABLED') ?? {};
}

export function productWorkbenchStatuses({
  product,
  skus,
  stops,
  refundPolicies,
  dateRules,
  targetAppId,
  coverage,
  coverageLoaded,
  readiness,
  readinessLoaded,
}: {
  product: JsonRecord;
  skus: JsonRecord[];
  stops: JsonRecord[];
  media: JsonRecord[];
  refundPolicies: JsonRecord[];
  dateRules: JsonRecord[];
  targetAppId: string;
  coverage: JsonRecord[];
  coverageLoaded: boolean;
  readiness: JsonRecord;
  readinessLoaded: boolean;
}): Record<string, WorkbenchStatus> {
  const missingBasic = [['产品名称', product.title]]
    .filter(([, value]) => !String(value ?? '').trim())
    .map(([name]) => name);
  const activeSkus = skus.filter((sku) => textOf(sku, 'status') === 'ACTIVE');
  const validStops = stops.filter((stop) => Boolean(textOf(stop, 'name').trim()));
  const missingRules = activeSkus.filter(
    (sku) =>
      textOf(skuBoundDateRule(sku, dateRules), 'status') !== 'ACTIVE' ||
      textOf(skuBoundPolicy(sku, refundPolicies), 'status') !== 'ACTIVE',
  );
  const covered = coverage.some(
    (entry) =>
      textOf(entry, 'id') === textOf(product, 'id') &&
      nested(entry, 'appOverride').enabled === true,
  );
  const publishable = readiness.publishable === true;
  const failedChecks = nestedRows(readiness, 'checks').filter((check) => check.passed !== true);
  const readinessReason = clean(
    readiness.reason ?? readiness.message,
    publishable
      ? '服务端发布检查已通过'
      : failedChecks.length
        ? failedChecks.map(readinessFailureReason).join('；')
        : '服务端发布检查仍有未通过项，请进入发布检查查看',
  );
  return {
    basic: {
      state: missingBasic.length ? '待完善' : '已完善',
      reason: missingBasic.length
        ? `缺少：${missingBasic.join('、')}`
        : '线路名称已保存；图片与描述可继续补充',
    },
    itinerary: {
      state: validStops.length ? '已完善' : '选填',
      reason: validStops.length
        ? `${validStops.length} 个有效行程节点`
        : '推荐行程选填，未填写不影响保存与上架',
    },
    skus: {
      state: activeSkus.length ? '已完善' : '待完善',
      reason: activeSkus.length
        ? `${activeSkus.length} 个已启用套餐`
        : '至少需要一个已启用的可售套餐（草稿套餐不计入）',
    },
    rules: {
      state: activeSkus.length > 0 && missingRules.length === 0 ? '已完善' : '待完善',
      reason: activeSkus.length
        ? missingRules.length
          ? `${missingRules.length} 个套餐的服务日历或退款规则缺失、未启用`
          : '所有有效套餐规则齐全'
        : '请先配置并启用可售套餐',
    },
    coverage: !targetAppId
      ? { state: '需选择小程序', reason: '请先在本页筛选区选择操作小程序' }
      : coverageLoaded
        ? {
            state: covered ? '已完善' : '待完善',
            reason: covered ? '当前小程序已启用该产品' : '当前小程序尚未启用该产品',
          }
        : { state: '未检查', reason: '覆盖数据尚未成功读取' },
    readiness: !targetAppId
      ? { state: '需选择小程序', reason: '请先在本页筛选区选择操作小程序' }
      : readinessLoaded
        ? {
            state: publishable ? '已完善' : '待完善',
            reason: readinessReason,
          }
        : { state: '未检查', reason: '尚未取得服务端发布检查结果' },
  };
}

function productDisplayChecks(
  product: JsonRecord,
  skus: JsonRecord[],
  stops: JsonRecord[],
  media: JsonRecord[],
) {
  const activeSkus = activeRows(skus);
  return [
    {
      label: '产品名称',
      passed: Boolean(textOf(product, 'title')),
      note: clean(product.title, '待填写'),
    },
    {
      label: '所属城市',
      passed: Boolean(textOf(nested(product, 'city'), 'name') || textOf(product, 'cityId')),
      note: clean(nested(product, 'city').name ?? product.cityId, '待绑定'),
    },
    {
      label: '产品主图',
      passed: media.length > 0 || Boolean(textOf(product, 'coverMediaUrl')),
      note: media.length ? `${media.length} 项媒体` : clean(product.coverMediaUrl, '待上传'),
    },
    {
      label: '推荐行程',
      passed: stops.length > 0,
      note: stops.length ? `${stops.length} 个节点` : '待配置',
    },
    {
      label: '服务套餐',
      passed: activeSkus.length > 0,
      note: activeSkus.length ? `${activeSkus.length} 个可用套餐` : '待配置',
    },
    {
      label: '游客须知',
      passed: Boolean(textOf(product, 'bookingNotice')),
      note: textOf(product, 'bookingNotice') ? '已配置' : '待配置',
    },
  ];
}

function serviceModeSummary(skus: JsonRecord[]) {
  const modes = [
    ...new Set(
      activeRows(skus)
        .map((sku) => label(sku.serviceMode))
        .filter(Boolean),
    ),
  ];
  return modes.length ? modes.join(' / ') : '待配置服务模式';
}

function bookingPeopleSummary(skus: JsonRecord[]) {
  const ranges = activeRows(skus)
    .map((sku) => peopleRange(sku.minPeople, sku.maxPeople))
    .filter((value) => value !== '未限定');
  return ranges.length ? [...new Set(ranges)].join(' / ') : '按套餐设置';
}

function refundRuleText(policy: JsonRecord) {
  const rules = isRecord(policy.rules) ? policy.rules : {};
  const hours = Number(rules.cancelBeforeHours);
  const percent = Number(rules.refundPercent);
  if (Number.isFinite(hours) && Number.isFinite(percent)) {
    return `出发前 ${hours} 小时，可退 ${percent}%`;
  }
  return clean(policy.notice ?? policy.description, '规则已配置，按订单快照执行');
}

function calendarRuleText(rule: JsonRecord) {
  const dateRule = isRecord(rule.dateRule) ? rule.dateRule : {};
  const weekdays = Array.isArray(dateRule.weekdays)
    ? dateRule.weekdays.filter((day): day is number => typeof day === 'number')
    : [];
  const advance = isRecord(rule.advanceBookingRule)
    ? Number(rule.advanceBookingRule.minimumHours)
    : Number.NaN;
  const blackoutDates = Array.isArray(rule.blackoutDates) ? rule.blackoutDates.length : 0;
  const parts = [
    weekdays.length ? `每周 ${weekdays.length} 天可订` : '按运营日历判断',
    Number.isFinite(advance) ? `最少提前 ${advance} 小时` : undefined,
    blackoutDates ? `${blackoutDates} 个不可预订日期` : undefined,
  ].filter(Boolean);
  return parts.join(' · ');
}

function skuBoundPolicy(sku: JsonRecord, refundPolicies: JsonRecord[]) {
  const embedded = nested(sku, 'refundPolicy');
  if (Object.keys(embedded).length) return embedded;
  const id = textValue(sku, 'refundPolicyId');
  return refundPolicies.find((policy) => textValue(policy, 'id') === id) ?? {};
}

function skuBoundDateRule(sku: JsonRecord, dateRules: JsonRecord[]) {
  const embedded = nested(sku, 'dateRule');
  if (Object.keys(embedded).length) return embedded;
  const id = textValue(sku, 'dateRuleId');
  return dateRules.find((rule) => textValue(rule, 'id') === id) ?? {};
}

function tagLabel(tag: JsonRecord) {
  return clean(tag.name ?? tag.tagName ?? tag.code, '未命名标签');
}

function tagScopeLabel(tag: JsonRecord) {
  if (textValue(tag, 'cityId')) return '指定城市标签';
  if (textValue(tag, 'appId')) return '小程序自定义标签';
  return '系统标签';
}

function productTagRecords(product: JsonRecord) {
  const explicit = nestedRows(product, 'tags')
    .map((item) => {
      const tag = nested(item, 'tag');
      return Object.keys(tag).length ? tag : item;
    })
    .filter((tag) => textValue(tag, 'name') || textValue(tag, 'tagName') || textValue(tag, 'code'));
  if (explicit.length) return explicit;
  const overrideNames = Array.isArray(product.tagNamesOverride)
    ? product.tagNamesOverride.filter((item): item is string => typeof item === 'string')
    : [];
  return overrideNames.map((name, index) => ({
    id: `override-${index}`,
    name,
    source: 'APP_OVERRIDE',
  }));
}

function skuRuleChips(
  sku: JsonRecord,
  dateRules: JsonRecord[],
  refundPolicies: JsonRecord[],
): string[] {
  const chips = [label(sku.ticketMode), label(sku.guideMode)];
  const dateRule = skuBoundDateRule(sku, dateRules);
  const policy = skuBoundPolicy(sku, refundPolicies);
  chips.push(Object.keys(dateRule).length ? '已绑定服务日期规则' : '待绑定服务日历');
  chips.push(Object.keys(policy).length ? '已绑定退款规则' : '待绑定退款规则');
  return chips;
}

function MaintenanceDetails({ summary, children }: { summary: string; children: React.ReactNode }) {
  return (
    <section className="r2-maintenance product-maintenance-section">
      <h3>{summary}</h3>
      <div>{children}</div>
    </section>
  );
}

function ProductPhonePreview({
  product,
  skus,
  stops,
  media,
}: {
  product: JsonRecord;
  skus: JsonRecord[];
  stops: JsonRecord[];
  media: JsonRecord[];
}) {
  const hero = primaryMedia(media);
  const heroUrl = textOf(hero, 'url') || textOf(product, 'coverMediaUrl');
  const activeSkus = activeRows(skus);
  return (
    <div className="r2-phone">
      <div className={`r2-phone-media ${heroUrl ? 'has-image' : ''}`}>
        <span>{heroUrl ? clean(hero.altText, '产品主图') : '线路详情'}</span>
      </div>
      <h3>{clean(product.title, '未命名产品')}</h3>
      <p>{clean(product.subtitle ?? product.summary, '待补充副标题')}</p>
      <div className="r2-tag-list">
        {activeSkus.slice(0, 3).map((sku) => (
          <span key={textOf(sku, 'id')}>{label(sku.serviceMode)}</span>
        ))}
        {!activeSkus.length && <span>待配置套餐</span>}
      </div>
      <b>{lowestSkuPrice(activeSkus)}</b>
      <h4>推荐行程</h4>
      {bySortOrder(stops)
        .slice(0, 4)
        .map((stop, index) => (
          <p key={textOf(stop, 'id') || `${clean(stop.name)}-${index}`}>● {clean(stop.name)}</p>
        ))}
      {!stops.length && <p>● 待配置推荐行程</p>}
    </div>
  );
}

function ProductBasicForm({
  api,
  product,
  onDone,
  writeDenied,
  onPermissionDenied,
}: {
  api: AdminApiClient;
  product: JsonRecord;
  onDone: () => void;
  writeDenied: boolean;
  onPermissionDenied: () => void;
}) {
  const command = useCommand(onDone, { onPermissionDenied });
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [summary, setSummary] = useState('');
  const [bookingNotice, setBookingNotice] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [validation, setValidation] = useState('');
  const previousProductId = useRef('');
  const dirtyRef = useRef(false);
  const basicSignature = JSON.stringify([
    textValue(product, 'id'),
    textValue(product, 'title'),
    textValue(product, 'subtitle'),
    textValue(product, 'summary'),
    textValue(product, 'bookingNotice'),
    textValue(product, 'sortOrder', '0'),
  ]);
  const [acceptedSignature, setAcceptedSignature] = useState(basicSignature);
  const localSignature = JSON.stringify([
    textValue(product, 'id'),
    title,
    subtitle,
    summary,
    bookingNotice,
    sortOrder,
  ]);
  const localSignatureRef = useRef(localSignature);
  localSignatureRef.current = localSignature;
  useEffect(() => {
    if (
      previousProductId.current === textValue(product, 'id') &&
      dirtyRef.current &&
      localSignatureRef.current !== basicSignature
    )
      return;
    previousProductId.current = textValue(product, 'id');
    setAcceptedSignature(basicSignature);
    setTitle(textValue(product, 'title'));
    setSubtitle(textValue(product, 'subtitle'));
    setSummary(textValue(product, 'summary'));
    setBookingNotice(textValue(product, 'bookingNotice'));
    setSortOrder(textValue(product, 'sortOrder', '0'));
  }, [basicSignature, localSignature]);
  const productId = textValue(product, 'id');
  const dirty = localSignature !== acceptedSignature;
  dirtyRef.current = dirty;
  useUnsavedNavigationGuard(dirty || command.busy);
  return (
    <form
      className="typed-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (!title.trim()) {
          setValidation('请填写产品名称');
          return;
        }
        if (!Number.isSafeInteger(Number(sortOrder))) {
          setValidation('前端排序必须填写整数');
          return;
        }
        setValidation('');
        void command
          .run(() =>
            api.send(`/api/admin/m2/products/${encodeURIComponent(productId)}`, 'PATCH', {
              title: title.trim(),
              subtitle: subtitle.trim(),
              summary: summary.trim(),
              bookingNotice: bookingNotice.trim(),
              sortOrder: numberValue(sortOrder),
            }),
          )
          .then((saved) => {
            if (!saved) return;
            setTitle((current) => (current === title ? title.trim() : current));
            setSubtitle((current) => (current === subtitle ? subtitle.trim() : current));
            setSummary((current) => (current === summary ? summary.trim() : current));
            setBookingNotice((current) =>
              current === bookingNotice ? bookingNotice.trim() : current,
            );
            setSortOrder((current) =>
              current === sortOrder ? String(numberValue(sortOrder)) : current,
            );
          });
      }}
    >
      <label>
        产品名称
        <input required value={title} onChange={(event) => setTitle(event.target.value)} />
      </label>
      <label>
        前端排序
        <input
          type="number"
          value={sortOrder}
          onChange={(event) => setSortOrder(event.target.value)}
        />
      </label>
      <label>
        副标题（选填）
        <input value={subtitle} onChange={(event) => setSubtitle(event.target.value)} />
      </label>
      <label>
        线路摘要（选填）
        <textarea value={summary} onChange={(event) => setSummary(event.target.value)} />
      </label>
      <label>
        预订须知（可后补）
        <textarea
          value={bookingNotice}
          onChange={(event) => setBookingNotice(event.target.value)}
        />
      </label>
      {validation ? <Notice tone="error">{validation}</Notice> : null}
      <div className="button-row">
        <button className="primary" disabled={writeDenied || command.busy || !productId}>
          {command.busy ? '保存中…' : '保存产品草稿'}
        </button>
        <CommandMessage command={command} />
      </div>
      <PermissionBlocked visible={writeDenied} />
    </form>
  );
}

function ProductRescuePolicyForm({
  api,
  appId,
  productId,
  policy,
  onDone,
  writeDenied,
  onPermissionDenied,
}: {
  api: AdminApiClient;
  appId: string;
  productId: string;
  policy: JsonRecord;
  onDone: () => void;
  writeDenied: boolean;
  onPermissionDenied: () => void;
}) {
  const command = useCommand(onDone, { onPermissionDenied });
  const [enabled, setEnabled] = useState(false);
  const [amountYuan, setAmountYuan] = useState('50');
  const [validMinutes, setValidMinutes] = useState('10');
  const [maxExitPrompts, setMaxExitPrompts] = useState('3');
  const [validation, setValidation] = useState('');
  useEffect(() => {
    setEnabled(policy.enabled === true);
    setAmountYuan((Number(policy.amountCents ?? 5_000) / 100).toFixed(2));
    setValidMinutes(textValue(policy, 'validMinutes', '10'));
    setMaxExitPrompts(textValue(policy, 'maxExitPrompts', '3'));
  }, [policy]);
  const initialAmount = (Number(policy.amountCents ?? 5_000) / 100).toFixed(2);
  const dirty =
    enabled !== (policy.enabled === true) ||
    amountYuan !== initialAmount ||
    validMinutes !== textValue(policy, 'validMinutes', '10') ||
    maxExitPrompts !== textValue(policy, 'maxExitPrompts', '3');
  useUnsavedNavigationGuard(dirty || command.busy);
  return (
    <form
      className="typed-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const amount = Number(amountYuan);
        const minutes = Number(validMinutes);
        const prompts = Number(maxExitPrompts);
        if (
          !Number.isFinite(amount) ||
          amount <= 0 ||
          !Number.isSafeInteger(minutes) ||
          minutes < 1 ||
          minutes > 1440 ||
          !Number.isSafeInteger(prompts) ||
          prompts < 1 ||
          prompts > 10
        ) {
          setValidation('请填写有效的优惠金额、1–1440 分钟有效期和 1–10 次提醒次数');
          return;
        }
        setValidation('');
        void command.run(
          () =>
            api.send(
              `/api/admin/m5/product-rescue/${encodeURIComponent(productId)}?appId=${encodeURIComponent(appId)}`,
              'PUT',
              {
                enabled,
                amountCents: Math.round(numberValue(amountYuan) * 100),
                validMinutes: numberValue(validMinutes),
                maxExitPrompts: numberValue(maxExitPrompts),
              },
            ),
          enabled ? '挽留优惠已启用' : '挽留优惠已停用',
        );
      }}
    >
      <label className="r2-rescue-toggle">
        <span>
          <b>启用挽留优惠券</b>
          <small>仅在当前商品未支付订单发生取消、失败或返回时触发</small>
        </span>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
        />
      </label>
      {enabled && (
        <>
          <label>
            优惠金额（元）
            <input
              required
              type="number"
              min="0.01"
              max="10000"
              step="0.01"
              value={amountYuan}
              onChange={(event) => setAmountYuan(event.target.value)}
            />
          </label>
          <label>
            领取后有效期（分钟）
            <input
              required
              type="number"
              min="1"
              max="1440"
              value={validMinutes}
              onChange={(event) => setValidMinutes(event.target.value)}
            />
          </label>
          <label>
            离开时最多强提醒次数
            <input
              required
              type="number"
              min="1"
              max="10"
              value={maxExitPrompts}
              onChange={(event) => setMaxExitPrompts(event.target.value)}
            />
          </label>
          <div className="r2-form-footnote">
            同一用户、同一商品仅复用一笔待支付订单；优惠不累计，券后应付必须大于 0 元。
          </div>
        </>
      )}
      {validation ? <Notice tone="error">{validation}</Notice> : null}
      <div className="button-row">
        <button className="primary" disabled={writeDenied || command.busy || !appId || !productId}>
          {command.busy ? '保存中…' : '保存挽留设置'}
        </button>
        <CommandMessage command={command} />
      </div>
      <PermissionBlocked visible={writeDenied} />
    </form>
  );
}

function ProductMediaUploadForm({
  api,
  appId,
  productId,
  onDone,
  writeDenied,
  onPermissionDenied,
}: {
  api: AdminApiClient;
  appId: string;
  productId: string;
  onDone: () => void;
  writeDenied: boolean;
  onPermissionDenied: () => void;
}) {
  const command = useCommand(onDone, {
    onPermissionDenied,
    formatError: (error) => {
      if (
        error instanceof ApiError &&
        /product must be configured for the target app/iu.test(error.message)
      )
        return '图片尚未上传。请核对本页小程序与线路所属城市后重试；无需先上架线路，已填写资料与未完成图片会保留。';
      if (error instanceof Error && /[\u3400-\u9fff]/u.test(error.message)) return error.message;
      if (error instanceof ApiError && error.isPermissionError)
        return '当前账号没有上传图片的权限，请联系管理员；已填写资料会保留。';
      return '图片上传未完成，请稍后重试；已填写资料与未完成图片会保留。';
    },
  });
  const fileInput = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [altText, setAltText] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [validation, setValidation] = useState('');
  const dirty = files.length > 0 || altText.trim().length > 0 || sortOrder !== '0';
  useUnsavedNavigationGuard(dirty || command.busy);
  return (
    <form
      className="typed-form r2-media-upload"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (!appId || appId === AUTHORIZED_SCOPE) {
          setValidation('请先在本页顶部选择图片所属的小程序，再上传图片。');
          return;
        }
        if (files.length === 0) {
          setValidation('请选择至少一张产品图片');
          return;
        }
        const invalidFile = files.find(
          (file) =>
            !['image/jpeg', 'image/png'].includes(file.type) || file.size > 10 * 1024 * 1024,
        );
        if (invalidFile) {
          setValidation(`“${invalidFile.name}”不是 JPEG/PNG，或文件超过 10MB`);
          return;
        }
        if (!Number.isSafeInteger(Number(sortOrder))) {
          setValidation('展示顺序必须填写整数');
          return;
        }
        setValidation('');
        void command.run(async () => {
          const baseSortOrder = numberValue(sortOrder);
          let uploaded = 0;
          try {
            for (const [index, file] of files.entries()) {
              const form = new FormData();
              form.append('file', file);
              if (altText.trim()) form.append('altText', altText.trim());
              form.append('sortOrder', String(baseSortOrder + index));
              await api.upload(
                `/api/admin/m2/apps/${encodeURIComponent(appId)}/products/${encodeURIComponent(productId)}/media/upload`,
                form,
                clientCommandId('product-media'),
              );
              uploaded += 1;
              setFiles((pending) => pending.filter((item) => item !== file));
            }
          } catch (error) {
            if (uploaded > 0) {
              setSortOrder(String(baseSortOrder + uploaded));
              onDone();
            }
            throw error;
          }
          setFiles([]);
          setAltText('');
          setSortOrder('0');
          if (fileInput.current) fileInput.current.value = '';
        }, `${files.length} 张产品图片已优化并上传`);
      }}
    >
      <label>
        产品图片
        <input
          required
          ref={fileInput}
          type="file"
          multiple
          accept="image/jpeg,image/png"
          disabled={command.busy || writeDenied}
          onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
        />
        <small>可多选 JPG/PNG，每张不超过 10MB。系统自动优化大小并保留清晰度，不裁切图片。</small>
      </label>
      {!appId || appId === AUTHORIZED_SCOPE ? (
        <Notice>请先在本页顶部选择图片所属的小程序，再选择并上传图片。</Notice>
      ) : null}
      <label>
        图片说明
        <input
          maxLength={255}
          disabled={command.busy || writeDenied}
          value={altText}
          onChange={(event) => setAltText(event.target.value)}
          placeholder="例如：洱海环线实景"
        />
      </label>
      <label>
        展示顺序
        <input
          type="number"
          disabled={command.busy || writeDenied}
          value={sortOrder}
          onChange={(event) => setSortOrder(event.target.value)}
        />
      </label>
      {validation ? <Notice tone="error">{validation}</Notice> : null}
      {files.length > 0 && (
        <p className="product-upload-selection">
          待上传 {files.length} 张：{files.map((file) => file.name).join('、')}
        </p>
      )}
      <div className="button-row">
        <button
          className="primary"
          disabled={
            writeDenied ||
            command.busy ||
            files.length === 0 ||
            !appId ||
            appId === AUTHORIZED_SCOPE ||
            !productId
          }
        >
          {command.busy
            ? '上传中…'
            : `上传产品图片${files.length > 1 ? `（${files.length} 张）` : ''}`}
        </button>
        <CommandMessage command={command} />
      </div>
      <PermissionBlocked visible={writeDenied} />
    </form>
  );
}

function expectedOrder(items: JsonRecord[]) {
  return bySortOrder(items).map((item, index) => ({
    id: textOf(item, 'id'),
    sortOrder: sortOrderOf(item, index),
  }));
}

function movedIds(items: JsonRecord[], selectedId: string, targetIndex: number) {
  const ordered = bySortOrder(items).map((item) => textOf(item, 'id'));
  const currentIndex = ordered.indexOf(selectedId);
  if (currentIndex < 0 || targetIndex < 0 || targetIndex >= ordered.length) return ordered;
  ordered.splice(currentIndex, 1);
  ordered.splice(targetIndex, 0, selectedId);
  return ordered;
}

function ProductMediaManager({
  api,
  productId,
  media,
  onDone,
  writeDenied,
  onPermissionDenied,
}: {
  api: AdminApiClient;
  productId: string;
  media: JsonRecord[];
  onDone: () => void;
  writeDenied: boolean;
  onPermissionDenied: () => void;
}) {
  const [deleteMedia, setDeleteMedia] = useState<JsonRecord>();
  const command = useCommand(onDone, {
    onPermissionDenied: () => {
      setDeleteMedia(undefined);
      onPermissionDenied();
    },
  });
  const ordered = bySortOrder(media);
  const reorder = (mediaId: string, targetIndex: number, success: string) =>
    command.run(
      () =>
        api.send(`/api/admin/m2/products/${encodeURIComponent(productId)}/media/reorder`, 'POST', {
          orderedIds: movedIds(ordered, mediaId, targetIndex),
          expectedOrder: expectedOrder(ordered),
        }),
      success,
    );
  if (!ordered.length)
    return (
      <div className="product-media-empty">
        <ProductThumbnail api={api} productId={productId} alt="线路图片示意图" />
        <p>暂无产品图片，请在下方上传线路图片</p>
      </div>
    );
  return (
    <>
      <div className="r2-product-media-grid">
        {ordered.map((item, index) => {
          const id = textOf(item, 'id');
          const active = textOf(item, 'status') !== 'DISABLED';
          return (
            <article key={id} className={index === 0 && active ? 'primary' : ''}>
              <div className="r2-product-media-thumb">
                <ProductThumbnail
                  api={api}
                  productId={productId}
                  src={textOf(item, 'url')}
                  alt={clean(item.altText, `产品图片 ${index + 1}`)}
                />
                {index === 0 && active ? <b>当前主图</b> : null}
              </div>
              <div>
                <strong>{clean(item.altText, '未填写图片说明')}</strong>
                <small>
                  排序 {sortOrderOf(item, index)} · {active ? '启用' : '停用'}
                </small>
              </div>
              <div className="r2-record-actions">
                <button
                  type="button"
                  disabled={writeDenied || command.busy || index === 0 || !active}
                  onClick={() => void reorder(id, 0, '主图已更新')}
                >
                  设为主图
                </button>
                <button
                  type="button"
                  aria-label={`上移 ${clean(item.altText, id)}`}
                  disabled={writeDenied || command.busy || index === 0}
                  onClick={() => void reorder(id, index - 1, '图片顺序已更新')}
                >
                  上移
                </button>
                <button
                  type="button"
                  aria-label={`下移 ${clean(item.altText, id)}`}
                  disabled={writeDenied || command.busy || index === ordered.length - 1}
                  onClick={() => void reorder(id, index + 1, '图片顺序已更新')}
                >
                  下移
                </button>
                <button
                  type="button"
                  className="danger-link"
                  disabled={writeDenied || command.busy}
                  onClick={() => setDeleteMedia(item)}
                >
                  删除
                </button>
              </div>
            </article>
          );
        })}
      </div>
      <PermissionBlocked visible={writeDenied} />
      <CommandMessage command={command} />
      <ConfirmDialog
        open={Boolean(deleteMedia)}
        title="删除产品图片"
        description={`确认删除“${clean(deleteMedia?.altText, '当前图片')}”？删除后前端不再展示。`}
        confirmLabel="确认删除"
        dangerous
        pending={command.busy}
        onCancel={() => {
          if (!command.busy) setDeleteMedia(undefined);
        }}
        onConfirm={() => {
          const mediaId = textOf(deleteMedia ?? {}, 'id');
          void command
            .run(
              () =>
                api.send(
                  `/api/admin/m2/products/${encodeURIComponent(productId)}/media/${encodeURIComponent(mediaId)}`,
                  'DELETE',
                ),
              '产品图片已删除',
            )
            .then((succeeded) => succeeded && setDeleteMedia(undefined));
        }}
      />
    </>
  );
}

function RouteStopCreateForm({
  api,
  productId,
  onDone,
  collectionBusy,
  onBusyChange,
  writeDenied,
  onPermissionDenied,
}: {
  api: AdminApiClient;
  productId: string;
  onDone: () => void;
  collectionBusy: boolean;
  onBusyChange: (busy: boolean) => void;
  writeDenied: boolean;
  onPermissionDenied: () => void;
}) {
  const command = useCommand(onDone, { onBusyChange, onPermissionDenied });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [stayMinutes, setStayMinutes] = useState('60');
  const [sortOrder, setSortOrder] = useState('0');
  const [validation, setValidation] = useState('');
  const dirty = Boolean(
    name.trim() || description.trim() || stayMinutes !== '60' || sortOrder !== '0',
  );
  useUnsavedNavigationGuard(dirty || command.busy);
  return (
    <form
      className="typed-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const minutes = Number(stayMinutes);
        if (!name.trim()) {
          setValidation('请填写节点名称');
          return;
        }
        if (!Number.isSafeInteger(minutes) || minutes < 0) {
          setValidation('建议停留时间必须填写大于或等于 0 的整数');
          return;
        }
        if (!Number.isSafeInteger(Number(sortOrder))) {
          setValidation('顺序必须填写整数');
          return;
        }
        setValidation('');
        void command
          .run(
            () =>
              api.send(
                `/api/admin/m2/products/${encodeURIComponent(productId)}/route-stops`,
                'POST',
                {
                  name: name.trim(),
                  description: description.trim() || null,
                  stayMinutes: minutes,
                  sortOrder: numberValue(sortOrder),
                },
              ),
            '行程节点已新增',
          )
          .then((succeeded) => {
            if (!succeeded) return;
            setName('');
            setDescription('');
            setStayMinutes('60');
            setSortOrder('0');
          });
      }}
    >
      <label>
        节点名称
        <input required value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label>
        建议停留（分钟）
        <input
          type="number"
          min="0"
          value={stayMinutes}
          onChange={(e) => setStayMinutes(e.target.value)}
        />
      </label>
      <label>
        顺序
        <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
      </label>
      <label>
        节点说明
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      {validation ? <Notice tone="error">{validation}</Notice> : null}
      <div className="button-row">
        <button className="primary" disabled={writeDenied || collectionBusy || command.busy}>
          {command.busy ? '保存中…' : '新增行程节点'}
        </button>
        <CommandMessage command={command} />
      </div>
      <PermissionBlocked visible={writeDenied} />
    </form>
  );
}

function RouteStopEditForm({
  api,
  productId,
  stop,
  close,
  onDone,
  collectionBusy,
  onBusyChange,
  writeDenied,
  onPermissionDenied,
}: {
  api: AdminApiClient;
  productId: string;
  stop: JsonRecord;
  close: () => void;
  onDone: () => void;
  collectionBusy: boolean;
  onBusyChange: (busy: boolean) => void;
  writeDenied: boolean;
  onPermissionDenied: () => void;
}) {
  const command = useCommand(onDone, { onBusyChange, onPermissionDenied });
  const [name, setName] = useState(textValue(stop, 'name'));
  const [description, setDescription] = useState(textValue(stop, 'description'));
  const [stayMinutes, setStayMinutes] = useState(textValue(stop, 'stayMinutes', '0'));
  const [validation, setValidation] = useState('');
  const dirty =
    name !== textValue(stop, 'name') ||
    description !== textValue(stop, 'description') ||
    stayMinutes !== textValue(stop, 'stayMinutes', '0');
  useUnsavedNavigationGuard(dirty || command.busy);
  const cancel = () => {
    if (!dirty || confirmDiscardChanges()) close();
  };
  return (
    <form
      className="typed-form r2-inline-edit"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (!name.trim()) {
          setValidation('请填写节点名称');
          return;
        }
        if (!Number.isSafeInteger(Number(stayMinutes)) || Number(stayMinutes) < 0) {
          setValidation('建议停留时间必须填写大于或等于 0 的整数');
          return;
        }
        setValidation('');
        void command
          .run(
            () =>
              api.send(
                `/api/admin/m2/products/${encodeURIComponent(productId)}/route-stops/${encodeURIComponent(textOf(stop, 'id'))}`,
                'PATCH',
                {
                  name: name.trim(),
                  description: description.trim() || null,
                  stayMinutes: numberValue(stayMinutes),
                },
              ),
            '行程节点已更新',
          )
          .then((succeeded) => succeeded && close());
      }}
    >
      <label>
        节点名称
        <input value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      <label>
        建议停留（分钟）
        <input
          type="number"
          min="0"
          value={stayMinutes}
          onChange={(event) => setStayMinutes(event.target.value)}
        />
      </label>
      <label className="wide">
        节点说明
        <textarea value={description} onChange={(event) => setDescription(event.target.value)} />
      </label>
      {validation ? <Notice tone="error">{validation}</Notice> : null}
      <div className="button-row">
        <button type="button" disabled={collectionBusy || command.busy} onClick={cancel}>
          取消
        </button>
        <button className="primary" disabled={writeDenied || collectionBusy || command.busy}>
          {command.busy ? '保存中…' : '保存修改'}
        </button>
        <CommandMessage command={command} />
      </div>
      <PermissionBlocked visible={writeDenied} />
    </form>
  );
}

function RouteStopRow({
  api,
  productId,
  stop,
  index,
  stops,
  onDone,
  collectionBusy,
  onBusyChange,
  writeDenied,
  onPermissionDenied,
}: {
  api: AdminApiClient;
  productId: string;
  stop: JsonRecord;
  index: number;
  stops: JsonRecord[];
  onDone: () => void;
  collectionBusy: boolean;
  onBusyChange: (busy: boolean) => void;
  writeDenied: boolean;
  onPermissionDenied: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const command = useCommand(onDone, {
    onBusyChange,
    onPermissionDenied: () => {
      setDeleting(false);
      onPermissionDenied();
    },
  });
  const ordered = bySortOrder(stops);
  const id = textOf(stop, 'id');
  const reorder = (targetIndex: number) =>
    command.run(
      () =>
        api.send(
          `/api/admin/m2/products/${encodeURIComponent(productId)}/route-stops/reorder`,
          'POST',
          { orderedIds: movedIds(ordered, id, targetIndex), expectedOrder: expectedOrder(ordered) },
        ),
      '行程顺序已更新',
    );
  return (
    <>
      <tr>
        <td>{index + 1}</td>
        <td>{clean(stop.referenceTime ?? stop.startTime, '灵活安排')}</td>
        <td>
          <b>{clean(stop.name)}</b>
          <small className="r2-cell-note">{clean(stop.description, '暂无补充说明')}</small>
        </td>
        <td>{stop.stayMinutes ? `${stop.stayMinutes} 分钟` : '灵活安排'}</td>
        <td>
          <div className="r2-record-actions">
            <button
              type="button"
              disabled={writeDenied || collectionBusy || command.busy || index === 0}
              onClick={() => void reorder(index - 1)}
            >
              上移
            </button>
            <button
              type="button"
              disabled={
                writeDenied || collectionBusy || command.busy || index === ordered.length - 1
              }
              onClick={() => void reorder(index + 1)}
            >
              下移
            </button>
            <button
              type="button"
              disabled={writeDenied || collectionBusy || command.busy}
              onClick={() => setEditing(true)}
            >
              编辑
            </button>
            <button
              type="button"
              className="danger-link"
              disabled={writeDenied || collectionBusy || command.busy}
              onClick={() => setDeleting(true)}
            >
              删除
            </button>
          </div>
          <CommandMessage command={command} />
        </td>
      </tr>
      {editing ? (
        <tr className="r2-edit-row">
          <td colSpan={5}>
            <RouteStopEditForm
              api={api}
              productId={productId}
              stop={stop}
              close={() => setEditing(false)}
              onDone={onDone}
              collectionBusy={collectionBusy}
              onBusyChange={onBusyChange}
              writeDenied={writeDenied}
              onPermissionDenied={onPermissionDenied}
            />
          </td>
        </tr>
      ) : null}
      {deleting ? (
        <tr className="r2-dialog-row">
          <td colSpan={5}>
            <ConfirmDialog
              open
              title="删除行程节点"
              description={`确认删除“${clean(stop.name)}”？前端推荐行程将同步移除。`}
              confirmLabel="确认删除"
              dangerous
              pending={command.busy}
              onCancel={() => {
                if (!command.busy) setDeleting(false);
              }}
              onConfirm={() => {
                void command
                  .run(
                    () =>
                      api.send(
                        `/api/admin/m2/products/${encodeURIComponent(productId)}/route-stops/${encodeURIComponent(id)}`,
                        'DELETE',
                      ),
                    '行程节点已删除',
                  )
                  .then((succeeded) => succeeded && setDeleting(false));
              }}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function SkuCreateForm({
  api,
  productId,
  refundPolicies,
  dateRules,
  onDone,
  collectionBusy,
  onBusyChange,
  onDirtyChange,
  writeDenied,
  onPermissionDenied,
}: {
  api: AdminApiClient;
  productId: string;
  refundPolicies: JsonRecord[];
  dateRules: JsonRecord[];
  onDone: () => void;
  collectionBusy: boolean;
  onBusyChange: (busy: boolean) => void;
  onDirtyChange: (dirty: boolean) => void;
  writeDenied: boolean;
  onPermissionDenied: () => void;
}) {
  const command = useCommand(onDone, { onBusyChange, onPermissionDenied });
  const [validation, setValidation] = useState<string>();
  const suggestedCode = useRef(clientCommandId('SKU'));
  const [bookingConfig, setBookingConfig] = useState(defaultBookingConfig);
  const [form, setForm] = useState({
    skuCode: suggestedCode.current,
    skuName: '标准套餐',
    serviceMode: 'CHARTER_GUIDE_PACKAGE',
    pricingMode: 'PER_CAR',
    salePrice: '',
    minPeople: '1',
    maxPeople: '6',
    guideMode: 'INCLUDED',
    ticketMode: 'INCLUDED',
    dateRuleId: '',
    refundPolicyId: '',
    includedItems: '',
    excludedItems: '',
  });
  const set = (key: keyof typeof form, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));
  const dirty = Boolean(
    JSON.stringify(bookingConfig) !== JSON.stringify(defaultBookingConfig()) ||
    form.skuCode !== suggestedCode.current ||
    form.skuName !== '标准套餐' ||
    form.salePrice ||
    form.dateRuleId ||
    form.refundPolicyId ||
    form.includedItems ||
    form.excludedItems ||
    form.serviceMode !== 'CHARTER_GUIDE_PACKAGE' ||
    form.pricingMode !== 'PER_CAR' ||
    form.minPeople !== '1' ||
    form.maxPeople !== '6' ||
    form.guideMode !== 'INCLUDED' ||
    form.ticketMode !== 'INCLUDED',
  );
  useUnsavedNavigationGuard(dirty);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  return (
    <form
      className="typed-form wide"
      onSubmit={(event) => {
        event.preventDefault();
        const desiredStatus =
          (event.nativeEvent as SubmitEvent).submitter?.getAttribute('value') === 'ACTIVE'
            ? 'ACTIVE'
            : 'DRAFT';
        const failure = validateSkuEditor({
          skuCode: form.skuCode,
          skuName: form.skuName,
          salePrice: form.salePrice,
          minPeople: form.minPeople,
          maxPeople: form.maxPeople,
          dateRuleId: form.dateRuleId,
          refundPolicyId: form.refundPolicyId,
          includedItems: form.includedItems,
          excludedItems: form.excludedItems,
        });
        setValidation(failure);
        if (failure) return;
        let normalizedBooking;
        try {
          normalizedBooking = parseBookingConfig(bookingConfig);
        } catch (error) {
          setValidation(error instanceof Error ? error.message : '请完善下单配置');
          return;
        }
        void command.run(
          () =>
            api.send(`/api/admin/m2/products/${encodeURIComponent(productId)}/skus`, 'POST', {
              skuCode: form.skuCode.trim(),
              skuName: form.skuName.trim(),
              serviceMode: form.serviceMode,
              pricingMode: form.pricingMode,
              salePriceMinorUnits: yuanToMinorUnits(form.salePrice),
              minPeople: numberValue(form.minPeople, 1),
              maxPeople: numberValue(form.maxPeople, 1),
              guideMode: form.guideMode,
              ticketMode: form.ticketMode,
              dateRuleId: form.dateRuleId,
              refundPolicyId: form.refundPolicyId,
              includedItems: lines(form.includedItems),
              excludedItems: lines(form.excludedItems),
              status: desiredStatus,
              bookingConfig: normalizedBooking,
            }),
          desiredStatus === 'ACTIVE'
            ? '套餐已保存并启用，可继续上架线路'
            : '套餐草稿已保存；启用后才能上架线路',
        );
      }}
    >
      <label>
        套餐名称
        <input required value={form.skuName} onChange={(e) => set('skuName', e.target.value)} />
      </label>
      <label>
        服务类型
        <select value={form.serviceMode} onChange={(e) => set('serviceMode', e.target.value)}>
          <option value="CHARTER_GUIDE_PACKAGE">包车＋向导＋套餐</option>
          <option value="TRANSFER_TICKET_PACKAGE">接送＋票务＋套餐</option>
          <option value="CARPOOL_GUIDE_PACKAGE">拼车＋向导＋套餐</option>
        </select>
      </label>
      <label>
        计价方式
        <select value={form.pricingMode} onChange={(e) => set('pricingMode', e.target.value)}>
          <option value="PER_PERSON">按人计价</option>
          <option value="PER_CAR">按车计价</option>
          <option value="PER_PACKAGE">按套餐计价</option>
        </select>
      </label>
      <label>
        销售价（元）
        <input
          required
          inputMode="decimal"
          value={form.salePrice}
          onChange={(e) => set('salePrice', e.target.value)}
        />
      </label>
      <label>
        最少人数
        <input
          type="number"
          min="1"
          value={form.minPeople}
          onChange={(e) => set('minPeople', e.target.value)}
        />
      </label>
      <label>
        最多人数
        <input
          type="number"
          min="1"
          value={form.maxPeople}
          onChange={(e) => set('maxPeople', e.target.value)}
        />
      </label>
      <label>
        向导服务
        <select value={form.guideMode} onChange={(e) => set('guideMode', e.target.value)}>
          <option value="INCLUDED">已包含</option>
          <option value="NOT_INCLUDED">不包含</option>
        </select>
      </label>
      <label>
        票务方式
        <select value={form.ticketMode} onChange={(e) => set('ticketMode', e.target.value)}>
          <option value="INCLUDED">已包含</option>
          <option value="SELF_PAY">游客自理</option>
          <option value="NOT_INCLUDED">不包含</option>
        </select>
      </label>
      <label>
        服务日历
        <select
          required
          value={form.dateRuleId}
          onChange={(e) => set('dateRuleId', e.target.value)}
        >
          <option value="">请选择</option>
          {dateRules
            .filter((rule) => textOf(rule, 'status') === 'ACTIVE')
            .map((rule) => (
              <option key={textOf(rule, 'id')} value={textValue(rule, 'id')}>
                {clean(rule.name)}
              </option>
            ))}
        </select>
      </label>
      <label>
        退款规则
        <select
          required
          value={form.refundPolicyId}
          onChange={(e) => set('refundPolicyId', e.target.value)}
        >
          <option value="">请选择</option>
          {refundPolicies
            .filter((policy) => textOf(policy, 'status') === 'ACTIVE')
            .map((policy) => (
              <option key={textOf(policy, 'id')} value={textValue(policy, 'id')}>
                {clean(policy.name)}
              </option>
            ))}
        </select>
      </label>
      <label>
        费用包含（每行一项）
        <textarea
          value={form.includedItems}
          onChange={(e) => set('includedItems', e.target.value)}
        />
      </label>
      <label>
        费用不含（每行一项）
        <textarea
          value={form.excludedItems}
          onChange={(e) => set('excludedItems', e.target.value)}
        />
      </label>
      <details className="product-sku-advanced">
        <summary>高级下单配置（儿童、车辆、酒店等，按需调整）</summary>
        <label>
          内部套餐编码（自动建议，可修改）
          <input required value={form.skuCode} onChange={(e) => set('skuCode', e.target.value)} />
        </label>
        <BookingConfigEditor
          api={api}
          productId={productId}
          value={bookingConfig}
          onChange={setBookingConfig}
          disabled={writeDenied || collectionBusy || command.busy}
        />
      </details>
      <div className="button-row booking-save-bar">
        {validation ? <Notice tone="error">{validation}</Notice> : null}
        <button
          type="submit"
          value="DRAFT"
          disabled={writeDenied || collectionBusy || command.busy}
        >
          {command.busy ? '保存中…' : '保存套餐草稿'}
        </button>
        <button
          type="submit"
          value="ACTIVE"
          className="primary"
          disabled={writeDenied || collectionBusy || command.busy}
        >
          {command.busy ? '保存中…' : '保存并启用套餐'}
        </button>
        <CommandMessage command={command} />
      </div>
      <PermissionBlocked visible={writeDenied} />
    </form>
  );
}

function RuleCreateForms({
  api,
  appId,
  refundPolicies,
  dateRules,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  refundPolicies: JsonRecord[];
  dateRules: JsonRecord[];
  onDone: () => void;
}) {
  const [writeDenied, setWriteDenied] = useState(false);
  const refund = useCommand(onDone, { onPermissionDenied: () => setWriteDenied(true) });
  const calendar = useCommand(onDone, { onPermissionDenied: () => setWriteDenied(true) });
  const [refundForm, setRefundForm] = useState({
    code: '',
    name: '',
    hours: '24',
    percent: '100',
    notice: '',
  });
  const [calendarForm, setCalendarForm] = useState({
    code: '',
    name: '',
    blackout: '',
    advanceHours: '24',
  });
  const [validation, setValidation] = useState('');
  const dirty = Boolean(
    refundForm.code ||
    refundForm.name ||
    refundForm.notice ||
    calendarForm.code ||
    calendarForm.name ||
    calendarForm.blackout,
  );
  useUnsavedNavigationGuard(dirty);
  const validateCode = (value: string) =>
    /^[A-Za-z0-9][A-Za-z0-9_-]{0,49}$/.test(value.trim())
      ? ''
      : '编码只能包含字母、数字、下划线或短横线（1–50 位）。';
  return (
    <div className="r2-stack">
      <Notice tone="warning">
        退款规则与服务日历是平台共享规则；修改会影响所有引用它们的小程序和套餐。停用前系统会再次检查有效引用。
      </Notice>
      <div className="r2-rule-library">
        <section>
          <h3>退款规则库</h3>
          {refundPolicies.map((policy) => (
            <RuleLibraryRow
              key={textOf(policy, 'id')}
              api={api}
              appId={appId}
              kind="refund"
              rule={policy}
              writeDenied={writeDenied}
              onPermissionDenied={() => setWriteDenied(true)}
              onDone={onDone}
            />
          ))}
          {!refundPolicies.length && <div className="canonical-inline-empty">暂无退款规则</div>}
        </section>
        <section>
          <h3>服务日历库</h3>
          {dateRules.map((rule) => (
            <RuleLibraryRow
              key={textOf(rule, 'id')}
              api={api}
              appId={appId}
              kind="calendar"
              rule={rule}
              writeDenied={writeDenied}
              onPermissionDenied={() => setWriteDenied(true)}
              onDone={onDone}
            />
          ))}
          {!dateRules.length && <div className="canonical-inline-empty">暂无服务日历</div>}
        </section>
      </div>
      <PermissionBlocked visible={writeDenied} />
      <MaintenanceDetails summary="新增平台共享规则">
        <div className="r2-two">
          <form
            className="typed-form"
            onSubmit={(event) => {
              event.preventDefault();
              const error = validateCode(refundForm.code);
              const hours = Number(refundForm.hours);
              const percent = Number(refundForm.percent);
              const numericError =
                !Number.isInteger(hours) || hours < 0 || hours > 8760
                  ? '出发前小时数须为 0–8760 的整数。'
                  : !Number.isInteger(percent) || percent < 0 || percent > 100
                    ? '可退比例须为 0–100 的整数。'
                    : refundForm.notice.trim().length > 500
                      ? '游客可见说明最多 500 字。'
                      : '';
              if (
                error ||
                !refundForm.name.trim() ||
                refundForm.name.trim().length > 100 ||
                numericError
              ) {
                setValidation(
                  error ||
                    (!refundForm.name.trim()
                      ? '请填写退款规则名称。'
                      : refundForm.name.trim().length > 100
                        ? '规则名称最多 100 字。'
                        : numericError),
                );
                return;
              }
              setValidation('');
              void refund
                .run(
                  () =>
                    api.send(
                      `/api/admin/m2/apps/${encodeURIComponent(appId)}/refund-policies`,
                      'POST',
                      {
                        code: refundForm.code.trim(),
                        name: refundForm.name.trim(),
                        notice: refundForm.notice.trim(),
                        rules: {
                          cancelBeforeHours: numberValue(refundForm.hours),
                          refundPercent: numberValue(refundForm.percent),
                        },
                      },
                    ),
                  '退款规则已创建',
                )
                .then(
                  (succeeded) =>
                    succeeded &&
                    setRefundForm({ code: '', name: '', hours: '24', percent: '100', notice: '' }),
                );
            }}
          >
            <h3>新增退款规则</h3>
            <label>
              规则编码
              <input
                required
                value={refundForm.code}
                onChange={(e) => setRefundForm((v) => ({ ...v, code: e.target.value }))}
              />
            </label>
            <label>
              规则名称
              <input
                required
                value={refundForm.name}
                onChange={(e) => setRefundForm((v) => ({ ...v, name: e.target.value }))}
              />
            </label>
            <label>
              出发前小时数
              <input
                type="number"
                min="0"
                value={refundForm.hours}
                onChange={(e) => setRefundForm((v) => ({ ...v, hours: e.target.value }))}
              />
            </label>
            <label>
              可退比例（%）
              <input
                type="number"
                min="0"
                max="100"
                value={refundForm.percent}
                onChange={(e) => setRefundForm((v) => ({ ...v, percent: e.target.value }))}
              />
            </label>
            <label>
              游客可见说明
              <textarea
                value={refundForm.notice}
                onChange={(e) => setRefundForm((v) => ({ ...v, notice: e.target.value }))}
              />
            </label>
            <div className="button-row">
              <button className="primary" disabled={refund.busy || writeDenied}>
                {refund.busy ? '创建中…' : '创建退款规则'}
              </button>
              <CommandMessage command={refund} />
            </div>
          </form>
          <form
            className="typed-form"
            onSubmit={(event) => {
              event.preventDefault();
              const error = validateCode(calendarForm.code);
              const dates = [...new Set(lines(calendarForm.blackout))];
              const invalidDate = dates.find((value) => !validIsoDate(value));
              const minimumHours = Number(calendarForm.advanceHours);
              const numericError =
                !Number.isInteger(minimumHours) || minimumHours < 0 || minimumHours > 8760
                  ? '最少提前预订须为 0–8760 的整数。'
                  : '';
              if (
                error ||
                !calendarForm.name.trim() ||
                calendarForm.name.trim().length > 100 ||
                invalidDate ||
                dates.length > 366 ||
                numericError
              ) {
                setValidation(
                  error ||
                    (!calendarForm.name.trim()
                      ? '请填写服务日历名称。'
                      : calendarForm.name.trim().length > 100
                        ? '日历名称最多 100 字。'
                        : invalidDate
                          ? `不可预订日期无效：${invalidDate}`
                          : dates.length > 366
                            ? '不可预订日期最多 366 项。'
                            : numericError),
                );
                return;
              }
              setValidation('');
              void calendar
                .run(
                  () =>
                    api.send(
                      `/api/admin/m2/apps/${encodeURIComponent(appId)}/sell-date-rules`,
                      'POST',
                      {
                        code: calendarForm.code.trim(),
                        name: calendarForm.name.trim(),
                        dateRule: { weekdays: [1, 2, 3, 4, 5, 6, 7] },
                        blackoutDates: dates,
                        advanceBookingRule: {
                          minimumHours: numberValue(calendarForm.advanceHours),
                        },
                      },
                    ),
                  '服务日历已创建',
                )
                .then(
                  (succeeded) =>
                    succeeded &&
                    setCalendarForm({ code: '', name: '', blackout: '', advanceHours: '24' }),
                );
            }}
          >
            <h3>新增服务日历</h3>
            <label>
              日历编码
              <input
                required
                value={calendarForm.code}
                onChange={(e) => setCalendarForm((v) => ({ ...v, code: e.target.value }))}
              />
            </label>
            <label>
              日历名称
              <input
                required
                value={calendarForm.name}
                onChange={(e) => setCalendarForm((v) => ({ ...v, name: e.target.value }))}
              />
            </label>
            <label>
              不可预订日期（每行一天）
              <textarea
                placeholder="2026-10-01"
                value={calendarForm.blackout}
                onChange={(e) => setCalendarForm((v) => ({ ...v, blackout: e.target.value }))}
              />
            </label>
            <label>
              最少提前预订（小时）
              <input
                type="number"
                min="0"
                value={calendarForm.advanceHours}
                onChange={(e) => setCalendarForm((v) => ({ ...v, advanceHours: e.target.value }))}
              />
            </label>
            <div className="button-row">
              <button className="primary" disabled={calendar.busy || writeDenied}>
                {calendar.busy ? '创建中…' : '创建服务日历'}
              </button>
              <CommandMessage command={calendar} />
            </div>
          </form>
        </div>
        {validation ? <Notice tone="error">{validation}</Notice> : null}
      </MaintenanceDetails>
    </div>
  );
}

function RuleLibraryRow({
  api,
  appId,
  kind,
  rule,
  writeDenied,
  onPermissionDenied,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  kind: 'refund' | 'calendar';
  rule: JsonRecord;
  writeDenied: boolean;
  onPermissionDenied: () => void;
  onDone: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const rules = nested(rule, 'rules');
  const advance = nested(rule, 'advanceBookingRule');
  const [name, setName] = useState(textValue(rule, 'name'));
  const [notice, setNotice] = useState(textValue(rule, 'notice'));
  const [hours, setHours] = useState(textValue(rules, 'cancelBeforeHours'));
  const [percent, setPercent] = useState(textValue(rules, 'refundPercent'));
  const [blackout, setBlackout] = useState(
    Array.isArray(rule.blackoutDates)
      ? rule.blackoutDates.filter((v): v is string => typeof v === 'string').join('\n')
      : '',
  );
  const [advanceHours, setAdvanceHours] = useState(textValue(advance, 'minimumHours'));
  const [validation, setValidation] = useState('');
  const path = `/api/admin/m2/apps/${encodeURIComponent(appId)}/${kind === 'refund' ? 'refund-policies' : 'sell-date-rules'}/${encodeURIComponent(textValue(rule, 'id'))}`;
  const command = useCommand(onDone, { onPermissionDenied });
  const enabled = textValue(rule, 'status', 'ACTIVE') !== 'DISABLED';
  const referenceCount =
    numberValue(textValue(rule, 'activeSkuReferenceCount')) +
    (kind === 'refund' ? numberValue(textValue(rule, 'activeDefaultProductReferenceCount')) : 0);
  useEffect(() => {
    if (editing) return;
    setName(textValue(rule, 'name'));
    setNotice(textValue(rule, 'notice'));
    setHours(textValue(rules, 'cancelBeforeHours'));
    setPercent(textValue(rules, 'refundPercent'));
    setBlackout(
      Array.isArray(rule.blackoutDates)
        ? rule.blackoutDates
            .filter((value): value is string => typeof value === 'string')
            .join('\n')
        : '',
    );
    setAdvanceHours(textValue(advance, 'minimumHours'));
  }, [
    advance.minimumHours,
    editing,
    rule.blackoutDates,
    rule.id,
    rule.name,
    rule.notice,
    rules.cancelBeforeHours,
    rules.refundPercent,
  ]);
  const dirty =
    editing &&
    (name !== textValue(rule, 'name') ||
      notice !== textValue(rule, 'notice') ||
      hours !== textValue(rules, 'cancelBeforeHours') ||
      percent !== textValue(rules, 'refundPercent') ||
      blackout !==
        (Array.isArray(rule.blackoutDates)
          ? rule.blackoutDates.filter((v): v is string => typeof v === 'string').join('\n')
          : '') ||
      advanceHours !== textValue(advance, 'minimumHours'));
  useUnsavedNavigationGuard(dirty);
  const save = () => {
    const dates = [...new Set(lines(blackout))];
    const invalidDate = dates.find((value) => !validIsoDate(value));
    const hourNumber = Number(kind === 'refund' ? hours : advanceHours);
    const percentNumber = Number(percent);
    const error = !name.trim()
      ? '请填写规则名称。'
      : name.trim().length > 100
        ? '规则名称最多 100 字。'
        : kind === 'refund' && notice.trim().length > 500
          ? '游客可见说明最多 500 字。'
          : kind === 'refund' &&
              hours !== '' &&
              (!Number.isInteger(hourNumber) || hourNumber < 0 || hourNumber > 8760)
            ? '出发前小时数须为 0–8760 的整数。'
            : kind === 'refund' &&
                percent !== '' &&
                (!Number.isInteger(percentNumber) || percentNumber < 0 || percentNumber > 100)
              ? '可退比例须为 0–100 的整数。'
              : kind === 'calendar' && invalidDate
                ? `不可预订日期无效：${invalidDate}`
                : kind === 'calendar' && dates.length > 366
                  ? '不可预订日期最多 366 项。'
                  : kind === 'calendar' &&
                      advanceHours !== '' &&
                      (!Number.isInteger(hourNumber) || hourNumber < 0 || hourNumber > 8760)
                    ? '最少提前预订须为 0–8760 的整数。'
                    : '';
    if (error) {
      setValidation(error);
      return;
    }
    setValidation('');
    const payload: JsonRecord = { name: name.trim() };
    if (kind === 'refund') {
      payload.notice = notice.trim();
      if (hours !== '' && percent !== '') {
        payload.rules = {
          cancelBeforeHours: numberValue(hours),
          refundPercent: numberValue(percent),
        };
      }
    } else {
      payload.blackoutDates = dates;
      if (advanceHours !== '')
        payload.advanceBookingRule = { minimumHours: numberValue(advanceHours) };
    }
    void command
      .run(() => api.send(path, 'PATCH', payload), '规则已保存')
      .then((ok) => ok && setEditing(false));
  };
  return (
    <article className="r2-rule-row">
      <div className="r2-rule-row__summary">
        <div>
          <b>{clean(rule.name)}</b>
          <small>
            {textValue(rule, 'code')} ·{' '}
            {kind === 'refund' ? refundRuleText(rule) : calendarRuleText(rule)}
          </small>
          {kind === 'refund' ? (
            <small>
              套餐引用 {textValue(rule, 'activeSkuReferenceCount', '0')} 处 · 产品默认引用{' '}
              {textValue(rule, 'activeDefaultProductReferenceCount', '0')} 处
            </small>
          ) : (
            <small>套餐引用 {textValue(rule, 'activeSkuReferenceCount', '0')} 处</small>
          )}
          {referenceCount ? <small>停用前需先解除全部有效引用</small> : null}
        </div>
        <Pill value={rule.status} />
      </div>
      {editing ? (
        <div className="r2-rule-row__editor">
          <label>
            名称
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          {kind === 'refund' ? (
            <>
              <label>
                游客可见说明
                <textarea value={notice} onChange={(e) => setNotice(e.target.value)} />
              </label>
              {hours !== '' && percent !== '' ? (
                <div className="r2-field-grid">
                  <label>
                    出发前小时
                    <input
                      type="number"
                      min="0"
                      value={hours}
                      onChange={(e) => setHours(e.target.value)}
                    />
                  </label>
                  <label>
                    可退比例（%）
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={percent}
                      onChange={(e) => setPercent(e.target.value)}
                    />
                  </label>
                </div>
              ) : (
                <Notice>
                  该规则包含非标准结构，本页只修改名称和说明，原规则 JSON 会原样保留。
                </Notice>
              )}
            </>
          ) : (
            <>
              <label>
                不可预订日期（每行一天）
                <textarea value={blackout} onChange={(e) => setBlackout(e.target.value)} />
              </label>
              <label>
                最少提前预订（小时）
                <input
                  type="number"
                  min="0"
                  value={advanceHours}
                  onChange={(e) => setAdvanceHours(e.target.value)}
                />
              </label>
            </>
          )}
          <div className="button-row">
            <ActionButton
              type="button"
              className="primary"
              pending={command.busy}
              disabled={!dirty || !name.trim()}
              {...(!dirty ? { disabledReason: '没有待保存的修改' } : {})}
              onClick={save}
            >
              保存规则
            </ActionButton>
            <button type="button" disabled={command.busy} onClick={() => setEditing(false)}>
              取消编辑
            </button>
          </div>
          {validation ? <Notice tone="error">{validation}</Notice> : null}
        </div>
      ) : null}
      <div className="button-row">
        <button
          type="button"
          disabled={writeDenied || command.busy}
          onClick={() => setEditing((v) => !v)}
        >
          {editing ? '收起编辑' : '编辑'}
        </button>
        <ActionButton
          type="button"
          className={enabled ? 'danger-button' : 'primary'}
          disabled={writeDenied || editing}
          onClick={() => setConfirming(true)}
        >
          {enabled ? '停用' : '重新启用'}
        </ActionButton>
        <CommandMessage command={command} />
      </div>
      <ConfirmDialog
        open={confirming}
        title={`${enabled ? '停用' : '重新启用'}“${clean(rule.name)}”`}
        description={
          enabled
            ? `${referenceCount ? `当前仍被 ${referenceCount} 处有效业务引用，提交后服务端会再次校验；请先解除引用。` : ''}停用后不可再绑定到新套餐；历史订单仍按已保存快照执行。`
            : '重新启用后可再次被套餐选择。'
        }
        confirmLabel={enabled ? '确认停用' : '确认启用'}
        dangerous={enabled}
        pending={command.busy}
        onCancel={() => !command.busy && setConfirming(false)}
        onConfirm={() =>
          void command
            .run(
              () =>
                api.send(
                  path,
                  enabled ? 'DELETE' : 'PATCH',
                  enabled ? undefined : { status: 'ACTIVE' },
                ),
              enabled ? '规则已停用' : '规则已启用',
            )
            .then((ok) => ok && setConfirming(false))
        }
      />
    </article>
  );
}

function ProductCoverageForm({
  api,
  appId,
  product,
  appProduct,
  onDone,
  onDirtyChange,
}: {
  api: AdminApiClient;
  appId: string;
  product: JsonRecord;
  appProduct: JsonRecord;
  onDone: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const command = useCommand(onDone);
  const override = nested(appProduct, 'appOverride');
  const [enabled, setEnabled] = useState(false);
  const [titleOverride, setTitleOverride] = useState('');
  const [subtitleOverride, setSubtitleOverride] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const productId = textValue(product, 'id');
  useEffect(() => {
    const saved = nested(appProduct, 'appOverride');
    setEnabled(saved.enabled === true);
    setTitleOverride(textValue(saved, 'titleOverride'));
    setSubtitleOverride(textValue(saved, 'subtitleOverride'));
    setSortOrder(textValue(saved, 'sortOrderOverride', textValue(product, 'sortOrder', '0')));
  }, [appProduct, product]);
  const dirty =
    enabled !== (override.enabled === true) ||
    titleOverride !== textValue(override, 'titleOverride') ||
    subtitleOverride !== textValue(override, 'subtitleOverride') ||
    sortOrder !== textValue(override, 'sortOrderOverride', textValue(product, 'sortOrder', '0'));
  useUnsavedNavigationGuard(dirty || command.busy);
  useEffect(() => {
    onDirtyChange?.(dirty || command.busy);
    return () => onDirtyChange?.(false);
  }, [dirty, command.busy, onDirtyChange]);
  return (
    <form
      id="product-app-display"
      className="typed-form"
      onSubmit={(event) => {
        event.preventDefault();
        void command.run(() =>
          api.send(
            `/api/admin/m2/apps/${encodeURIComponent(appId)}/products/${encodeURIComponent(productId)}/override`,
            'PUT',
            {
              enabled,
              titleOverride: titleOverride.trim() || null,
              subtitleOverride: subtitleOverride.trim() || null,
              sortOrderOverride: numberValue(sortOrder),
            },
          ),
        );
      }}
    >
      <label>
        展示状态
        <select
          value={enabled ? 'yes' : 'no'}
          onChange={(e) => setEnabled(e.target.value === 'yes')}
        >
          <option value="yes">展示</option>
          <option value="no">隐藏</option>
        </select>
      </label>
      <label>
        展示排序
        <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
      </label>
      <label>
        专属标题（可选）
        <input value={titleOverride} onChange={(e) => setTitleOverride(e.target.value)} />
      </label>
      <label>
        专属副标题（可选）
        <input value={subtitleOverride} onChange={(e) => setSubtitleOverride(e.target.value)} />
      </label>
      <div className="button-row">
        <button className="primary" disabled={command.busy || !productId}>
          {command.busy ? '保存中…' : '保存小程序覆盖'}
        </button>
        <CommandMessage command={command} />
      </div>
    </form>
  );
}

function ProductPageContent({
  api,
  pageId,
  productId,
  targetAppId,
  scopeFilter,
}: {
  api: AdminApiClient;
  pageId: string;
  productId: string;
  targetAppId: string;
  scopeFilter?: React.ReactNode;
}) {
  const [rescueRequested, setRescueRequested] = useState(false);
  const [presentationRequested, setPresentationRequested] = useState(false);
  const retainedProduct = useRef<{ id: string; api: AdminApiClient; data: JsonRecord }>();
  const state = useApi(
      api,
      productId ? `/api/admin/m2/products/${encodeURIComponent(productId)}` : undefined,
      pageId === 'ADM-C02',
    ),
    loadedProduct = unwrapRecord(state.data);
  if (textOf(loadedProduct, 'id') === productId && !state.error)
    retainedProduct.current = { id: productId, api, data: loadedProduct };
  const retainBasicDraft =
    pageId === 'ADM-C02' &&
    Boolean(state.error) &&
    !(state.error instanceof ApiError && state.error.isPermissionError) &&
    retainedProduct.current?.id === productId &&
    retainedProduct.current.api === api;
  const product = retainBasicDraft ? retainedProduct.current!.data : loadedProduct,
    skus = nestedRows(product, 'skus'),
    stops = nestedRows(product, 'routeStops'),
    media = nestedRows(product, 'media'),
    tags = nestedRows(product, 'tags');
  const configurationAppId = configurationScopeAppId(api, targetAppId);
  const rescueAccess = useApi(api, pageId === 'ADM-C02' ? '/api/admin/m8/security/me' : undefined);
  const rescueCodes = unwrapRecord(rescueAccess.data).permissionCodes;
  const canViewRescue = Array.isArray(rescueCodes) && rescueCodes.includes('marketing.rescue.view');
  const canManageRescue =
    Array.isArray(rescueCodes) && rescueCodes.includes('marketing.rescue.manage');
  const basicState = retainBasicDraft ? { ...state, data: product, error: undefined } : state;
  const coverage = useApi(
      api,
      (['ADM-C09', 'ADM-C10'].includes(pageId) ||
        (pageId === 'ADM-C02' && presentationRequested)) &&
        configurationAppId &&
        productId
        ? `/api/admin/m2/apps/${encodeURIComponent(configurationAppId)}/products/${encodeURIComponent(productId)}/override`
        : undefined,
    ),
    refundPoliciesState = useApi(
      api,
      ['ADM-C04', 'ADM-C07', 'ADM-C10'].includes(pageId) && configurationAppId
        ? `/api/admin/m2/apps/${encodeURIComponent(configurationAppId)}/refund-policies`
        : undefined,
    ),
    dateRulesState = useApi(
      api,
      ['ADM-C04', 'ADM-C07', 'ADM-C10'].includes(pageId) && configurationAppId
        ? `/api/admin/m2/apps/${encodeURIComponent(configurationAppId)}/sell-date-rules`
        : undefined,
    ),
    readiness = useApi(
      api,
      pageId === 'ADM-C10' && configurationAppId
        ? `/api/admin/m2/apps/${encodeURIComponent(configurationAppId)}/products/${encodeURIComponent(productId)}/publish-readiness`
        : undefined,
    ),
    rescuePolicyState = useApi(
      api,
      pageId === 'ADM-C02' && canViewRescue && rescueRequested && configurationAppId && productId
        ? `/api/admin/m5/product-rescue/${encodeURIComponent(productId)}?appId=${encodeURIComponent(configurationAppId)}`
        : undefined,
    );
  const reloadProductFacts = () => {
    state.reload();
    readiness.reload();
    coverage.reload();
  };
  const activation = useCommand(reloadProductFacts);
  const refundPolicies = rows(refundPoliciesState.data),
    dateRules = rows(dateRulesState.data);
  const coverageProducts = useMemo(
    () =>
      Object.prototype.hasOwnProperty.call(unwrapRecord(coverage.data), 'appOverride')
        ? [{ id: productId, appOverride: unwrapRecord(coverage.data).appOverride }]
        : [],
    [coverage.data, productId],
  );
  const selectingServiceItems =
    pageId === 'ADM-C04' && new URLSearchParams(location.search).get('task') === 'service-items';
  const title = state.loading
    ? '正在读取产品'
    : state.error
      ? '产品信息暂不可用'
      : clean(product.title, '未命名产品');
  const tabs = productTabs(productId, pageId, {}, configurationAppId);
  const mainTabs = [
    { ...tabs[0]!, label: '1 线路资料', active: ['ADM-C02', 'ADM-C03'].includes(pageId) },
    { ...tabs[2]!, label: '2 售价与预订', active: ['ADM-C04', 'ADM-C07'].includes(pageId) },
    { ...tabs[5]!, label: '3 上架', active: ['ADM-C09', 'ADM-C10'].includes(pageId) },
  ];
  const workbenchNavigation = (
    <>
      {scopeFilter}
      <nav className="product-entry-navigation" aria-label="线路录入步骤">
        <Tabs items={mainTabs} />
      </nav>
      <details className="product-advanced-navigation">
        <summary>更多设置（选填）</summary>
        <Tabs
          items={[
            tabs[1]!,
            { ...tabs[3]!, label: '预订与退款规则库' },
            { ...tabs[4]!, label: '小程序专属展示' },
          ]}
        />
      </details>
      <span id="product-current-step" />
    </>
  );
  const [showSkuCreate, setShowSkuCreate] = useState(false);
  const [editingSkuId, setEditingSkuId] = useState('');
  const [inlineSkuDirty, setInlineSkuDirty] = useState(false);
  const [coverageDirty, setCoverageDirty] = useState(false);
  const [skuCreateDirty, setSkuCreateDirty] = useState(false);
  const [productWriteDenied, setProductWriteDenied] = useState(false);
  const [presentationDirty, setPresentationDirty] = useState(false);
  useUnsavedNavigationGuard(presentationDirty);
  const [rescueWriteDenied, setRescueWriteDenied] = useState(false);
  const [routeCollectionBusy, setRouteCollectionBusy] = useState(false);
  const [skuCollectionBusy, setSkuCollectionBusy] = useState(false);
  const skuOrderCommand = useCommand(reloadProductFacts, {
    onPermissionDenied: () => setProductWriteDenied(true),
    onBusyChange: setSkuCollectionBusy,
    onConflict: state.reload,
  });
  useEffect(() => {
    setProductWriteDenied(false);
    setRescueWriteDenied(false);
    setRouteCollectionBusy(false);
    setSkuCollectionBusy(false);
    setShowSkuCreate(false);
    setEditingSkuId('');
    setInlineSkuDirty(false);
    setCoverageDirty(false);
    setSkuCreateDirty(false);
    setRescueRequested(false);
    setPresentationRequested(false);
  }, [productId, targetAppId]);
  if (pageId === 'ADM-C02')
    return (
      <div className="r2-page">
        <Header
          title="产品基本信息编辑"
          subtitle={`${title} · 先保存线路资料和图片，再设置售价与预订`}
          actions={
            <>
              <a className="button" href="/admin/products">
                返回产品列表
              </a>
              <Pill value={product.status} />
              <button onClick={reloadProductFacts}>刷新数据</button>
            </>
          }
        />
        {workbenchNavigation}
        {retainBasicDraft && (
          <Notice tone="warning">
            图片与资料刷新未完成，已保留当前文字。请重试刷新；确认数据后再保存。
          </Notice>
        )}
        <State state={basicState} count={Object.keys(product).length} noun="产品">
          <section className="product-entry-next" aria-label="线路录入下一步">
            <span>
              <strong>本步：保存名称、描述和图片</strong>{' '}
              副标题、推荐行程和扩展展示都可后补；草稿不会自动上架。
            </span>
            <a href={mainTabs[1]!.href}>下一步：售价与预订</a>
          </section>
          <div className="r2-product-editor-grid">
            <Panel title="基础信息" hint="交易价格、人数与车型由套餐决定，不在产品层写死">
              <ProductBasicForm
                key={productId}
                api={api}
                product={product}
                onDone={reloadProductFacts}
                writeDenied={productWriteDenied || Boolean(state.error)}
                onPermissionDenied={() => setProductWriteDenied(true)}
              />
              <div className="r2-form-footnote">
                名称、摘要和须知保存后用于小程序展示；销售价格和服务方式在套餐中配置。
              </div>
              {canViewRescue && (
                <details
                  className="r2-inline-config-section product-optional-marketing"
                  onToggle={(event) => {
                    if (event.currentTarget.open) setRescueRequested(true);
                  }}
                >
                  <summary>支付挽留优惠（选填）</summary>
                  {rescueRequested && (
                    <>
                      <div className="r2-inline-config-heading">
                        <div>
                          <h3 id="product-rescue-title">支付挽留优惠</h3>
                          <p>商品级配置；只作用于当前未支付订单，不与其他优惠叠加</p>
                        </div>
                        <span>{configurationAppId ? '本页操作小程序' : '尚未选择操作小程序'}</span>
                      </div>
                      {!configurationAppId ? (
                        <Notice>
                          支付挽留配置属于具体小程序，请在本页筛选区选择操作小程序；不会把未读取的配置显示为已停用或默认设置。
                        </Notice>
                      ) : (
                        <State
                          state={rescuePolicyState}
                          count={Object.keys(unwrapRecord(rescuePolicyState.data)).length}
                          noun="挽留设置"
                        >
                          <ProductRescuePolicyForm
                            api={api}
                            appId={configurationAppId}
                            productId={productId}
                            policy={unwrapRecord(rescuePolicyState.data)}
                            onDone={rescuePolicyState.reload}
                            writeDenied={rescueWriteDenied || !canManageRescue}
                            onPermissionDenied={() => setRescueWriteDenied(true)}
                          />
                        </State>
                      )}
                    </>
                  )}
                </details>
              )}
            </Panel>
            <div className="r2-stack">
              <Panel title="产品图片">
                <div className="r2-media-console">
                  <div className="r2-media-side">
                    <b>{media.length ? `${media.length} 项媒体已配置` : '暂无产品媒体'}</b>
                    <span>
                      {media.length
                        ? clean(primaryMedia(media).altText, '主图说明待补充')
                        : '请通过受控媒体入口上传产品主图与轮播图。'}
                    </span>
                    <ProductMediaManager
                      api={api}
                      productId={productId}
                      media={media}
                      onDone={reloadProductFacts}
                      writeDenied={productWriteDenied || Boolean(state.error)}
                      onPermissionDenied={() => setProductWriteDenied(true)}
                    />
                    <div className="product-image-entry">
                      <ProductMediaUploadForm
                        api={api}
                        appId={configurationAppId}
                        productId={productId}
                        onDone={reloadProductFacts}
                        writeDenied={productWriteDenied || Boolean(state.error)}
                        onPermissionDenied={() => setProductWriteDenied(true)}
                      />
                    </div>
                  </div>
                </div>
                <details className="erp-optional-preview">
                  <summary>查看小程序展示效果</summary>
                  <ProductPhonePreview product={product} skus={skus} stops={stops} media={media} />
                </details>
              </Panel>
            </div>
          </div>
          <details
            className="product-optional-presentation"
            onToggle={(event) => {
              if (event.currentTarget.open) setPresentationRequested(true);
            }}
          >
            <summary>扩展详情与推广地址（选填）</summary>
            {presentationRequested &&
              (configurationAppId ? (
                nested(coverageProducts[0] ?? {}, 'appOverride').id ? (
                  <>
                    <ProductPresentationEditor
                      key={`${configurationAppId}:${productId}`}
                      api={api}
                      appId={configurationAppId}
                      product={product}
                      binding={nested(coverageProducts[0] ?? {}, 'appOverride')}
                      loading={coverage.loading || Boolean(coverage.error)}
                      onDone={coverage.reload}
                      onDirtyChange={setPresentationDirty}
                    />
                    <MiniappPageLinks
                      api={api}
                      appId={configurationAppId}
                      targetType="PRODUCT"
                      targetId={productId}
                      title="线路详情推广地址"
                    />
                  </>
                ) : (
                  <Notice>
                    扩展详情可在上架页保存小程序展示设置后补充。线路资料和图片可以先录入。
                  </Notice>
                )
              ) : (
                <Notice>
                  专属展示覆盖与推广地址属于具体小程序，请在本页筛选区选择操作小程序后配置。中央产品资料仍可按授权范围维护。
                </Notice>
              ))}
          </details>
        </State>
      </div>
    );
  if (pageId === 'ADM-C03')
    return (
      <div className="r2-page">
        <Header
          title="推荐行程与景点编辑"
          subtitle={`${title} · 推荐行程、景点与线路亮点`}
          actions={
            <>
              <a className="button" href="/admin/products">
                返回产品列表
              </a>
              <Pill value={product.status} />
              <button onClick={reloadProductFacts}>刷新数据</button>
            </>
          }
        />
        {workbenchNavigation}
        <State state={state} count={Object.keys(product).length} noun="产品">
          <div className="r2-two-one product-primary-table">
            <Panel title="推荐行程时间轴" hint="这是前端参考行程，不代表强制固定时间">
              <div className="r2-itinerary-toolbar">
                <div>
                  <b>{stops.length} 个行程节点</b>
                  <span>前端详情页按这里的顺序展示路线、景点和停留说明。</span>
                </div>
                <a
                  href={productScopeHref(
                    `/admin/products/${encodeURIComponent(productId)}/publish-check`,
                    configurationAppId,
                  )}
                >
                  查看发布检查
                </a>
              </div>
              <PermissionBlocked visible={productWriteDenied} />
              <Table
                heads={['顺序', '参考时间', '节点内容', '建议停留', '操作']}
                empty={!stops.length}
              >
                {bySortOrder(stops).map((stop, index) => (
                  <RouteStopRow
                    key={textOf(stop, 'id')}
                    api={api}
                    productId={productId}
                    stop={stop}
                    index={index}
                    stops={stops}
                    onDone={reloadProductFacts}
                    collectionBusy={routeCollectionBusy}
                    onBusyChange={setRouteCollectionBusy}
                    writeDenied={productWriteDenied}
                    onPermissionDenied={() => setProductWriteDenied(true)}
                  />
                ))}
              </Table>
              <MaintenanceDetails summary="新增推荐行程节点">
                <RouteStopCreateForm
                  api={api}
                  productId={productId}
                  onDone={reloadProductFacts}
                  collectionBusy={routeCollectionBusy}
                  onBusyChange={setRouteCollectionBusy}
                  writeDenied={productWriteDenied}
                  onPermissionDenied={() => setProductWriteDenied(true)}
                />
              </MaintenanceDetails>
            </Panel>
            {(stops.some((stop) => stop.poiId || stop.poi) || tags.length > 0) && (
              <div className="product-record-summary">
                {stops.some((stop) => stop.poiId || stop.poi) && (
                  <span>
                    关联景点：
                    {bySortOrder(stops)
                      .filter((stop) => stop.poiId || stop.poi)
                      .map((stop) => clean(nested(stop, 'poi').name ?? stop.name))
                      .join('、')}
                  </span>
                )}
                {tags.length > 0 && (
                  <span>
                    线路标签：
                    {tags.map((tag) => clean(nested(tag, 'tag').name ?? tag.name)).join('、')}
                  </span>
                )}
              </div>
            )}
          </div>
        </State>
      </div>
    );
  if (pageId === 'ADM-C04')
    return (
      <div className="r2-page">
        <Header
          title={selectingServiceItems ? '选择套餐配置票务与费用' : '售价与预订'}
          subtitle={
            selectingServiceItems
              ? `${title} / 选择一个套餐后进入费用与票务配置`
              : `${title} / 填售价，选择有效服务日历与退款规则，保存套餐后启用`
          }
          actions={
            <>
              <button
                onClick={() => {
                  state.reload();
                  refundPoliciesState.reload();
                  dateRulesState.reload();
                  readiness.reload();
                }}
              >
                刷新数据
              </button>
              <button
                className="primary"
                disabled={skuCollectionBusy}
                onClick={() => {
                  if (showSkuCreate && skuCreateDirty && !confirmDiscardChanges()) return;
                  if (editingSkuId && inlineSkuDirty && !confirmDiscardChanges()) return;
                  setEditingSkuId('');
                  setShowSkuCreate((value) => !value);
                }}
              >
                {showSkuCreate ? '收起新增' : '+ 新增套餐'}
              </button>
            </>
          }
        />
        {workbenchNavigation}
        <State state={state} count={Object.keys(product).length} noun="产品">
          <Panel
            title={`可销售套餐（${skus.length}）`}
            hint="每个套餐单独设置价格与预订规则；可先保存为草稿，再明确启用。"
          >
            <div className="r2-sku-hero">
              <div>
                <span>当前线路</span>
                <h2>{clean(product.title)}</h2>
                <p>
                  {serviceModeSummary(skus)} · {priceRangeFromSkus(skus)} ·{' '}
                  {bookingPeopleSummary(skus)}
                </p>
              </div>
              <Pill value={product.status} />
            </div>
            <PermissionBlocked visible={productWriteDenied} />
            <CommandMessage command={skuOrderCommand} />
            <Table
              heads={['顺序', '套餐', '服务模式', '计价方式', '人数范围', '销售价', '状态', '操作']}
              empty={!skus.length}
            >
              {bySortOrder(skus).map((sku, index) => (
                <tr key={textOf(sku, 'id')}>
                  <td>{index + 1}</td>
                  <td>
                    <b>{clean(sku.skuName)}</b>
                    <small className="r2-cell-note">{clean(sku.skuCode)}</small>
                    <div className="r2-inline-chip-row">
                      {skuRuleChips(sku, dateRules, refundPolicies).map((chip, chipIndex) => (
                        <span key={`${textOf(sku, 'id')}-${chipIndex}-${chip}`}>{chip}</span>
                      ))}
                    </div>
                  </td>
                  <td>{label(sku.serviceMode)}</td>
                  <td>{label(sku.pricingMode)}</td>
                  <td>{peopleRange(sku.minPeople, sku.maxPeople)}</td>
                  <td>
                    <b className="r2-price">{skuPrice(sku)}</b>
                  </td>
                  <td>
                    <Pill value={sku.status} />
                  </td>
                  <td>
                    <div className="r2-operation-links">
                      <button
                        type="button"
                        disabled={productWriteDenied || skuCollectionBusy || index === 0}
                        onClick={() =>
                          void skuOrderCommand.run(
                            () =>
                              api.send(
                                `/api/admin/m2/products/${encodeURIComponent(productId)}/skus/reorder`,
                                'POST',
                                {
                                  orderedIds: movedIds(skus, textOf(sku, 'id'), index - 1),
                                  expectedOrder: expectedOrder(skus),
                                },
                              ),
                            '套餐顺序已更新',
                          )
                        }
                      >
                        上移
                      </button>
                      <button
                        type="button"
                        disabled={
                          productWriteDenied || skuCollectionBusy || index === skus.length - 1
                        }
                        onClick={() =>
                          void skuOrderCommand.run(
                            () =>
                              api.send(
                                `/api/admin/m2/products/${encodeURIComponent(productId)}/skus/reorder`,
                                'POST',
                                {
                                  orderedIds: movedIds(skus, textOf(sku, 'id'), index + 1),
                                  expectedOrder: expectedOrder(skus),
                                },
                              ),
                            '套餐顺序已更新',
                          )
                        }
                      >
                        下移
                      </button>
                      {selectingServiceItems ? (
                        <a
                          href={productScopeHref(
                            `/admin/skus/${encodeURIComponent(textOf(sku, 'id'))}/service-items`,
                            configurationAppId,
                          )}
                        >
                          选择并继续
                        </a>
                      ) : null}
                      <button
                        type="button"
                        disabled={skuCollectionBusy}
                        onClick={() => {
                          if ((inlineSkuDirty || skuCreateDirty) && !confirmDiscardChanges())
                            return;
                          setShowSkuCreate(false);
                          setEditingSkuId(textOf(sku, 'id'));
                        }}
                      >
                        编辑售价与预订
                      </button>
                      <a
                        href={productScopeHref(
                          `/admin/skus/${encodeURIComponent(textOf(sku, 'id'))}`,
                          configurationAppId,
                        )}
                      >
                        完整设置
                      </a>
                      {!selectingServiceItems ? (
                        <a
                          href={productScopeHref(
                            `/admin/skus/${encodeURIComponent(textOf(sku, 'id'))}/service-items`,
                            configurationAppId,
                          )}
                        >
                          费用票务
                        </a>
                      ) : null}
                      <a
                        href={productScopeHref(
                          `/admin/products/${encodeURIComponent(productId)}/booking-refund`,
                          configurationAppId,
                        )}
                      >
                        规则
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
            {showSkuCreate && (
              <div className="r2-collapsible-form">
                <SkuCreateForm
                  api={api}
                  productId={productId}
                  refundPolicies={refundPolicies}
                  dateRules={dateRules}
                  onDone={() => {
                    reloadProductFacts();
                    setSkuCreateDirty(false);
                    setShowSkuCreate(false);
                  }}
                  collectionBusy={skuCollectionBusy}
                  onBusyChange={setSkuCollectionBusy}
                  onDirtyChange={setSkuCreateDirty}
                  writeDenied={productWriteDenied}
                  onPermissionDenied={() => setProductWriteDenied(true)}
                />
              </div>
            )}
            {editingSkuId && skus.some((sku) => textOf(sku, 'id') === editingSkuId) && (
              <section className="product-inline-sku" aria-label="当前套餐售价与预订">
                <div className="product-inline-sku-heading">
                  <h3>
                    {clean(
                      skus.find((sku) => textOf(sku, 'id') === editingSkuId)?.skuName,
                      '当前套餐',
                    )}
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      if (inlineSkuDirty && !confirmDiscardChanges()) return;
                      setEditingSkuId('');
                    }}
                  >
                    收起套餐编辑
                  </button>
                </div>
                <SkuEditForm
                  key={editingSkuId}
                  api={api}
                  productId={productId}
                  sku={skus.find((sku) => textOf(sku, 'id') === editingSkuId)!}
                  refundPolicies={refundPolicies}
                  dateRules={dateRules}
                  onDone={reloadProductFacts}
                  writeDenied={productWriteDenied}
                  onPermissionDenied={() => setProductWriteDenied(true)}
                  onDirtyChange={setInlineSkuDirty}
                />
              </section>
            )}
            <div className="canonical-rule-note">
              套餐分别维护价格、人数、票务、向导及服务日历。下架后停止接收新订单，历史订单继续履约。
            </div>
            <div className="r2-step-actionbar">
              <a
                href={productScopeHref(
                  `/admin/products/${encodeURIComponent(productId)}/basic`,
                  configurationAppId,
                )}
              >
                上一步：线路资料
              </a>
              <button
                onClick={() => {
                  state.reload();
                  refundPoliciesState.reload();
                  dateRulesState.reload();
                  readiness.reload();
                }}
              >
                刷新数据
              </button>
              <a
                className="primary-link"
                href={productScopeHref(
                  `/admin/products/${encodeURIComponent(productId)}/publish-check`,
                  configurationAppId,
                )}
              >
                下一步：上架
              </a>
            </div>
          </Panel>
        </State>
      </div>
    );
  if (pageId === 'ADM-C07')
    return (
      <div className="r2-page">
        <Header
          title="预订与退款规则"
          subtitle={`${title} · 套餐级预订限制、退款策略和游客可见规则`}
          actions={
            <button
              onClick={() => {
                state.reload();
                refundPoliciesState.reload();
                dateRulesState.reload();
              }}
            >
              刷新数据
            </button>
          }
        />
        {workbenchNavigation}
        <State state={state} count={Object.keys(product).length} noun="产品">
          <div className="r2-two">
            <Panel title="预订规则总览" hint="游客下单前读取产品须知与 SKU 绑定的服务日历">
              <div className="r2-field-grid">
                <Field name="游客可见预订须知" value={product.bookingNotice} wide />
                <Field name="服务套餐数量" value={`${skus.length} 个`} />
                <Field
                  name="已配置服务日历"
                  value={`${skus.filter((s) => Boolean(s.dateRuleId) || Object.keys(nested(s, 'dateRule')).length > 0).length} 个套餐`}
                />
              </div>
              <div className="canonical-rule-note">
                服务日期规则只决定某日是否可预订，不代表数量配额。
              </div>
              <div className="r2-calendar-list">
                {activeRows(skus).map((sku) => {
                  const rule = skuBoundDateRule(sku, dateRules);
                  return (
                    <article key={textOf(sku, 'id')}>
                      <div>
                        <b>{clean(sku.skuName)}</b>
                        <span>
                          {Object.keys(rule).length ? calendarRuleText(rule) : '待绑定服务日历'}
                        </span>
                      </div>
                      <Pill value={Object.keys(rule).length ? rule.status : 'DRAFT'} />
                    </article>
                  );
                })}
                {!activeRows(skus).length && (
                  <div className="canonical-inline-empty">暂无可用套餐</div>
                )}
              </div>
            </Panel>
            <Panel title="退款规则" hint="售后申请读取订单创建时绑定的规则快照">
              <div className="r2-policy-timeline">
                {activeRows(skus).map((sku) => {
                  const policy = skuBoundPolicy(sku, refundPolicies);
                  return (
                    <article key={textOf(sku, 'id')}>
                      <div className="r2-policy-dot" />
                      <div className="r2-policy-body">
                        <div>
                          <b>{clean(sku.skuName)}</b>
                          <Pill value={Object.keys(policy).length ? policy.status : 'DRAFT'} />
                        </div>
                        <strong>{clean(policy.name ?? policy.title, '暂未绑定退款规则')}</strong>
                        <span>
                          {Object.keys(policy).length
                            ? refundRuleText(policy)
                            : '保存套餐时必须绑定退款规则'}
                        </span>
                      </div>
                    </article>
                  );
                })}
                {!activeRows(skus).length && (
                  <div className="canonical-inline-empty">暂无套餐规则</div>
                )}
              </div>
            </Panel>
          </div>
          <Panel title="维护退款规则与服务日历">
            {configurationAppId ? (
              <RuleCreateForms
                api={api}
                appId={configurationAppId}
                refundPolicies={refundPolicies}
                dateRules={dateRules}
                onDone={() => {
                  refundPoliciesState.reload();
                  dateRulesState.reload();
                  readiness.reload();
                }}
              />
            ) : (
              <Notice>请先在本页顶部选择操作小程序，再读取和维护对应的服务日历、退款规则。</Notice>
            )}
          </Panel>
        </State>
      </div>
    );
  if (pageId === 'ADM-C09') {
    if (!configurationAppId)
      return (
        <div className="r2-page">
          <Header title="产品小程序覆盖" subtitle={`${title} · 按授权范围维护中央产品`} />
          {workbenchNavigation}
          <Notice>
            当前为全部授权范围。产品是否展示必须按具体小程序核对，请在本页筛选区选择操作小程序；未读取配置不会显示为“未开启”。
          </Notice>
        </div>
      );
    const appProduct = coverageProducts[0] ?? {};
    const covered = nested(appProduct, 'appOverride').enabled === true;
    const coverageState: LoadState<unknown> = {
      loading: state.loading || coverage.loading,
      error: state.error ?? coverage.error,
      data: coverage.data,
      reload: () => {
        state.reload();
        coverage.reload();
      },
    };
    return (
      <div className="r2-page">
        <Header
          title="产品小程序覆盖"
          subtitle={`${title} · 中央产品与小程序前端展示策略`}
          actions={
            <button
              onClick={() => {
                state.reload();
                coverage.reload();
              }}
            >
              刷新数据
            </button>
          }
        />
        {workbenchNavigation}
        <State
          state={coverageState}
          count={Object.keys(product).length && configurationAppId ? 1 : 0}
          noun="产品与小程序覆盖"
        >
          <div className="r2-two-one product-primary-table">
            <Panel title="本页小程序覆盖" hint="产品是否进入所选小程序的前端展示范围">
              <div className="product-record-summary">
                <span>产品状态：{label(product.status)}</span>
                <span>小程序展示：{covered ? '已开启' : '未开启'}</span>
                <span>可用套餐：{activeRows(skus).length} 个</span>
              </div>
              <MaintenanceDetails summary="维护当前小程序覆盖">
                <ProductCoverageForm
                  api={api}
                  appId={configurationAppId}
                  product={product}
                  appProduct={appProduct}
                  onDone={() => {
                    reloadProductFacts();
                  }}
                />
              </MaintenanceDetails>
            </Panel>
            <div className="canonical-rule-note">
              展示开启后，产品还须处于启用状态且至少有一个可用套餐。专属标题留空时沿用产品原名。
            </div>
          </div>
        </State>
      </div>
    );
  }
  const result = unwrapRecord(readiness.data),
    checks = nestedRows(result, 'checks'),
    previewChecks = productDisplayChecks(product, skus, stops, media);
  if (!configurationAppId)
    return (
      <div className="r2-page">
        <Header title="产品预览与发布检查" subtitle={`${title} · 发布检查需要明确小程序`} />
        {workbenchNavigation}
        <Notice>
          请在本页筛选区选择操作小程序后检查发布、可售性与支付链路；当前没有执行单小程序发布检查。
        </Notice>
      </div>
    );
  const readinessState: LoadState<unknown> = {
    loading: state.loading || readiness.loading,
    error: state.error ?? readiness.error,
    data: readiness.data,
    reload: () => {
      state.reload();
      readiness.reload();
    },
  };
  return (
    <div className="r2-page">
      <Header
        title="产品预览与发布检查"
        subtitle={`${title} · 发布完整性、可售性与前端链路检查`}
        actions={
          <>
            <button onClick={readiness.reload} disabled={activation.busy}>
              重新检查
            </button>
            <button
              className="primary"
              disabled={
                activation.busy ||
                coverageDirty ||
                state.loading ||
                readiness.loading ||
                coverage.loading ||
                Boolean(state.error) ||
                Boolean(readiness.error) ||
                Boolean(coverage.error) ||
                result.publishable !== true
              }
              onClick={() =>
                void activation.run(
                  () =>
                    api.send(
                      `/api/admin/m2/apps/${encodeURIComponent(configurationAppId)}/products/${encodeURIComponent(productId)}/activate`,
                      'POST',
                    ),
                  '线路已上架',
                )
              }
            >
              {activation.busy ? '上架中…' : '确认上架'}
            </button>
          </>
        }
      />
      {workbenchNavigation}
      <Panel
        title="本小程序展示设置"
        hint="在本页选择展示并保存，无需另行进入小程序覆盖页面。展示开启后仍须通过下方检查并确认上架。"
      >
        <State state={coverage} count={Object.keys(product).length ? 1 : 0} noun="小程序展示设置">
          <ProductCoverageForm
            key={`${configurationAppId}:${productId}`}
            api={api}
            appId={configurationAppId}
            product={product}
            appProduct={coverageProducts[0] ?? {}}
            onDone={reloadProductFacts}
            onDirtyChange={setCoverageDirty}
          />
        </State>
        {coverageDirty && (
          <p className="product-entry-note">展示设置尚未保存，请先保存后再上架线路。</p>
        )}
      </Panel>
      <State
        state={readinessState}
        count={Object.keys(product).length && Object.keys(result).length ? 1 : 0}
        noun="产品发布检查"
      >
        <div className="r2-two-one">
          <Panel title="发布前自动检查" hint="所有服务事实与小程序覆盖必须通过后才能发布">
            <CommandMessage command={activation} />
            <div className={`r2-readiness ${result.publishable === true ? 'ready' : 'blocked'}`}>
              <b>{result.publishable === true ? '检查通过' : '暂不可发布'}</b>
              <span>
                {checks.filter((c) => c.passed === true).length} / {checks.length} 项通过
              </span>
            </div>
            <Table heads={['结果', '检查项', '说明', '补充入口']} empty={!checks.length}>
              {checks.map((check, index) => (
                <tr key={`${textOf(check, 'code')}-${index}`}>
                  <td>
                    <Pill value={check.passed === true ? 'ACTIVE' : 'FAILED'} />
                  </td>
                  <td>{readinessLabel(check, index)}</td>
                  <td>{check.passed === true ? '已满足' : readinessFailureReason(check, index)}</td>
                  <td>
                    {check.passed !== true && READINESS_REPAIRS[textOf(check, 'code')] ? (
                      <a
                        href={
                          textOf(check, 'code') === 'APP_EXPOSURE_ACTIVE'
                            ? '#product-app-display'
                            : productScopeHref(
                                `/admin/products/${encodeURIComponent(productId)}/${READINESS_REPAIRS[textOf(check, 'code')]!.step}`,
                                configurationAppId,
                              )
                        }
                      >
                        前往补充
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </Table>
            <details className="product-optional-checks">
              <summary>展示内容建议（选填，不是发布条件）</summary>
              <div className="r2-check-card-list">
                {previewChecks.map((check) => (
                  <article key={check.label} className={check.passed ? 'pass' : 'pending'}>
                    <b>
                      {check.passed ? '✓' : '○'} {check.label}
                    </b>
                    <span>{check.note}</span>
                  </article>
                ))}
              </div>
            </details>
          </Panel>
          <Panel title="前端详情预览">
            <ProductPhonePreview product={product} skus={skus} stops={stops} media={media} />
            <div className="r2-publish-summary">
              <b>{result.publishable === true ? '可以确认上架' : '请处理上方未通过项'}</b>
              <span>
                发布动作会重新校验产品与城市状态、已启用套餐、有效预订退款规则和小程序展示。图片、描述和推荐行程可以继续补充。
              </span>
            </div>
          </Panel>
        </div>
      </State>
    </div>
  );
}

function bookingConfigForEditor(sku: JsonRecord) {
  const config = parseBookingConfig(sku.bookingConfig, String(sku.pickupMode ?? ''));
  if (!config.transport && typeof sku.vehicleType === 'string' && sku.vehicleType)
    config.transport = { mode: 'INCLUDED', summary: sku.vehicleType };
  if (!config.pickup.description && typeof sku.pickupAreaText === 'string')
    config.pickup.description = sku.pickupAreaText;
  return config;
}

function SkuEditForm({
  api,
  productId,
  sku,
  refundPolicies,
  dateRules,
  onDone,
  writeDenied,
  onPermissionDenied,
  onDirtyChange,
}: {
  api: AdminApiClient;
  productId: string;
  sku: JsonRecord;
  refundPolicies: JsonRecord[];
  dateRules: JsonRecord[];
  onDone: () => void;
  writeDenied: boolean;
  onPermissionDenied: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [collectionBusy, setCollectionBusy] = useState(false);
  const command = useCommand(onDone, { onBusyChange: setCollectionBusy, onPermissionDenied });
  const statusCommand = useCommand(onDone, { onBusyChange: setCollectionBusy, onPermissionDenied });
  const [validation, setValidation] = useState<string>();
  const [confirmingStatus, setConfirmingStatus] = useState(false);
  const [skuName, setSkuName] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [marketPrice, setMarketPrice] = useState('');
  const [serviceMode, setServiceMode] = useState('CHARTER_GUIDE_PACKAGE');
  const [bookingConfig, setBookingConfig] = useState(() => bookingConfigForEditor(sku));
  const [durationHours, setDurationHours] = useState('');
  const [pricingMode, setPricingMode] = useState('PER_CAR');
  const [guideMode, setGuideMode] = useState('INCLUDED');
  const [ticketMode, setTicketMode] = useState('INCLUDED');
  const [minPeople, setMinPeople] = useState('1');
  const [maxPeople, setMaxPeople] = useState('');
  const [included, setIncluded] = useState('');
  const [excluded, setExcluded] = useState('');
  const [dateRuleId, setDateRuleId] = useState('');
  const [refundPolicyId, setRefundPolicyId] = useState('');
  useEffect(() => {
    setBookingConfig(bookingConfigForEditor(sku));
    setSkuName(textValue(sku, 'skuName'));
    const cents = Number(sku.salePriceCents);
    setSalePrice(Number.isFinite(cents) ? (cents / 100).toFixed(2) : '');
    setMarketPrice(
      sku.marketPriceCents == null ? '' : (Number(sku.marketPriceCents) / 100).toFixed(2),
    );
    setServiceMode(textValue(sku, 'serviceMode', 'CHARTER_GUIDE_PACKAGE'));
    setDurationHours(textValue(sku, 'durationHours'));
    setPricingMode(textValue(sku, 'pricingMode', 'PER_CAR'));
    setGuideMode(textValue(sku, 'guideMode', 'INCLUDED'));
    setTicketMode(textValue(sku, 'ticketMode', 'INCLUDED'));
    setMinPeople(textValue(sku, 'minPeople', '1'));
    setMaxPeople(textValue(sku, 'maxPeople'));
    setIncluded(
      Array.isArray(sku.includedItems)
        ? sku.includedItems.filter((v): v is string => typeof v === 'string').join('\n')
        : '',
    );
    setExcluded(
      Array.isArray(sku.excludedItems)
        ? sku.excludedItems.filter((v): v is string => typeof v === 'string').join('\n')
        : '',
    );
    setDateRuleId(textValue(sku, 'dateRuleId'));
    setRefundPolicyId(
      textValue(sku, 'refundPolicyId', textValue(nested(sku, 'refundPolicy'), 'id')),
    );
  }, [sku]);
  const skuId = textValue(sku, 'id');
  const dirty =
    Boolean(skuId) &&
    (JSON.stringify(bookingConfig) !== JSON.stringify(bookingConfigForEditor(sku)) ||
      skuName !== textValue(sku, 'skuName') ||
      marketPrice !==
        (sku.marketPriceCents == null ? '' : (Number(sku.marketPriceCents) / 100).toFixed(2)) ||
      salePrice !==
        (Number.isFinite(Number(sku.salePriceCents))
          ? (Number(sku.salePriceCents) / 100).toFixed(2)
          : '') ||
      serviceMode !== textValue(sku, 'serviceMode', 'CHARTER_GUIDE_PACKAGE') ||
      durationHours !== textValue(sku, 'durationHours') ||
      pricingMode !== textValue(sku, 'pricingMode', 'PER_CAR') ||
      guideMode !== textValue(sku, 'guideMode', 'INCLUDED') ||
      ticketMode !== textValue(sku, 'ticketMode', 'INCLUDED') ||
      minPeople !== textValue(sku, 'minPeople', '1') ||
      maxPeople !== textValue(sku, 'maxPeople') ||
      included !==
        (Array.isArray(sku.includedItems)
          ? sku.includedItems.filter((v): v is string => typeof v === 'string').join('\n')
          : '') ||
      excluded !==
        (Array.isArray(sku.excludedItems)
          ? sku.excludedItems.filter((v): v is string => typeof v === 'string').join('\n')
          : '') ||
      dateRuleId !== textValue(sku, 'dateRuleId') ||
      refundPolicyId !==
        textValue(sku, 'refundPolicyId', textValue(nested(sku, 'refundPolicy'), 'id')));
  useUnsavedNavigationGuard(dirty);
  useEffect(() => {
    onDirtyChange?.(dirty || collectionBusy);
    return () => onDirtyChange?.(false);
  }, [dirty, collectionBusy, onDirtyChange]);
  const currentStatus = textValue(sku, 'status', 'DRAFT');
  const enabling = currentStatus !== 'ACTIVE';
  const statusLabel =
    currentStatus === 'DRAFT' ? '启用套餐' : enabling ? '重新启用套餐' : '停用套餐';
  return (
    <>
      <form
        className="typed-form wide"
        onSubmit={(event) => {
          event.preventDefault();
          let normalizedBooking;
          try {
            normalizedBooking = parseBookingConfig(bookingConfig);
          } catch (error) {
            setValidation(error instanceof Error ? error.message : '请检查下单规则');
            return;
          }
          if (normalizedBooking.children.bed === 'ADULT_PRICE' && pricingMode !== 'PER_PERSON') {
            setValidation('按车或按套餐计价，请将儿童占床设为补差价');
            return;
          }
          if (normalizedBooking.transport?.mode === 'AUTO') {
            for (let n = Number(minPeople); n <= (Number(maxPeople) || 99); n++)
              if (
                !normalizedBooking.vehicles.some(
                  (v) => n >= (v.minPeople ?? 1) && n <= (v.maxPeople ?? 99),
                )
              ) {
                setValidation(`自动配车未覆盖 ${n} 人，请补齐人数区间`);
                return;
              }
          }
          const failure = validateSkuEditor({
            skuName,
            salePrice,
            minPeople,
            maxPeople,
            allowUnlimitedMax: sku.maxPeople == null,
            dateRuleId,
            refundPolicyId,
            includedItems: included,
            excludedItems: excluded,
            status: currentStatus,
          });
          const specificationError =
            (durationHours.trim() || textValue(sku, 'durationHours')) &&
            (!durationHours.trim() ||
              !Number.isFinite(Number(durationHours)) ||
              Number(durationHours) <= 0)
              ? '服务时长须为大于 0 的小时数'
              : '';
          const marketPriceError =
            marketPrice.trim() &&
            (!/^(?:0|[1-9]\d{0,7})(?:\.\d{1,2})?$/.test(marketPrice.trim()) ||
              Number(marketPrice) <= Number(salePrice))
              ? '划线参考价须大于销售价；无有效参考价时请留空'
              : '';
          setValidation(failure || specificationError || marketPriceError);
          if (failure || specificationError || marketPriceError) return;
          void command.run(
            () =>
              api.send(
                `/api/admin/m2/products/${encodeURIComponent(productId)}/skus/${encodeURIComponent(skuId)}`,
                'PATCH',
                {
                  bookingConfig: normalizedBooking,
                  skuName: skuName.trim(),
                  salePriceMinorUnits: yuanToMinorUnits(salePrice),
                  marketPriceMinorUnits: marketPrice.trim() ? yuanToMinorUnits(marketPrice) : null,
                  serviceMode,
                  ...(durationHours.trim() ? { durationHours: Number(durationHours) } : {}),
                  pricingMode,
                  guideMode,
                  ticketMode,
                  minPeople: numberValue(minPeople, 1),
                  ...(maxPeople.trim() ? { maxPeople: Number(maxPeople) } : {}),
                  includedItems: lines(included),
                  excludedItems: lines(excluded),
                  dateRuleId,
                  refundPolicyId,
                },
              ),
            '套餐已保存',
          );
        }}
      >
        <label>
          套餐编码
          <input value={textValue(sku, 'skuCode')} disabled aria-describedby="sku-code-help" />
          <small id="sku-code-help">编码用于订单与接口识别，创建后不可修改</small>
        </label>
        <label>
          套餐名称
          <input required value={skuName} onChange={(e) => setSkuName(e.target.value)} />
        </label>
        <label>
          销售价（元）
          <input
            required
            inputMode="decimal"
            value={salePrice}
            onChange={(e) => setSalePrice(e.target.value)}
          />
        </label>
        <label>
          划线参考价（元，可选）
          <input
            inputMode="decimal"
            value={marketPrice}
            onChange={(e) => setMarketPrice(e.target.value)}
            placeholder="留空则不展示划线价"
          />
        </label>
        <label>
          服务类型
          <select value={serviceMode} onChange={(e) => setServiceMode(e.target.value)}>
            <option value="CHARTER_GUIDE_PACKAGE">包车＋向导＋套餐</option>
            <option value="TRANSFER_TICKET_PACKAGE">接送＋票务＋套餐</option>
            <option value="CARPOOL_GUIDE_PACKAGE">拼车＋向导＋套餐</option>
          </select>
        </label>
        <label>
          服务时长（小时）
          <input
            type="number"
            min="0.01"
            step="any"
            value={durationHours}
            onChange={(event) => setDurationHours(event.target.value)}
            placeholder="例如：8"
          />
        </label>
        <label>
          计价方式
          <select value={pricingMode} onChange={(e) => setPricingMode(e.target.value)}>
            <option value="PER_PERSON">按人计价</option>
            <option value="PER_CAR">按车计价</option>
            <option value="PER_PACKAGE">按套餐计价</option>
          </select>
        </label>
        <label>
          最少人数
          <input
            type="number"
            min="1"
            value={minPeople}
            onChange={(e) => setMinPeople(e.target.value)}
          />
        </label>
        <label>
          最多人数
          <input
            type="number"
            min="1"
            value={maxPeople}
            placeholder={sku.maxPeople == null ? '未限定' : '请输入人数上限'}
            onChange={(e) => setMaxPeople(e.target.value)}
          />
        </label>
        <label>
          向导服务
          <select value={guideMode} onChange={(e) => setGuideMode(e.target.value)}>
            <option value="INCLUDED">已包含</option>
            <option value="NOT_INCLUDED">不包含</option>
          </select>
        </label>
        <label>
          票务方式
          <select value={ticketMode} onChange={(e) => setTicketMode(e.target.value)}>
            <option value="INCLUDED">已包含</option>
            <option value="SELF_PAY">游客自理</option>
            <option value="NOT_INCLUDED">不包含</option>
          </select>
        </label>
        <label>
          费用包含（每行一项）
          <textarea value={included} onChange={(e) => setIncluded(e.target.value)} />
        </label>
        <label>
          费用不含（每行一项）
          <textarea value={excluded} onChange={(e) => setExcluded(e.target.value)} />
        </label>
        <label>
          服务日历
          <select required value={dateRuleId} onChange={(e) => setDateRuleId(e.target.value)}>
            <option value="">请选择</option>
            {dateRules.map((rule) => (
              <option key={textOf(rule, 'id')} value={textValue(rule, 'id')}>
                {clean(rule.name)}
              </option>
            ))}
          </select>
          {dateRuleId && (
            <small>
              {calendarRuleText(
                dateRules.find((rule) => textValue(rule, 'id') === dateRuleId) ?? {},
              )}
            </small>
          )}
        </label>
        <label>
          退款规则
          <select
            required
            value={refundPolicyId}
            onChange={(e) => setRefundPolicyId(e.target.value)}
          >
            <option value="">请选择</option>
            {refundPolicies.map((policy) => (
              <option key={textOf(policy, 'id')} value={textValue(policy, 'id')}>
                {clean(policy.name)}
              </option>
            ))}
          </select>
          {refundPolicyId && (
            <small>
              {refundRuleText(
                refundPolicies.find((policy) => textValue(policy, 'id') === refundPolicyId) ?? {},
              )}
            </small>
          )}
        </label>
        <details className="product-sku-advanced">
          <summary>高级下单配置（按需调整）</summary>
          <BookingConfigEditor
            api={api}
            productId={productId}
            value={bookingConfig}
            onChange={setBookingConfig}
            disabled={writeDenied || collectionBusy}
          />
        </details>
        <div className="button-row booking-save-bar">
          {validation ? <Notice tone="error">{validation}</Notice> : null}
          <button
            className="primary"
            disabled={writeDenied || collectionBusy || !productId || !skuId}
          >
            {command.busy ? '保存中…' : '保存套餐'}
          </button>
          <button
            type="button"
            className={enabling ? undefined : 'danger-button'}
            disabled={writeDenied || collectionBusy || dirty || !productId || !skuId}
            onClick={() => setConfirmingStatus(true)}
          >
            {statusLabel}
          </button>
          {dirty ? <small>请先保存或还原当前修改，再切换套餐状态。</small> : null}
          <CommandMessage command={command} />
          <CommandMessage command={statusCommand} />
        </div>
        <PermissionBlocked visible={writeDenied} />
      </form>
      <ConfirmDialog
        open={confirmingStatus}
        title={statusLabel}
        description={`确认${enabling ? '重新启用' : '停用'}“${clean(sku.skuName)}”？${enabling ? '启用前服务端会再次检查有效规则。' : '停用后不可创建新订单，历史订单不受影响。'}`}
        confirmLabel={enabling ? '确认启用' : '确认停用'}
        dangerous={!enabling}
        pending={statusCommand.busy}
        onCancel={() => !statusCommand.busy && setConfirmingStatus(false)}
        onConfirm={() => {
          const path = `/api/admin/m2/products/${encodeURIComponent(productId)}/skus/${encodeURIComponent(skuId)}`;
          void statusCommand
            .run(
              () =>
                api.send(
                  enabling ? path : `${path}/disable`,
                  'PATCH',
                  enabling ? { status: 'ACTIVE' } : undefined,
                ),
              enabling ? '套餐已重新启用' : '套餐已停用',
            )
            .then((succeeded) => succeeded && setConfirmingStatus(false));
        }}
      />
    </>
  );
}

function ServiceItemCreateForm({
  api,
  productId,
  skuId,
  onDone,
  collectionBusy,
  onBusyChange,
  writeDenied,
  onPermissionDenied,
}: {
  api: AdminApiClient;
  productId: string;
  skuId: string;
  onDone: () => void;
  collectionBusy: boolean;
  onBusyChange: (busy: boolean) => void;
  writeDenied: boolean;
  onPermissionDenied: () => void;
}) {
  const command = useCommand(onDone, { onBusyChange, onPermissionDenied });
  const [validation, setValidation] = useState<string>();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [included, setIncluded] = useState(true);
  const dirty = Boolean(code || name || description || !included);
  useUnsavedNavigationGuard(dirty);
  return (
    <form
      className="typed-form"
      onSubmit={(event) => {
        event.preventDefault();
        const failure = validateServiceItem({ code, name, description });
        setValidation(failure);
        if (failure) return;
        void command.run(
          () =>
            api.send(
              `/api/admin/m2/products/${encodeURIComponent(productId)}/skus/${encodeURIComponent(skuId)}/service-items`,
              'POST',
              {
                itemCode: code.trim(),
                name: name.trim(),
                ...(description.trim() ? { description: description.trim() } : {}),
                included,
              },
            ),
          '服务项已新增',
        );
      }}
    >
      <label>
        服务项编码
        <input required value={code} onChange={(e) => setCode(e.target.value)} />
      </label>
      <label>
        服务项名称
        <input required value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label>
        费用关系
        <select
          value={included ? 'yes' : 'no'}
          onChange={(e) => setIncluded(e.target.value === 'yes')}
        >
          <option value="yes">费用已包含</option>
          <option value="no">费用不包含</option>
        </select>
      </label>
      <label>
        游客可见说明
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <div className="button-row">
        {validation ? <Notice tone="error">{validation}</Notice> : null}
        <button
          className="primary"
          disabled={writeDenied || collectionBusy || command.busy || !productId || !skuId}
        >
          {command.busy ? '保存中…' : '新增服务项'}
        </button>
        <CommandMessage command={command} />
      </div>
      <PermissionBlocked visible={writeDenied} />
    </form>
  );
}

function ServiceItemEditForm({
  api,
  productId,
  skuId,
  item,
  close,
  onDone,
  collectionBusy,
  onBusyChange,
  writeDenied,
  onPermissionDenied,
}: {
  api: AdminApiClient;
  productId: string;
  skuId: string;
  item: JsonRecord;
  close: () => void;
  onDone: () => void;
  collectionBusy: boolean;
  onBusyChange: (busy: boolean) => void;
  writeDenied: boolean;
  onPermissionDenied: () => void;
}) {
  const command = useCommand(onDone, { onBusyChange, onPermissionDenied });
  const [name, setName] = useState(textValue(item, 'name'));
  const [description, setDescription] = useState(textValue(item, 'description'));
  const [included, setIncluded] = useState(item.included !== false);
  const [validation, setValidation] = useState<string>();
  const dirty =
    name !== textValue(item, 'name') ||
    description !== textValue(item, 'description') ||
    included !== (item.included !== false);
  useUnsavedNavigationGuard(dirty);
  return (
    <form
      className="typed-form r2-inline-edit"
      onSubmit={(event) => {
        event.preventDefault();
        const failure = validateServiceItem({
          code: textValue(item, 'itemCode'),
          name,
          description,
        });
        setValidation(failure);
        if (failure) return;
        void command
          .run(
            () =>
              api.send(
                `/api/admin/m2/products/${encodeURIComponent(productId)}/skus/${encodeURIComponent(skuId)}/service-items/${encodeURIComponent(textOf(item, 'id'))}`,
                'PATCH',
                { name: name.trim(), description: description.trim() || null, included },
              ),
            '服务项已更新',
          )
          .then((succeeded) => succeeded && close());
      }}
    >
      <label>
        服务项名称
        <input value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      <label>
        费用关系
        <select
          value={included ? 'yes' : 'no'}
          onChange={(event) => setIncluded(event.target.value === 'yes')}
        >
          <option value="yes">费用已包含</option>
          <option value="no">费用不包含</option>
        </select>
      </label>
      <label className="wide">
        游客可见说明
        <textarea value={description} onChange={(event) => setDescription(event.target.value)} />
      </label>
      {validation ? <Notice tone="error">{validation}</Notice> : null}
      <div className="button-row">
        <button
          type="button"
          disabled={collectionBusy || command.busy}
          onClick={() => {
            if (!dirty || confirmDiscardChanges()) close();
          }}
        >
          取消
        </button>
        <button className="primary" disabled={writeDenied || collectionBusy || command.busy}>
          {command.busy ? '保存中…' : '保存修改'}
        </button>
        <CommandMessage command={command} />
      </div>
      <PermissionBlocked visible={writeDenied} />
    </form>
  );
}

function ServiceItemRow({
  api,
  productId,
  skuId,
  item,
  index,
  items,
  onDone,
  collectionBusy,
  onBusyChange,
  writeDenied,
  onPermissionDenied,
}: {
  api: AdminApiClient;
  productId: string;
  skuId: string;
  item: JsonRecord;
  index: number;
  items: JsonRecord[];
  onDone: () => void;
  collectionBusy: boolean;
  onBusyChange: (busy: boolean) => void;
  writeDenied: boolean;
  onPermissionDenied: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const command = useCommand(onDone, {
    onBusyChange,
    onPermissionDenied,
    onConflict: onDone,
  });
  const ordered = bySortOrder(items);
  const itemId = textOf(item, 'id');
  const basePath = `/api/admin/m2/products/${encodeURIComponent(productId)}/skus/${encodeURIComponent(skuId)}/service-items`;
  const reorder = (targetIndex: number) =>
    command.run(
      () =>
        api.send(`${basePath}/reorder`, 'POST', {
          orderedIds: movedIds(ordered, itemId, targetIndex),
          expectedOrder: expectedOrder(ordered),
        }),
      '服务项顺序已更新',
    );
  return (
    <>
      <tr>
        <td>{index + 1}</td>
        <td>
          <b>{clean(item.name)}</b>
          <small className="r2-cell-note">{clean(item.itemCode)}</small>
        </td>
        <td>{item.included === false ? '费用不含' : '费用包含'}</td>
        <td>{clean(item.description, '暂无补充说明')}</td>
        <td>
          <div className="r2-record-actions">
            <button
              type="button"
              disabled={writeDenied || collectionBusy || command.busy || index === 0}
              onClick={() => void reorder(index - 1)}
            >
              上移
            </button>
            <button
              type="button"
              disabled={
                writeDenied || collectionBusy || command.busy || index === ordered.length - 1
              }
              onClick={() => void reorder(index + 1)}
            >
              下移
            </button>
            <button
              type="button"
              disabled={writeDenied || collectionBusy || command.busy}
              onClick={() => setEditing(true)}
            >
              编辑
            </button>
            <button
              type="button"
              className="danger-link"
              disabled={writeDenied || collectionBusy || command.busy}
              onClick={() => setDeleting(true)}
            >
              删除
            </button>
          </div>
          <CommandMessage command={command} />
        </td>
      </tr>
      {editing ? (
        <tr className="r2-edit-row">
          <td colSpan={5}>
            <ServiceItemEditForm
              api={api}
              productId={productId}
              skuId={skuId}
              item={item}
              close={() => setEditing(false)}
              onDone={onDone}
              collectionBusy={collectionBusy}
              onBusyChange={onBusyChange}
              writeDenied={writeDenied}
              onPermissionDenied={onPermissionDenied}
            />
          </td>
        </tr>
      ) : null}
      {deleting ? (
        <tr className="r2-dialog-row">
          <td colSpan={5}>
            <ConfirmDialog
              open
              title="删除服务项"
              description={`确认删除“${clean(item.name)}”？删除后前端费用说明将同步移除。`}
              confirmLabel="确认删除"
              dangerous
              pending={command.busy}
              onCancel={() => !command.busy && setDeleting(false)}
              onConfirm={() =>
                void command
                  .run(
                    () => api.send(`${basePath}/${encodeURIComponent(itemId)}`, 'DELETE'),
                    '服务项已删除',
                  )
                  .then((succeeded) => succeeded && setDeleting(false))
              }
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function SkuPageContent({
  api,
  pageId,
  skuId,
  targetAppId: selectedAppId = '',
  scopeFilter,
}: {
  api: AdminApiClient;
  pageId: string;
  skuId: string;
  targetAppId?: string;
  scopeFilter?: React.ReactNode;
}) {
  const targetAppId = configurationScopeAppId(api, selectedAppId);
  const state = useApi(api, skuId ? `/api/admin/m2/skus/${encodeURIComponent(skuId)}` : undefined);
  const skuResult = unwrapRecord(state.data),
    sku = nested(skuResult, 'sku'),
    parentProduct = nested(skuResult, 'product'),
    productId = textValue(parentProduct, 'id', textValue(sku, 'spuId'));
  const refundPoliciesState = useApi(
    api,
    pageId === 'ADM-C05' && targetAppId
      ? `/api/admin/m2/apps/${encodeURIComponent(targetAppId)}/refund-policies`
      : undefined,
  );
  const dateRulesState = useApi(
    api,
    pageId === 'ADM-C05' && targetAppId
      ? `/api/admin/m2/apps/${encodeURIComponent(targetAppId)}/sell-date-rules`
      : undefined,
  );
  const serviceItemsState = useApi(
    api,
    pageId === 'ADM-C06' && productId && skuId
      ? `/api/admin/m2/products/${encodeURIComponent(productId)}/skus/${encodeURIComponent(skuId)}/service-items`
      : undefined,
  );
  const serviceItems = rows(serviceItemsState.data);
  const [writeDenied, setWriteDenied] = useState(false);
  const [serviceCollectionBusy, setServiceCollectionBusy] = useState(false);
  useEffect(() => {
    setWriteDenied(false);
    setServiceCollectionBusy(false);
  }, [skuId, productId]);
  if (pageId === 'ADM-C06')
    return (
      <div className="r2-page">
        <Header
          title="费用包含、不含与票务配置"
          subtitle={`${clean(sku.skuName, '套餐')} · 服务内容与票务展示`}
          actions={
            <button
              onClick={() => {
                state.reload();
                serviceItemsState.reload();
              }}
            >
              刷新数据
            </button>
          }
        />
        {scopeFilter}
        <State state={state} count={Object.keys(sku).length} noun="套餐">
          <div className="r2-sku-hero">
            <div>
              <span>当前套餐</span>
              <h2>{clean(sku.skuName, '套餐')}</h2>
              <p>
                {label(sku.serviceMode)} · {label(sku.pricingMode)} · {skuPrice(sku)}
              </p>
            </div>
            <Pill value={sku.status} />
          </div>
          <div className="r2-two-one r3a-service-layout product-primary-table">
            <Panel
              title={`服务项目（${serviceItems.length}）`}
              hint="费用包含与不含按当前顺序同步到小程序详情页"
            >
              <PermissionBlocked visible={writeDenied} />
              <State state={serviceItemsState} count={serviceItems.length} noun="服务项">
                <Table
                  heads={['顺序', '服务项', '费用关系', '游客可见说明', '操作']}
                  empty={!serviceItems.length}
                >
                  {bySortOrder(serviceItems).map((item, index) => (
                    <ServiceItemRow
                      key={textOf(item, 'id')}
                      api={api}
                      productId={productId}
                      skuId={skuId}
                      item={item}
                      index={index}
                      items={serviceItems}
                      onDone={serviceItemsState.reload}
                      collectionBusy={serviceCollectionBusy}
                      onBusyChange={setServiceCollectionBusy}
                      writeDenied={writeDenied}
                      onPermissionDenied={() => setWriteDenied(true)}
                    />
                  ))}
                </Table>
              </State>
              <MaintenanceDetails summary="新增费用包含/不含服务项">
                <ServiceItemCreateForm
                  api={api}
                  productId={productId}
                  skuId={skuId}
                  onDone={serviceItemsState.reload}
                  collectionBusy={serviceCollectionBusy}
                  onBusyChange={setServiceCollectionBusy}
                  writeDenied={writeDenied}
                  onPermissionDenied={() => setWriteDenied(true)}
                />
              </MaintenanceDetails>
            </Panel>
            <div className="product-record-summary">
              <span>票务：{label(sku.ticketMode)}</span>
              <span>向导：{label(sku.guideMode)}</span>
              <span>服务模式：{label(sku.serviceMode)}</span>
              <a href={productScopeHref(`/admin/skus/${encodeURIComponent(skuId)}`, targetAppId)}>
                修改套餐服务方式
              </a>
            </div>
          </div>
        </State>
      </div>
    );
  return (
    <div className="r2-page">
      <Header
        title="套餐编辑"
        subtitle={`${clean(sku.skuName, '套餐')} · 交易参数、服务模式、计价与服务日历`}
        actions={<button onClick={state.reload}>刷新数据</button>}
      />
      {scopeFilter}
      <State state={state} count={Object.keys(sku).length} noun="套餐">
        <div className="r2-sku-hero">
          <div>
            <span>套餐交易配置</span>
            <h2>{clean(sku.skuName, '套餐')}</h2>
            <p>
              {label(sku.serviceMode)} · {peopleRange(sku.minPeople, sku.maxPeople)} ·{' '}
              {skuPrice(sku)}
            </p>
          </div>
          <Pill value={sku.status} />
        </div>
        <div className="r2-two r3a-sku-layout product-primary-table">
          <Panel title="套餐基本与计价">
            <SkuEditForm
              api={api}
              productId={productId}
              sku={sku}
              refundPolicies={rows(refundPoliciesState.data)}
              dateRules={rows(dateRulesState.data)}
              onDone={() => {
                state.reload();
                refundPoliciesState.reload();
                dateRulesState.reload();
              }}
              writeDenied={writeDenied}
              onPermissionDenied={() => setWriteDenied(true)}
            />
            <div className="r2-form-footnote">
              保存后用于小程序套餐选择。历史订单保留创建时的价格与规则。
            </div>
          </Panel>
        </div>
      </State>
    </div>
  );
}

function TagsSortPageContent({
  api,
  targetAppId = '',
}: {
  api: AdminApiClient;
  targetAppId?: string;
}) {
  const state = useApi(
      api,
      targetAppId
        ? `/api/admin/m2/products?appId=${encodeURIComponent(targetAppId)}&pageSize=100`
        : undefined,
    ),
    tagsState = useApi(
      api,
      targetAppId
        ? `/api/admin/m2/apps/${encodeURIComponent(targetAppId)}/product-tags`
        : undefined,
    ),
    appState = useApi(
      api,
      targetAppId ? `/api/admin/m2/apps/${encodeURIComponent(targetAppId)}` : undefined,
    ),
    products = rows(state.data),
    availableTags = rows(tagsState.data);
  const orderedProducts = [...products].sort(
    (a, b) =>
      numberValue(textValue(a, 'effectiveSortOrder')) -
      numberValue(textValue(b, 'effectiveSortOrder')),
  );
  const systemTags = availableTags.filter(
    (tag) => !textValue(tag, 'appId') && !textValue(tag, 'cityId'),
  );
  const scopedTags = availableTags.filter((tag) => textValue(tag, 'appId') === targetAppId);
  const app = unwrapRecord(appState.data);
  const cities = nestedRows(app, 'cities').map((item) =>
    Object.keys(nested(item, 'city')).length ? nested(item, 'city') : item,
  );
  const [writeDenied, setWriteDenied] = useState(false);
  const create = useCommand(
    () => {
      tagsState.reload();
    },
    { onPermissionDenied: () => setWriteDenied(true) },
  );
  const attach = useCommand(
    () => {
      state.reload();
      tagsState.reload();
    },
    { onPermissionDenied: () => setWriteDenied(true) },
  );
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'APP' | 'CITY'>('APP');
  const [cityId, setCityId] = useState('');
  const [tagSortOrder, setTagSortOrder] = useState('0');
  const [validation, setValidation] = useState('');
  const [productId, setProductId] = useState('');
  const [tagId, setTagId] = useState('');
  const [detaching, setDetaching] = useState<JsonRecord>();
  const selectedProduct = products.find((product) => textOf(product, 'id') === productId) ?? {};
  const selectedTags = productTagRecords(selectedProduct);
  const selectableTags = availableTags.filter(
    (tag) => textValue(tag, 'status', 'ACTIVE') !== 'DISABLED',
  );
  const alreadyBound = selectedTags.some((tag) => textOf(tag, 'id') === tagId);
  const dirty = Boolean(code || name || cityId || tagSortOrder !== '0');
  useUnsavedNavigationGuard(dirty);
  const combinedState: LoadState<unknown> = {
    loading: state.loading || tagsState.loading || appState.loading,
    error:
      state.error ??
      tagsState.error ??
      appState.error ??
      (!targetAppId ? new Error('请先选择小程序') : undefined),
    data: state.data,
    reload: () => {
      state.reload();
      tagsState.reload();
      appState.reload();
    },
  };
  return (
    <div className="r2-page">
      <Header
        title="营销标签与排序"
        subtitle="系统标签、自定义标签、生效范围及前端人工排序"
        actions={
          <button
            onClick={() => {
              state.reload();
              tagsState.reload();
              appState.reload();
            }}
          >
            刷新数据
          </button>
        }
      />
      <State state={combinedState} count={1} noun="产品与标签">
        <div className="r2-two r2-tags-sort-layout product-primary-table">
          <div className="r2-two product-tags-maintenance">
            <Panel title="营销标签库" hint="系统标签与运营自定义标签都必须先保存再使用">
              <div className="r2-tag-library">
                <section>
                  <h3>系统标签</h3>
                  <small>平台内置，只读；如需差异化展示请创建当前小程序标签。</small>
                  <div className="r2-tag-list">
                    {systemTags.map((tag) => (
                      <span key={textOf(tag, 'id')} title="系统标签不可编辑">
                        {tagLabel(tag)} · 只读
                      </span>
                    ))}
                    {!systemTags.length && <em>暂无系统标签</em>}
                  </div>
                </section>
                <section>
                  <h3>自定义标签</h3>
                  <div className="r2-tag-admin-list">
                    {scopedTags.map((tag) => (
                      <TagLibraryRow
                        key={textOf(tag, 'id')}
                        api={api}
                        appId={targetAppId}
                        tag={tag}
                        cities={cities}
                        writeDenied={writeDenied}
                        onPermissionDenied={() => setWriteDenied(true)}
                        onDone={() => {
                          tagsState.reload();
                          state.reload();
                        }}
                      />
                    ))}
                    {!scopedTags.length && <em>暂无小程序/城市自定义标签</em>}
                  </div>
                </section>
              </div>
              <MaintenanceDetails summary="+ 新建自定义标签">
                <form
                  className="typed-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,49}$/.test(code.trim())) {
                      setValidation('标签编码只能包含字母、数字、下划线或短横线（1–50 位）。');
                      return;
                    }
                    if (!name.trim() || name.trim().length > 100) {
                      setValidation(!name.trim() ? '请填写标签名称。' : '标签名称最多 100 字。');
                      return;
                    }
                    const sortValue = Number(tagSortOrder);
                    if (!Number.isInteger(sortValue) || sortValue < -100000 || sortValue > 100000) {
                      setValidation('标签排序值须为 -100000–100000 的整数。');
                      return;
                    }
                    if (scope === 'CITY' && !cityId) {
                      setValidation('请选择标签生效城市。');
                      return;
                    }
                    setValidation('');
                    void create
                      .run(
                        () =>
                          api.send(
                            `/api/admin/m2/apps/${encodeURIComponent(targetAppId)}/product-tags`,
                            'POST',
                            {
                              code: code.trim(),
                              name: name.trim(),
                              sortOrder: numberValue(tagSortOrder),
                              ...(scope === 'CITY' ? { cityId } : {}),
                            },
                          ),
                        '营销标签已创建',
                      )
                      .then((ok) => {
                        if (ok) {
                          setCode('');
                          setName('');
                          setScope('APP');
                          setCityId('');
                          setTagSortOrder('0');
                        }
                      });
                  }}
                >
                  <label>
                    标签编码
                    <input required value={code} onChange={(e) => setCode(e.target.value)} />
                  </label>
                  <label>
                    标签名称
                    <input required value={name} onChange={(e) => setName(e.target.value)} />
                  </label>
                  <label>
                    生效范围
                    <select
                      value={scope}
                      onChange={(e) => {
                        setScope(e.target.value as 'APP' | 'CITY');
                        if (e.target.value === 'APP') setCityId('');
                      }}
                    >
                      <option value="APP">当前小程序</option>
                      <option value="CITY">当前小程序的指定城市</option>
                    </select>
                  </label>
                  {scope === 'CITY' ? (
                    <label>
                      生效城市
                      <select required value={cityId} onChange={(e) => setCityId(e.target.value)}>
                        <option value="">请选择</option>
                        {cities.map((city) => (
                          <option key={textOf(city, 'id')} value={textValue(city, 'id')}>
                            {clean(city.name)}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  <label>
                    标签排序值
                    <input
                      type="number"
                      min="-100000"
                      max="100000"
                      value={tagSortOrder}
                      onChange={(e) => setTagSortOrder(e.target.value)}
                    />
                  </label>
                  <div className="button-row">
                    <button
                      className="primary"
                      disabled={create.busy || !targetAppId || writeDenied}
                    >
                      {create.busy ? '创建中…' : '创建营销标签'}
                    </button>
                    <CommandMessage command={create} />
                  </div>
                  {validation ? <Notice tone="error">{validation}</Notice> : null}
                </form>
              </MaintenanceDetails>
              <div className="canonical-rule-note">
                只展示已保存的营销标签与产品显式关联标签，不展示根据标题、价格或热度推算的标签。
              </div>
            </Panel>
            <Panel title="标签生效范围" hint="选择已保存标签并绑定到具体产品">
              <form
                className="typed-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void attach.run(
                    () =>
                      api.send(
                        `/api/admin/m2/apps/${encodeURIComponent(targetAppId)}/products/${encodeURIComponent(productId)}/tags/${encodeURIComponent(tagId)}`,
                        'PUT',
                        { enabled: true },
                      ),
                    '标签已关联到产品',
                  );
                }}
              >
                <label>
                  选择产品
                  <select required value={productId} onChange={(e) => setProductId(e.target.value)}>
                    <option value="">请选择</option>
                    {orderedProducts.map((product) => (
                      <option key={textOf(product, 'id')} value={textValue(product, 'id')}>
                        {clean(product.title)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  选择标签
                  <select required value={tagId} onChange={(e) => setTagId(e.target.value)}>
                    <option value="">请选择</option>
                    {selectableTags.map((tag) => (
                      <option key={textOf(tag, 'id')} value={textValue(tag, 'id')}>
                        {tagLabel(tag)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="button-row">
                  <button
                    className="primary"
                    disabled={attach.busy || !productId || !tagId || writeDenied || alreadyBound}
                  >
                    {attach.busy ? '关联中…' : alreadyBound ? '已关联' : '关联标签'}
                  </button>
                  <CommandMessage command={attach} />
                </div>
              </form>
              <div className="r2-scope-checks">
                {selectedTags.map((tag) => (
                  <article key={textOf(tag, 'id')}>
                    <span className="r2-tag-check" aria-hidden="true">
                      ✓
                    </span>
                    <div>
                      <b>{tagLabel(tag)}</b>
                      <small>{tagScopeLabel(tag)}</small>
                    </div>
                    <button
                      type="button"
                      disabled={attach.busy || writeDenied}
                      onClick={() => setDetaching(tag)}
                    >
                      解除
                    </button>
                  </article>
                ))}
                {!selectedTags.length && (
                  <div className="canonical-inline-empty">请选择产品查看已关联标签</div>
                )}
              </div>
              <PermissionBlocked visible={writeDenied} />
              <ConfirmDialog
                open={Boolean(detaching)}
                title={`解除“${detaching ? tagLabel(detaching) : ''}”标签`}
                description={`确认从“${clean(selectedProduct.title, '当前产品')}”解除该标签？前端列表会在保存后立即按有效标签刷新。`}
                confirmLabel="确认解除"
                dangerous
                pending={attach.busy}
                onCancel={() => !attach.busy && setDetaching(undefined)}
                onConfirm={() =>
                  detaching &&
                  void attach
                    .run(
                      () =>
                        api.send(
                          `/api/admin/m2/apps/${encodeURIComponent(targetAppId)}/products/${encodeURIComponent(productId)}/tags/${encodeURIComponent(textValue(detaching, 'id'))}`,
                          'PUT',
                          { enabled: false },
                        ),
                      '标签关联已解除',
                    )
                    .then((ok) => ok && setDetaching(undefined))
                }
              />
            </Panel>
          </div>
          <div className="r2-stack product-tags-list">
            <Panel title="前端排序策略">
              <div className="canonical-rule-note">
                当前策略：使用产品的前端排序值；标签不会覆盖人工排序。
              </div>
              <Table
                heads={['产品', '有效标签', '当前排序', '小程序覆盖', '操作']}
                empty={!orderedProducts.length}
              >
                {orderedProducts.map((product) => (
                  <ProductSortRow
                    key={textOf(product, 'id')}
                    api={api}
                    appId={targetAppId}
                    product={product}
                    writeDenied={writeDenied}
                    onPermissionDenied={() => setWriteDenied(true)}
                    onDone={state.reload}
                  />
                ))}
              </Table>
            </Panel>
          </div>
        </div>
      </State>
    </div>
  );
}

function TagLibraryRow({
  api,
  appId,
  tag,
  cities,
  writeDenied,
  onPermissionDenied,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  tag: JsonRecord;
  cities: JsonRecord[];
  writeDenied: boolean;
  onPermissionDenied: () => void;
  onDone: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [name, setName] = useState(textValue(tag, 'name'));
  const [cityId, setCityId] = useState(textValue(tag, 'cityId'));
  const [sortOrder, setSortOrder] = useState(textValue(tag, 'sortOrder', '0'));
  const command = useCommand(onDone, { onPermissionDenied });
  const enabled = textValue(tag, 'status', 'ACTIVE') !== 'DISABLED';
  const sortValue = Number(sortOrder);
  const validSort = Number.isInteger(sortValue) && sortValue >= -100000 && sortValue <= 100000;
  const dirty =
    editing &&
    (name !== textValue(tag, 'name') ||
      cityId !== textValue(tag, 'cityId') ||
      sortOrder !== textValue(tag, 'sortOrder', '0'));
  useEffect(() => {
    if (editing) return;
    setName(textValue(tag, 'name'));
    setCityId(textValue(tag, 'cityId'));
    setSortOrder(textValue(tag, 'sortOrder', '0'));
  }, [editing, tag.cityId, tag.id, tag.name, tag.sortOrder]);
  useUnsavedNavigationGuard(dirty);
  const path = `/api/admin/m2/apps/${encodeURIComponent(appId)}/product-tags/${encodeURIComponent(textValue(tag, 'id'))}`;
  return (
    <article className="r2-tag-admin-row">
      <div>
        <b>{tagLabel(tag)}</b>
        <small>
          {textValue(tag, 'code')} · {tagScopeLabel(tag)}
        </small>
        <Pill value={tag.status} />
      </div>
      {editing ? (
        <div className="r2-rule-row__editor">
          <label>
            名称
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            城市范围
            <select value={cityId} onChange={(e) => setCityId(e.target.value)}>
              <option value="">整个小程序</option>
              {cities.map((city) => (
                <option key={textOf(city, 'id')} value={textValue(city, 'id')}>
                  {clean(city.name)}
                </option>
              ))}
            </select>
          </label>
          <label>
            排序值
            <input
              type="number"
              min="-100000"
              max="100000"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </label>
          <div className="button-row">
            <ActionButton
              type="button"
              className="primary"
              pending={command.busy}
              disabled={!dirty || !name.trim() || name.trim().length > 100 || !validSort}
              {...(!dirty
                ? { disabledReason: '没有待保存的修改' }
                : !validSort
                  ? { disabledReason: '排序值须为 -100000–100000 的整数' }
                  : name.trim().length > 100
                    ? { disabledReason: '名称最多 100 字' }
                    : {})}
              onClick={() =>
                void command
                  .run(
                    () =>
                      api.send(path, 'PATCH', {
                        name: name.trim(),
                        cityId: cityId || null,
                        sortOrder: sortValue,
                      }),
                    '标签已保存',
                  )
                  .then((ok) => ok && setEditing(false))
              }
            >
              保存
            </ActionButton>
            <button type="button" disabled={command.busy} onClick={() => setEditing(false)}>
              取消
            </button>
          </div>
        </div>
      ) : null}
      <div className="button-row">
        <button
          type="button"
          disabled={writeDenied || command.busy}
          onClick={() => setEditing((v) => !v)}
        >
          {editing ? '收起' : '编辑'}
        </button>
        <button
          type="button"
          className={enabled ? 'danger-button' : 'primary'}
          disabled={writeDenied || editing || command.busy}
          onClick={() => setConfirming(true)}
        >
          {enabled ? '停用' : '启用'}
        </button>
        <CommandMessage command={command} />
      </div>
      <ConfirmDialog
        open={confirming}
        title={`${enabled ? '停用' : '启用'}“${tagLabel(tag)}”`}
        description={
          enabled
            ? '停用后该标签立即从前端有效标签中移除，已有显式关联记录保留。'
            : '启用后会按当前小程序和城市范围重新生效。'
        }
        confirmLabel={enabled ? '确认停用' : '确认启用'}
        dangerous={enabled}
        pending={command.busy}
        onCancel={() => !command.busy && setConfirming(false)}
        onConfirm={() =>
          void command
            .run(
              () =>
                api.send(
                  path,
                  enabled ? 'DELETE' : 'PATCH',
                  enabled ? undefined : { status: 'ACTIVE' },
                ),
              enabled ? '标签已停用' : '标签已启用',
            )
            .then((ok) => ok && setConfirming(false))
        }
      />
    </article>
  );
}

function ProductSortRow({
  api,
  appId,
  product,
  writeDenied,
  onPermissionDenied,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  product: JsonRecord;
  writeDenied: boolean;
  onPermissionDenied: () => void;
  onDone: () => void;
}) {
  const current = textValue(product, 'effectiveSortOrder', textValue(product, 'sortOrder', '0'));
  const [sortOrder, setSortOrder] = useState(current);
  useEffect(() => setSortOrder(current), [current, product.id]);
  const command = useCommand(onDone, { onPermissionDenied });
  const configured = product.appExposureConfigured === true;
  const productId = textValue(product, 'id');
  const sortValue = Number(sortOrder);
  const validSort = Number.isInteger(sortValue) && sortValue >= -100000 && sortValue <= 100000;
  return (
    <tr>
      <td>
        <b>{clean(product.title)}</b>
        <small className="r2-cell-note">{priceRangeFromSkus(nestedRows(product, 'skus'))}</small>
      </td>
      <td>
        <div className="r2-inline-chip-row">
          {productTagRecords(product).map((tag) => (
            <span key={textOf(tag, 'id') || tagLabel(tag)}>{tagLabel(tag)}</span>
          ))}
          {!productTagRecords(product).length && <em>未关联</em>}
        </div>
      </td>
      <td>
        <input
          className="r2-sort-input"
          aria-label={`${clean(product.title)}排序值`}
          type="number"
          min="-100000"
          max="100000"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        />
      </td>
      <td>
        <Pill value={configured ? 'ACTIVE' : 'DRAFT'} />
        <small className="r2-cell-note">{configured ? '已配置小程序覆盖' : '未配置覆盖'}</small>
      </td>
      <td>
        <ActionButton
          type="button"
          className="primary"
          pending={command.busy}
          disabled={writeDenied || sortOrder === current || !validSort}
          {...(!configured
            ? { disabledReason: '请先在“产品小程序覆盖”配置该产品' }
            : !validSort
              ? { disabledReason: '排序值须为 -100000–100000 的整数' }
              : {})}
          onClick={() =>
            void command.run(
              () =>
                api.send(
                  `/api/admin/m2/apps/${encodeURIComponent(appId)}/products/${encodeURIComponent(productId)}/override`,
                  'PUT',
                  { sortOrderOverride: sortValue, requireExisting: true },
                ),
              '前端排序已保存',
            )
          }
        >
          保存排序
        </ActionButton>
        <CommandMessage command={command} />
      </td>
    </tr>
  );
}
